import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import React, { useMemo, useState, useEffect, useCallback, useRef, createContext, useContext, useLayoutEffect, createRef } from "react";
import { useTheme, styled, Box, Skeleton, Grid, Link, darken, Button, Divider, TextField, InputAdornment, Checkbox, Tooltip, debounce, Slider, TableCell, Table, TableHead, TableRow, TableBody, TableContainer, TableSortLabel, TablePagination, IconButton as IconButton$1, Menu, MenuItem, SvgIcon, Icon, Collapse, LinearProgress, Typography as Typography$1, lighten, ClickAwayListener, ToggleButton, capitalize, ListItem, Chip, List, Paper, Popover, Select, ListItemText, Drawer } from "@mui/material";
import { useIntl } from "react-intl";
import { h as getDefaultExportFromCjs, u as useImperativeIntl, T as Typography, S as SPACING, B as BREAKPOINTS, a as TablePaginationActions, b as ICON_SIZE_MEDIUM } from "../TablePaginationActions-C6TVGJM-.js";
import _ReactEcharts from "echarts-for-react";
import { FixedSizeList } from "react-window";
import { S as Search, a as SwatchSquare } from "../SwatchSquare-CaaLsjAC.js";
import { A as Alert, I as IconButton, L as Link$1 } from "../Link-RfbgGzdR.js";
import { ChevronLeft, ChevronRight, ExpandLess, ExpandMore, MoreVert, KeyboardArrowDown, ErrorOutline, Cancel, VisibilityOutlined, VisibilityOffOutlined, Close, LayersOutlined } from "@mui/icons-material";
import { a as getColorByCategory, c as commonPalette, b as getPalette } from "../paletteUtils-BHqJlHm9.js";
function detectTouchscreen() {
  let result = false;
  if (window.PointerEvent && "maxTouchPoints" in navigator) {
    if (navigator.maxTouchPoints > 0) {
      result = true;
    }
  } else {
    if (window.matchMedia && window.matchMedia("(any-pointer:coarse)").matches) {
      result = true;
    } else if (window.TouchEvent || "ontouchstart" in window) {
      result = true;
    }
  }
  return result;
}
function processFormatterResult(formatterResult) {
  if (!formatterResult || typeof formatterResult !== "object") {
    return formatterResult;
  }
  return `${formatterResult.prefix ?? ""}${formatterResult.value}${formatterResult.suffix ?? ""}`;
}
const EMPTY_ARR$8 = [];
const EMPTY_OBJ$6 = {};
const IDENTITY$5 = (n) => n;
function defaultTooltipFormatter$5(params, yAxisFormatter) {
  if (!params) {
    return null;
  }
  if (Array.isArray(params) && params.length === 0) {
    return null;
  }
  const _params = Array.isArray(params) ? params : [params];
  let message = "";
  message += String(
    _params[0].axisValueLabel
  );
  message += _params.map(({ seriesName, value, data, marker }) => {
    const formattedSeriesName = seriesName ? seriesName + ": " : "";
    const formattedValue = processFormatterResult(
      yAxisFormatter(Number(value))
    );
    const _data = data;
    const item = `<div style="margin-left: 8px; display: inline">
        ${formattedSeriesName}${formattedValue}${String((_data == null ? void 0 : _data.unit) || "")}
        </div>`;
    return `<div style="margin-top: 4px">${String(marker)}${item}</div>`;
  }).join(" ");
  return message;
}
function useProcessedProps({
  height,
  labels = EMPTY_OBJ$6,
  yAxisData: _yAxisData,
  selectedBars: _selectedBars = EMPTY_ARR$8,
  series: _series,
  colors: _colors,
  tooltip = true,
  tooltipFormatter = defaultTooltipFormatter$5,
  xAxisFormatter = IDENTITY$5,
  yAxisFormatter = IDENTITY$5,
  onSelectedBarsChange,
  animation = true,
  filterable = true,
  stacked = true,
  ...props
}) {
  const theme = useTheme();
  const series = useMemo(
    () => (_series ?? []).map((name) => labels[name] ?? name),
    [_series, labels]
  );
  const yAxisData = useMemo(
    () => Array.isArray(_yAxisData[0]) ? _yAxisData : [_yAxisData],
    [_yAxisData]
  );
  const colors = useMemo(() => {
    if (typeof _colors === "string") return [_colors];
    if (Array.isArray(_colors) && _colors.length) return _colors;
    if (yAxisData.length <= 1 || series.length === 1)
      return [theme.palette.secondary.main];
    if (yAxisData.length === 2 || series.length === 2)
      return [theme.palette.secondary.light, theme.palette.secondary.dark];
    return Object.values(theme.palette.qualitative.bold || {});
  }, [_colors, theme, yAxisData, series]);
  return {
    ...props,
    tooltip,
    tooltipFormatter,
    xAxisFormatter,
    yAxisFormatter,
    onSelectedBarsChange,
    animation,
    filterable,
    stacked,
    labels,
    height: height ?? parseInt(theme.spacing(22)),
    // parseInt removes 'px' from the string,
    selectedBars: formatSelectedBars(_selectedBars),
    yAxisData,
    colors,
    series
  };
}
function formatSelectedBars(selectedBars) {
  if (!(selectedBars ?? []).length) {
    return [];
  }
  return selectedBars.map((barCoords) => {
    const isWellFormatted = Array.isArray(barCoords) && barCoords.length === 2;
    if (isWellFormatted) {
      return barCoords;
    } else if (!Array.isArray(barCoords)) {
      return [barCoords, 0];
    } else {
      return [...Array(2)].map((_, idx) => barCoords[idx] ?? 0);
    }
  });
}
var fastDeepEqual = function equal(a, b) {
  if (a === b) return true;
  if (a && b && typeof a == "object" && typeof b == "object") {
    if (a.constructor !== b.constructor) return false;
    var length, i, keys;
    if (Array.isArray(a)) {
      length = a.length;
      if (length != b.length) return false;
      for (i = length; i-- !== 0; )
        if (!equal(a[i], b[i])) return false;
      return true;
    }
    if (a.constructor === RegExp) return a.source === b.source && a.flags === b.flags;
    if (a.valueOf !== Object.prototype.valueOf) return a.valueOf() === b.valueOf();
    if (a.toString !== Object.prototype.toString) return a.toString() === b.toString();
    keys = Object.keys(a);
    length = keys.length;
    if (length !== Object.keys(b).length) return false;
    for (i = length; i-- !== 0; )
      if (!Object.prototype.hasOwnProperty.call(b, keys[i])) return false;
    for (i = length; i-- !== 0; ) {
      var key = keys[i];
      if (!equal(a[key], b[key])) return false;
    }
    return true;
  }
  return a !== a && b !== b;
};
const isEqual = /* @__PURE__ */ getDefaultExportFromCjs(fastDeepEqual);
class ReactEcharts extends _ReactEcharts {
  componentDidUpdate(prevProps) {
    const { shouldSetOption } = this.props;
    if (typeof shouldSetOption === "function" && !shouldSetOption(prevProps, this.props)) {
      return;
    }
    if (!isEqual(prevProps.theme, this.props.theme) || !isEqual(prevProps.opts, this.props.opts)) {
      this.dispose();
      this.renderNewEcharts();
      return;
    }
    const echartsInstance = this.getEchartsInstance();
    if (!isEqual(prevProps.onEvents, this.props.onEvents)) {
      this.offEvents(echartsInstance, prevProps.onEvents);
      this.bindEvents(echartsInstance, this.props.onEvents);
    }
    const pickKeys = [
      "option",
      "notMerge",
      "lazyUpdate",
      "showLoading",
      "loadingOption"
    ];
    if (!isEqual(pick(this.props, pickKeys), pick(prevProps, pickKeys))) {
      this.updateEChartsOption();
    }
    if (!isEqual(prevProps.style, this.props.style) || !isEqual(prevProps.className, this.props.className)) {
      this.resize();
    }
  }
  updateEChartsOption() {
    const option = this.props.option;
    const loadingOption = this.props.loadingOption || null;
    const { lazyUpdate, showLoading } = this.props;
    const echartInstance = this.getEchartsInstance();
    echartInstance.setOption(option, { replaceMerge: ["series"], lazyUpdate });
    if (showLoading) {
      echartInstance.showLoading(loadingOption);
    } else {
      echartInstance.hideLoading();
    }
    return echartInstance;
  }
  offEvents(instance, events2) {
    if (!events2) return;
    for (const eventName in events2) {
      if (typeof eventName === "string") {
        instance.off(eventName);
      }
    }
  }
}
function pick(obj, keys) {
  const r = {};
  keys.forEach((key) => {
    r[key] = obj[key];
  });
  return r;
}
const SKELETON_HEIGHT = 240;
const SkeletonBarsGrid = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "space-between",
  position: "relative",
  padding: theme.spacing(0, 2),
  // Linear grid effect
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    right: 0,
    left: 0,
    bottom: 0,
    backgroundSize: theme.spacing(4, 4),
    backgroundImage: `linear-gradient(to bottom, ${theme.palette.grey[50]} 1px, transparent 1px)`,
    transform: "scaleY(-1)"
  }
}));
const SkeletonGraphGrid = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "space-between",
  // Square grid effect
  backgroundImage: `linear-gradient(${theme.palette.grey[50]} 0.5px, transparent 0.5px, transparent calc(100% - 0.5px), ${theme.palette.grey[50]} calc(100% - 0.5px)), linear-gradient(90deg, ${theme.palette.grey[50]} 0.5px, transparent 0.5px, transparent calc(100% - 0.5px), ${theme.palette.grey[50]} calc(100% - 0.5px))`,
  backgroundSize: "8.33% 20%",
  border: `0.5px solid ${theme.palette.grey[50]}`
}));
const SkeletonBarItem = styled(Skeleton)(({ theme }) => ({
  flex: 1,
  maxWidth: theme.spacing(12),
  "& + &": {
    marginLeft: theme.spacing(1)
  }
}));
const SkeletonThinBarItem = styled(Skeleton)(({ theme }) => ({
  flex: 1,
  maxWidth: theme.spacing(8),
  "& + &": {
    marginLeft: "1px"
  }
}));
const SkeletonMask = styled(Skeleton)(({ theme }) => ({
  backgroundColor: theme.palette.common.white
}));
const SkeletonSolid = styled(Skeleton)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100]
}));
function BarSkeleton({ height }) {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Box, { mb: 2, children: /* @__PURE__ */ jsx(Skeleton, { width: 48, height: 8 }) }),
    /* @__PURE__ */ jsxs(SkeletonBarsGrid, { style: { height: height || SKELETON_HEIGHT }, children: [
      /* @__PURE__ */ jsx(SkeletonBarItem, { variant: "rectangular", height: "20%" }),
      /* @__PURE__ */ jsx(SkeletonBarItem, { variant: "rectangular", height: "40%" }),
      /* @__PURE__ */ jsx(SkeletonBarItem, { variant: "rectangular", height: "60%" }),
      /* @__PURE__ */ jsx(SkeletonBarItem, { variant: "rectangular", height: "20%" }),
      /* @__PURE__ */ jsx(SkeletonBarItem, { variant: "rectangular", height: "80%" })
    ] })
  ] });
}
function useSkeleton(isDataLoading) {
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [skeletonEverBeenShown, setSkeletonEverBeenShown] = useState(false);
  useEffect(() => {
    if (isDataLoading && !skeletonEverBeenShown) {
      setShowSkeleton(true);
      setSkeletonEverBeenShown(true);
    }
    if (!isDataLoading) {
      setShowSkeleton(false);
    }
  }, [isDataLoading, skeletonEverBeenShown]);
  return { showSkeleton };
}
const IS_TOUCH_SCREEN$1 = detectTouchscreen();
const OptionsSelectedBar$2 = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  "& .MuiTypography-caption": {
    color: theme.palette.text.secondary
  }
}));
const SelectAllButton = styled(Link)(({ theme }) => ({
  ...theme.typography.caption,
  cursor: "pointer"
}));
function BarWidgetUI(props) {
  const {
    yAxisData,
    xAxisData,
    series,
    selectedBars,
    onSelectedBarsChange,
    tooltip,
    tooltipFormatter,
    labels,
    colors,
    xAxisFormatter,
    yAxisFormatter,
    stacked,
    height,
    filterable,
    animation,
    isLoading
  } = useProcessedProps(props);
  const isMultiSeries = series.length > 1;
  const theme = useTheme();
  const intl = useIntl();
  const intlConfig = useImperativeIntl(intl);
  const { showSkeleton } = useSkeleton(!!isLoading);
  const tooltipOptions = useMemo(
    () => ({
      show: tooltip,
      trigger: "axis",
      padding: [parseInt(theme.spacing(0.5)), parseInt(theme.spacing(1))],
      borderWidth: 0,
      textStyle: {
        ...theme.typography.caption,
        fontSize: 11,
        color: theme.palette.common.white
      },
      backgroundColor: theme.palette.black[90],
      position: function(point, _params, _dom, _rect, size) {
        const position = { top: 0 };
        if (size.contentSize[0] < size.viewSize[0] - point[0]) {
          position.left = point[0];
        } else {
          position.right = size.viewSize[0] - point[0];
        }
        return position;
      },
      formatter: (params) => tooltipFormatter(params, yAxisFormatter)
    }),
    [theme, tooltip, tooltipFormatter, yAxisFormatter]
  );
  const xAxisDataWithLabels = useMemo(
    () => xAxisData.map(
      (name) => processFormatterResult(xAxisFormatter(labels[name] ?? String(name)))
    ),
    [xAxisData, labels, xAxisFormatter]
  );
  const xAxisOptions = useMemo(
    () => ({
      type: "category",
      axisLine: {
        show: false
      },
      axisTick: {
        show: false
      },
      axisLabel: {
        ...theme.typography.overlineDelicate,
        padding: [parseInt(theme.spacing(0.5)), 0, 0, 0]
      },
      data: xAxisDataWithLabels
    }),
    [theme, xAxisDataWithLabels]
  );
  const maxValue = useMemo(() => {
    let dataValues = [];
    if (stacked) {
      dataValues = yAxisData.reduce((acc, row) => {
        row.forEach(
          (value, idx) => acc[idx] = (acc[idx] ?? 0) + (value ?? Number.MIN_SAFE_INTEGER)
        );
        return acc;
      }, []);
    } else {
      dataValues = yAxisData.flat().map((value) => value ?? Number.MIN_SAFE_INTEGER);
    }
    return Math.max(...dataValues);
  }, [yAxisData, stacked]);
  const yAxisOptions = useMemo(
    () => ({
      type: "value",
      axisLabel: {
        margin: 0,
        verticalAlign: "bottom",
        padding: [0, 0, parseInt(theme.spacing(1.25)), 0],
        show: true,
        showMaxLabel: true,
        showMinLabel: false,
        inside: true,
        color: (value) => value >= maxValue ? theme.palette.black[60] : "transparent",
        ...theme.typography.overlineDelicate,
        formatter: (v) => processFormatterResult(yAxisFormatter(v))
      },
      axisLine: {
        show: false
      },
      axisTick: {
        show: false
      },
      splitLine: {
        show: true,
        onZero: false,
        lineStyle: {
          color: theme.palette.black[4]
        }
      }
    }),
    [theme, maxValue, yAxisFormatter]
  );
  const seriesOptions = useMemo(
    () => yAxisData.map((row, componentIdx) => ({
      type: "bar",
      name: series[componentIdx] ?? "",
      animation,
      barMaxWidth: 100,
      data: row.map((value, dataIdx) => {
        const isSelected = selectedBars.some(
          ([sDataIdx, sComponentIdx = 0]) => sDataIdx === dataIdx && sComponentIdx === componentIdx
        );
        const isDisabled = !!selectedBars.length && !isSelected;
        return {
          value,
          ...isDisabled && {
            itemStyle: { color: theme.palette.black[25] },
            disabled: true
          }
        };
      }),
      ...stacked && { stack: "total" },
      ...!IS_TOUCH_SCREEN$1 && {
        emphasis: {
          focus: "series",
          itemStyle: {
            color: darken(colors[componentIdx] ?? "#000", 0.25)
          }
        }
      }
    })),
    [animation, colors, series, yAxisData, selectedBars, stacked, theme]
  );
  const options = useMemo(
    () => ({
      grid: {
        left: xAxisDataWithLabels.length >= 4 ? calculateMargin(
          String(xAxisDataWithLabels[0]),
          xAxisDataWithLabels.length
        ) : 0,
        top: parseInt(theme.spacing(2)),
        right: xAxisDataWithLabels.length >= 4 ? calculateMargin(
          String(xAxisDataWithLabels[xAxisDataWithLabels.length - 1]),
          xAxisDataWithLabels.length
        ) : 0,
        bottom: 0,
        containLabel: true
      },
      axisPointer: {
        lineStyle: {
          color: theme.palette.black[40]
        }
      },
      color: colors,
      tooltip: tooltipOptions,
      xAxis: xAxisOptions,
      yAxis: yAxisOptions,
      series: seriesOptions
    }),
    [
      xAxisDataWithLabels,
      theme,
      colors,
      tooltipOptions,
      xAxisOptions,
      yAxisOptions,
      seriesOptions
    ]
  );
  const clearBars = () => {
    if (onSelectedBarsChange) {
      onSelectedBarsChange([], []);
    }
  };
  const handleClearPress = (e) => {
    if (e.key === "Enter") {
      clearBars();
    }
  };
  const clickEvent = useCallback(
    (params) => {
      if (onSelectedBarsChange) {
        const selectedBarsCp = [...selectedBars];
        const { dataIndex, componentIndex } = params;
        const selectedIdx = selectedBarsCp.findIndex(
          ([sDataIndex, sComponentIndex = 0]) => dataIndex === sDataIndex && componentIndex === sComponentIndex
        );
        if (selectedIdx === -1) {
          selectedBarsCp.push([dataIndex, componentIndex]);
        } else {
          selectedBarsCp.splice(selectedIdx, 1);
        }
        const selectedBarsAsObject = selectedBarsCp.map(
          ([sDataIndex, sComponentIndex = 0]) => ({
            xAxis: xAxisData[sDataIndex],
            serie: series[sComponentIndex],
            yAxis: yAxisData[sComponentIndex][sDataIndex]
          })
        );
        if (isMultiSeries) {
          onSelectedBarsChange(selectedBarsCp, selectedBarsAsObject);
        } else {
          onSelectedBarsChange(
            selectedBarsCp.map((s) => s[0]),
            selectedBarsAsObject
          );
        }
      }
    },
    [
      yAxisData,
      onSelectedBarsChange,
      selectedBars,
      xAxisData,
      series,
      isMultiSeries
    ]
  );
  const onEvents = useMemo(
    () => filterable ? {
      click: clickEvent
    } : {},
    [filterable, clickEvent]
  );
  if (showSkeleton) {
    return /* @__PURE__ */ jsx(BarSkeleton, { height });
  }
  return /* @__PURE__ */ jsxs("div", { children: [
    onSelectedBarsChange && /* @__PURE__ */ jsxs(OptionsSelectedBar$2, { container: true, children: [
      /* @__PURE__ */ jsx(Typography, { variant: "caption", children: selectedBars.length > 0 ? intlConfig.formatMessage(
        { id: "c4r.widgets.bar.selectedItems" },
        { items: selectedBars.length }
      ) : intlConfig.formatMessage({ id: "c4r.widgets.bar.all" }) }),
      selectedBars && selectedBars.length > 0 && /* @__PURE__ */ jsx(
        SelectAllButton,
        {
          onClick: () => clearBars(),
          onKeyDown: handleClearPress,
          underline: "hover",
          tabIndex: 0,
          children: intlConfig.formatMessage({ id: "c4r.widgets.bar.clear" })
        }
      )
    ] }),
    !!options && /* @__PURE__ */ jsx(
      ReactEcharts,
      {
        option: options,
        lazyUpdate: true,
        notMerge: true,
        onEvents,
        style: { height }
      }
    )
  ] });
}
function calculateMargin(label = "", amountCategories = 0) {
  if (amountCategories <= 15 && label.length <= 8) return 0;
  return label.length * 8.5 / 6;
}
const REST_CATEGORY = "__rest__";
const OTHERS_CATEGORY_NAME = "Others";
var OrderTypes = /* @__PURE__ */ ((OrderTypes2) => {
  OrderTypes2["Ranking"] = "ranking";
  OrderTypes2["Fixed"] = "fixed";
  return OrderTypes2;
})(OrderTypes || {});
const CategoriesWrapper = styled(Grid)(({ theme }) => ({
  maxHeight: theme.spacing(40),
  overflow: "auto",
  padding: 0
}));
const CategoryItemGroup = styled(Grid, {
  shouldForwardProp: (prop) => prop !== "selectable" && prop !== "name" && prop !== "unselected"
})(({ theme, selectable, name, unselected }) => {
  return {
    flexDirection: "row",
    maxWidth: "100%",
    padding: theme.spacing(0.5, 0.25),
    margin: 0,
    "> .MuiGrid-item": {
      paddingTop: 0,
      paddingLeft: 0
    },
    "&:focus-visible": {
      outline: `none !important`,
      boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main} !important`
    },
    ...unselected && {
      color: theme.palette.text.disabled,
      ".progressbar div": {
        backgroundColor: theme.palette.text.disabled
      }
    },
    ...name !== REST_CATEGORY && selectable && {
      cursor: "pointer",
      flexWrap: "nowrap",
      "&:hover .progressbar div": {
        backgroundColor: theme.palette.secondary.dark
      }
    },
    ...name === REST_CATEGORY && {
      cursor: "default",
      ".progressbar div": {
        backgroundColor: theme.palette.text.disabled
      }
    }
  };
});
const OptionsSelectedBar$1 = styled(Grid)(
  ({ theme: { spacing, palette } }) => ({
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: spacing(1.5),
    paddingRight: spacing(1),
    "& .MuiTypography-caption": {
      color: palette.text.secondary
    }
  })
);
const ProgressBar = styled(Grid)(({ theme }) => ({
  height: theme.spacing(0.5),
  width: "100%",
  margin: theme.spacing(0.5, 0, 0.25, 0),
  borderRadius: theme.spacing(0.5),
  backgroundColor: theme.palette.action.disabledBackground,
  "& div": {
    width: 0,
    height: "100%",
    borderRadius: theme.spacing(0.5),
    backgroundColor: theme.palette.secondary.main,
    transition: `background-color ${theme.transitions.easing.sharp} ${theme.transitions.duration.shortest}ms,
                 width ${theme.transitions.easing.sharp} ${theme.transitions.duration.complex}ms`
  }
}));
const CategoryLabelWrapper = styled(Grid, {
  shouldForwardProp: (prop) => prop !== "isSelectable"
})(({ theme, isSelectable }) => {
  return {
    ...isSelectable && {
      width: `calc(100% - ${theme.spacing(4)})`
    }
  };
});
const CategoryLabel = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
  marginRight: theme.spacing(2)
}));
const LinkAsButton = styled(Link)(({ theme }) => ({
  ...theme.typography.caption,
  cursor: "pointer",
  "& + hr": {
    margin: theme.spacing(0, 1)
  }
}));
const CategoriesRoot = styled(Box)(({ theme }) => ({
  ...theme.typography.body2
}));
const HiddenButton = styled(Button)(({ theme }) => ({
  position: "absolute",
  left: "-999px",
  top: "-1px",
  width: "1px",
  height: "1px",
  display: "inline-flex",
  "&:focus-visible": {
    position: "static",
    width: "auto",
    height: "auto",
    marginTop: theme.spacing(2)
  }
}));
const SkeletonProgressbar = styled(Skeleton)(({ theme }) => ({
  marginTop: theme.spacing(1.25),
  marginBottom: theme.spacing(1.75)
}));
const isOdd = (num) => num % 2 === 1;
const CategorySkeleton = () => {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Skeleton, { width: 48, height: 8 }),
    /* @__PURE__ */ jsx(CategoriesWrapper, { container: true, mt: 3, children: [...Array(6)].map((_, i) => /* @__PURE__ */ jsxs(CategoryItemGroup, { container: true, spacing: 1, children: [
      /* @__PURE__ */ jsxs(Grid, { container: true, item: true, direction: "row", justifyContent: "space-between", children: [
        /* @__PURE__ */ jsx(Skeleton, { width: isOdd(i) ? 72 : 48, height: 8 }),
        /* @__PURE__ */ jsx(Skeleton, { width: 48, height: 8 })
      ] }),
      /* @__PURE__ */ jsx(SkeletonProgressbar, { height: 4, width: "100%" })
    ] }, i)) })
  ] });
};
function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}
function animateValue({
  start,
  end,
  duration,
  drawFrame,
  requestRef
}) {
  const startAndEndZero = start === 0 && end === 0;
  if (start === end && !startAndEndZero) {
    return;
  }
  const range = end - start;
  let current = start;
  const step = range / (duration / 1e3 * 60);
  const animate = () => {
    current += step;
    drawFrame(Math.floor(current));
    if (step > 0 && current < end || step < 0 && current > end) {
      requestRef.current = requestAnimationFrame(animate);
    } else {
      drawFrame(end);
    }
  };
  requestRef.current = requestAnimationFrame(animate);
}
function animateValues({
  start,
  end,
  duration,
  drawFrame,
  requestRef
}) {
  const isEqual2 = start.length === end.length && start.every((val, i) => val.value === end[i].value);
  if (isEqual2) {
    return;
  }
  let currentValues = end.map(
    (elem, i) => start[i] && start[i].name === elem.name ? { ...elem, value: start[i].value } : elem
  );
  let currentFrame = 0;
  const ranges = currentValues.map((elem, i) => end[i].value - elem.value);
  const noChanges = ranges.every((val) => val === 0);
  if (noChanges) {
    drawFrame(end);
    return;
  }
  const frames = duration / 1e3 * 60;
  const steps = ranges.map((val) => val / frames);
  const animate = () => {
    if (currentFrame < frames) {
      currentValues = currentValues.map((elem, i) => {
        const value = Math.floor(elem.value + steps[i]);
        const prevValue = i > 0 && Math.floor(elem.value + steps[i - 1]);
        return {
          ...elem,
          value: value === prevValue ? end[i].value : value
        };
      });
      drawFrame(currentValues);
      currentFrame++;
      requestRef.current = requestAnimationFrame(animate);
    } else {
      drawFrame(end);
    }
  };
  requestRef.current = requestAnimationFrame(animate);
}
const EMPTY_ARR$7 = [];
const EMPTY_OBJ$5 = {};
const IDENTITY$4 = (v) => v;
const EMPTY_FN$a = () => void 0;
function CategoryWidgetUI({
  data,
  formatter = IDENTITY$4,
  labels = EMPTY_OBJ$5,
  maxItems = 5,
  othersValue,
  order = OrderTypes.Ranking,
  selectedCategories = EMPTY_ARR$7,
  onSelectedCategoriesChange = EMPTY_FN$a,
  animation = true,
  filterable = true,
  searchable = true,
  isLoading
}) {
  const theme = useTheme();
  const [sortedData, setSortedData] = useState([]);
  const [maxValue, setMaxValue] = useState(1);
  const [showAll, setShowAll] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [blockedCategories, setBlockedCategories] = useState([]);
  const [tempBlockedCategories, setTempBlockedCategories] = useState(
    []
  );
  const [animValues, setAnimValues] = useState([]);
  const requestRef = useRef(null);
  const searchRef = useRef();
  const prevAnimValues = usePrevious(animValues);
  const referencedPrevAnimValues = useRef([]);
  const { showSkeleton } = useSkeleton(!!isLoading);
  const intl = useIntl();
  const intlConfig = useImperativeIntl(intl);
  function sortBlockedSameAsData(blockedCategories2) {
    const blockedCategoriesSet = new Set(blockedCategories2);
    let sortedByOrderData = data;
    if (order === OrderTypes.Ranking) {
      sortedByOrderData = [...data].sort(
        (a, b) => (b.value ?? 0) - (a.value ?? 0)
      );
    }
    return sortedByOrderData.reduce((acum, elem) => {
      if (blockedCategoriesSet.has(elem.name)) {
        acum.push(elem.name);
      }
      return acum;
    }, []);
  }
  function handleCategorySelected(name) {
    if (name !== REST_CATEGORY) {
      let categories;
      if (!selectedCategories.includes(name)) {
        categories = [...selectedCategories, name];
      } else {
        categories = selectedCategories.filter((c) => c !== name);
      }
      if (onSelectedCategoriesChange) {
        onSelectedCategoriesChange(categories);
      }
    }
  }
  const handleClearClicked = () => onSelectedCategoriesChange([]);
  const handleClearPress = (e) => {
    if (e.key === "Enter") {
      handleClearClicked();
    }
  };
  const handleUnblockClicked = () => {
    onSelectedCategoriesChange([]);
    setBlockedCategories([]);
  };
  const handleUnblockPress = (e) => {
    if (e.key === "Enter") {
      handleUnblockClicked();
    }
  };
  const handleBlockClicked = () => {
    setBlockedCategories(sortBlockedSameAsData(selectedCategories));
  };
  const handleBlockPress = (e) => {
    if (e.key === "Enter") {
      handleBlockClicked();
    }
  };
  const handleApplyClicked = () => {
    const blockedCategoriesOrdered = sortBlockedSameAsData(
      tempBlockedCategories
    );
    onSelectedCategoriesChange([...blockedCategoriesOrdered]);
    setBlockedCategories([...blockedCategoriesOrdered]);
    setTempBlockedCategories([]);
    setShowAll(false);
    setSearchValue("");
  };
  const handleApplyPress = (e) => {
    if (e.key === "Enter") {
      handleApplyClicked();
    }
  };
  const handleCancelClicked = () => {
    setSearchValue("");
    setShowAll(false);
  };
  const handleCategoryBlocked = (name) => {
    if (name !== REST_CATEGORY) {
      let categories;
      if (!tempBlockedCategories.includes(name)) {
        categories = [...tempBlockedCategories, name];
      } else {
        categories = tempBlockedCategories.filter((c) => c !== name);
      }
      setTempBlockedCategories(categories);
    }
  };
  const handleSearchChange = (event) => {
    setSearchValue(event.currentTarget.value);
  };
  const handleShowAllCategoriesClicked = () => {
    setShowAll(true);
    setTempBlockedCategories([...selectedCategories]);
  };
  const compressList = useCallback(
    (list) => {
      if (!showAll) {
        if (!blockedCategories.length) {
          const main = list.slice(0, maxItems);
          if (main.length < list.length && othersValue !== void 0) {
            if (othersValue && !isNaN(othersValue)) {
              const othersEntry = {
                name: REST_CATEGORY,
                value: othersValue
              };
              return [...main, othersEntry];
            } else {
              return main;
            }
          } else {
            return main;
          }
        } else {
          const main = blockedCategories.reduce((acum, name) => {
            const categoryElem = list.find((elem) => elem.name === name);
            acum.push({
              name,
              value: categoryElem ? categoryElem.value : null
            });
            return acum;
          }, []);
          return main;
        }
      } else {
        if (!searchValue) {
          return list;
        }
        return list.filter((elem) => {
          var _a;
          if (!elem.name) {
            return false;
          }
          const name = String(elem.name);
          if (name.toLowerCase().includes(searchValue.toLowerCase())) {
            return true;
          }
          if ((_a = labels[name]) == null ? void 0 : _a.toLowerCase().includes(searchValue.toLowerCase())) {
            return true;
          }
          return false;
        });
      }
    },
    [blockedCategories, labels, maxItems, searchValue, showAll, othersValue]
  );
  const getCategoriesCount = useCallback(() => {
    const blocked = blockedCategories.length;
    return blocked ? data.length - blocked : data.length - maxItems;
  }, [data, maxItems, blockedCategories]);
  const getCategoryLabel = useCallback(
    (name) => {
      if (name === REST_CATEGORY) {
        return `Others ${searchable ? "" : `(${getCategoriesCount()})`}`;
      } else {
        return labels[String(name)] ?? String(name);
      }
    },
    [getCategoriesCount, labels, searchable]
  );
  const getProgressbarLength = useCallback(
    (value) => {
      return value >= maxValue ? value != null ? "100%" : 0 : `${(value || 0) * 100 / maxValue}%`;
    },
    [maxValue]
  );
  useEffect(() => {
    if (selectedCategories.length === 0) {
      setBlockedCategories([]);
    }
  }, [selectedCategories]);
  useEffect(() => {
    if (data) {
      if (order === OrderTypes.Ranking) {
        const sorted = [...data].sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
        const compressed = compressList(sorted);
        if (compressed.length > 0) {
          setMaxValue(compressed[0].value ?? 0);
        } else {
          setMaxValue(1);
        }
        setSortedData(compressed);
      } else if (order === OrderTypes.Fixed) {
        setMaxValue(
          data.reduce(
            (max, e) => typeof e.value === "number" ? Math.max(max, e.value) : max,
            Number.NEGATIVE_INFINITY
          )
        );
        const compressed = compressList(data);
        setSortedData(compressed);
      }
    }
  }, [
    blockedCategories,
    compressList,
    data,
    labels,
    maxItems,
    order,
    searchValue,
    showAll
  ]);
  useEffect(() => {
    if (prevAnimValues) {
      referencedPrevAnimValues.current = prevAnimValues;
    }
  }, [prevAnimValues]);
  useEffect(() => {
    if (animation) {
      animateValues({
        start: (referencedPrevAnimValues.current || []).map((d) => ({
          ...d,
          value: d.value ?? 0
        })),
        end: sortedData.map((d) => ({ ...d, value: d.value ?? 0 })),
        duration: 500,
        drawFrame: (val) => setAnimValues(val),
        requestRef
      });
      return () => {
        if (requestRef.current) {
          cancelAnimationFrame(requestRef.current);
        }
      };
    } else {
      setAnimValues(sortedData);
    }
  }, [animation, sortedData]);
  useEffect(() => {
    if (showAll && searchRef.current) {
      searchRef.current.focus();
    }
  }, [showAll, searchRef]);
  function CategoryItem2({
    data: data2,
    onCategoryClick
  }) {
    const value = formatter(data2.value ?? 0);
    const [isOverflowed, setIsOverflowed] = useState(false);
    const textElementRef = useRef(null);
    const compareSize = () => {
      var _a, _b;
      const compare = (((_a = textElementRef == null ? void 0 : textElementRef.current) == null ? void 0 : _a.scrollWidth) ?? 0) > (((_b = textElementRef == null ? void 0 : textElementRef.current) == null ? void 0 : _b.clientWidth) ?? 0);
      setIsOverflowed(compare);
    };
    useEffect(() => {
      compareSize();
      window.addEventListener("resize", compareSize);
      return () => {
        window.removeEventListener("resize", compareSize);
      };
    }, []);
    const onCategoryPress = (e) => {
      if (e.key === "Enter") {
        onCategoryClick();
      }
    };
    const unselected = !showAll && selectedCategories.length > 0 && !selectedCategories.includes(data2.name);
    return /* @__PURE__ */ jsxs(
      CategoryItemGroup,
      {
        container: true,
        direction: "row",
        spacing: 1,
        onClick: filterable ? onCategoryClick : EMPTY_FN$a,
        onKeyDown: filterable ? onCategoryPress : EMPTY_FN$a,
        selectable: filterable,
        unselected,
        name: data2.name === REST_CATEGORY ? REST_CATEGORY : "",
        tabIndex: filterable ? 0 : -1,
        children: [
          filterable && showAll && /* @__PURE__ */ jsx(Grid, { item: true, mr: 1, children: /* @__PURE__ */ jsx(
            Checkbox,
            {
              checked: tempBlockedCategories.includes(data2.name),
              onChange: (e) => {
                if (e.target.checked) {
                  setTempBlockedCategories([
                    ...tempBlockedCategories,
                    data2.name
                  ]);
                } else {
                  setTempBlockedCategories(
                    tempBlockedCategories.filter((c) => c !== data2.name)
                  );
                }
              },
              tabIndex: -1
            }
          ) }),
          /* @__PURE__ */ jsxs(CategoryLabelWrapper, { container: true, item: true, xs: true, isSelectable: showAll, children: [
            /* @__PURE__ */ jsxs(
              Grid,
              {
                container: true,
                item: true,
                direction: "row",
                justifyContent: "space-between",
                wrap: "nowrap",
                children: [
                  /* @__PURE__ */ jsx(
                    Tooltip,
                    {
                      title: getCategoryLabel(data2.name),
                      disableHoverListener: !isOverflowed,
                      children: /* @__PURE__ */ jsx(CategoryLabel, { variant: "body2", noWrap: true, ref: textElementRef, children: getCategoryLabel(data2.name) })
                    }
                  ),
                  typeof value === "object" && value !== null ? /* @__PURE__ */ jsxs("span", { children: [
                    value.prefix,
                    value.value,
                    value.suffix
                  ] }) : /* @__PURE__ */ jsx("span", { children: value })
                ]
              }
            ),
            /* @__PURE__ */ jsx(ProgressBar, { className: "progressbar", item: true, children: /* @__PURE__ */ jsx("div", { style: { width: getProgressbarLength(data2.value ?? 0) } }) })
          ] })
        ]
      }
    );
  }
  if ((data == null ? void 0 : data.length) === 0 || showSkeleton) return /* @__PURE__ */ jsx(CategorySkeleton, {});
  const CategoryRow = ({
    index,
    style
  }) => {
    const d = animValues[index];
    if (!d) return null;
    return /* @__PURE__ */ jsx("div", { style, children: /* @__PURE__ */ jsx(
      CategoryItem2,
      {
        data: d,
        onCategoryClick: () => showAll ? handleCategoryBlocked(d.name) : handleCategorySelected(d.name)
      }
    ) });
  };
  const getFixedSizeListHeight = () => {
    const ITEM_HEIGHT = 38;
    const MAX_HEIGHT = 320;
    if (showAll) {
      return MAX_HEIGHT;
    }
    if (animValues.length <= maxItems) {
      return animValues.length * ITEM_HEIGHT;
    }
    if (blockedCategories.length > 0) {
      return blockedCategories.length * ITEM_HEIGHT;
    }
    return (maxItems + 1) * ITEM_HEIGHT;
  };
  return /* @__PURE__ */ jsxs(CategoriesRoot, { children: [
    filterable && sortedData.length > 0 && /* @__PURE__ */ jsxs(OptionsSelectedBar$1, { container: true, children: [
      /* @__PURE__ */ jsx(Typography, { variant: "caption", children: selectedCategories.length > 0 ? intlConfig.formatMessage(
        { id: "c4r.widgets.category.selectedItems" },
        { items: selectedCategories.length }
      ) : intlConfig.formatMessage({ id: "c4r.widgets.category.all" }) }),
      showAll ? /* @__PURE__ */ jsx(
        LinkAsButton,
        {
          onClick: handleApplyClicked,
          onKeyDown: handleApplyPress,
          underline: "hover",
          tabIndex: 0,
          "data-testid": "primaryApplyButton",
          children: intlConfig.formatMessage({ id: "c4r.widgets.category.apply" })
        }
      ) : blockedCategories.length > 0 ? /* @__PURE__ */ jsx(
        LinkAsButton,
        {
          onClick: handleUnblockClicked,
          onKeyDown: handleUnblockPress,
          underline: "hover",
          tabIndex: 0,
          children: intlConfig.formatMessage({ id: "c4r.widgets.category.unlock" })
        }
      ) : selectedCategories.length > 0 && /* @__PURE__ */ jsxs(Grid, { container: true, direction: "row", justifyContent: "flex-end", item: true, xs: true, children: [
        /* @__PURE__ */ jsx(
          LinkAsButton,
          {
            onClick: handleBlockClicked,
            onKeyDown: handleBlockPress,
            underline: "hover",
            tabIndex: 0,
            children: intlConfig.formatMessage({
              id: "c4r.widgets.category.lock"
            })
          }
        ),
        /* @__PURE__ */ jsx(Divider, { orientation: "vertical", flexItem: true }),
        /* @__PURE__ */ jsx(
          LinkAsButton,
          {
            onClick: handleClearClicked,
            onKeyDown: handleClearPress,
            underline: "hover",
            tabIndex: 0,
            children: intlConfig.formatMessage({
              id: "c4r.widgets.category.clear"
            })
          }
        )
      ] })
    ] }),
    data.length > maxItems && showAll && /* @__PURE__ */ jsxs(OptionsSelectedBar$1, { container: true, children: [
      /* @__PURE__ */ jsx(
        TextField,
        {
          size: "small",
          sx: { mt: -0.5 },
          placeholder: intlConfig.formatMessage({
            id: "c4r.widgets.category.search"
          }),
          onChange: handleSearchChange,
          InputProps: {
            startAdornment: /* @__PURE__ */ jsx(InputAdornment, { position: "start", children: /* @__PURE__ */ jsx(Search, {}) })
          },
          inputProps: {
            tabIndex: 0,
            ref: searchRef
          }
        }
      ),
      /* @__PURE__ */ jsx(HiddenButton, { size: "small", onClick: handleCancelClicked, children: intlConfig.formatMessage({ id: "c4r.widgets.category.cancel" }) })
    ] }),
    /* @__PURE__ */ jsx(CategoriesWrapper, { container: true, item: true, children: animValues.length ? /* @__PURE__ */ jsx(
      FixedSizeList,
      {
        height: getFixedSizeListHeight(),
        width: "100%",
        itemCount: animValues.length,
        itemSize: theme.spacingValue * 4.5,
        children: CategoryRow
      }
    ) : /* @__PURE__ */ jsxs(Box, { children: [
      /* @__PURE__ */ jsx(Typography, { variant: "body2", children: intlConfig.formatMessage({
        id: "c4r.widgets.category.noResults"
      }) }),
      /* @__PURE__ */ jsx(Typography, { component: "p", variant: "caption", mb: 2, children: intlConfig.formatMessage(
        { id: "c4r.widgets.category.noResultsMessage" },
        { searchValue }
      ) })
    ] }) }),
    showAll && /* @__PURE__ */ jsx(HiddenButton, { size: "small", onClick: handleApplyClicked, children: intlConfig.formatMessage({ id: "c4r.widgets.category.apply" }) }),
    data.length > maxItems && searchable ? showAll ? /* @__PURE__ */ jsx(Box, { mt: 1.5, children: /* @__PURE__ */ jsx(
      Button,
      {
        size: "small",
        color: "primary",
        onClick: handleCancelClicked,
        "data-testid": "primaryCancelButton",
        children: intlConfig.formatMessage({ id: "c4r.widgets.category.cancel" })
      }
    ) }) : /* @__PURE__ */ jsx(Box, { mt: 1.5, children: /* @__PURE__ */ jsx(
      Button,
      {
        "data-testid": "show-all-categories-button",
        size: "small",
        color: "primary",
        startIcon: /* @__PURE__ */ jsx(Search, {}),
        onClick: handleShowAllCategoriesClicked,
        children: intlConfig.formatMessage(
          { id: "c4r.widgets.category.searchInfo" },
          { elements: getCategoriesCount() }
        )
      }
    ) }) : null
  ] });
}
const FormulaSkeleton = () => {
  return /* @__PURE__ */ jsx(Box, { pt: 0.5, children: /* @__PURE__ */ jsx(Skeleton, { height: 24, width: 120 }) });
};
const Prefix = styled("span")(() => ({
  marginRight: "2px"
}));
const Suffix = styled("span")(() => ({
  marginLeft: "2px"
}));
const NO_DATA = "-";
const IDENTITY$3 = (v) => v;
function FormulaWidgetUI({
  data = NO_DATA,
  formatter = IDENTITY$3,
  animation = true,
  isLoading = false
}) {
  const [value, setValue] = useState(NO_DATA);
  const requestRef = useRef(null);
  const prevValue = usePrevious(value);
  const referencedPrevValue = useRef(prevValue);
  const { showSkeleton } = useSkeleton(!!isLoading);
  useEffect(() => {
    if (typeof data === "number" && animation) {
      animateValue({
        start: referencedPrevValue.current || 0,
        end: data,
        duration: 500,
        drawFrame: (val) => setValue(val),
        requestRef
      });
    } else if (typeof data === "object" && animation && data && referencedPrevValue.current && data.value !== null && data.value !== void 0) {
      animateValue({
        start: Number(
          referencedPrevValue.current.value
        ),
        end: Number(data.value),
        duration: 1e3,
        drawFrame: (val) => setValue({ value: val, prefix: data.prefix, suffix: data.suffix }),
        requestRef
      });
    } else {
      setValue(data ?? NO_DATA);
    }
    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [animation, data, setValue]);
  const formattedValue = value === NO_DATA ? NO_DATA : formatter(value);
  const isComplexFormat = typeof formattedValue === "object" && formattedValue !== null;
  const isDisabled = formattedValue === NO_DATA;
  if (showSkeleton) {
    return /* @__PURE__ */ jsx(FormulaSkeleton, {});
  }
  return isComplexFormat ? /* @__PURE__ */ jsxs(Typography, { variant: "h5", component: "div", weight: "medium", children: [
    /* @__PURE__ */ jsx(Prefix, { children: formattedValue.prefix }),
    formattedValue.value,
    /* @__PURE__ */ jsx(Suffix, { children: formattedValue.suffix })
  ] }) : /* @__PURE__ */ jsx(
    Typography,
    {
      variant: "h5",
      component: "div",
      weight: "medium",
      color: isDisabled ? "text.disabled" : "default",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      overflow: "hidden",
      children: formattedValue
    }
  );
}
const events$1 = {};
let initialTimeWindow$1 = null;
function useHistogramInteractivity({
  data,
  filterable,
  selectedBars,
  onSelectedBarsChange,
  echartsInstance
}) {
  const theme = useTheme();
  const [markArea, setMarkArea] = useState([]);
  const zr = useMemo(() => echartsInstance == null ? void 0 : echartsInstance.getZr(), [echartsInstance]);
  useEffect(() => {
    function mouseDownEvent(ev) {
      if (echartsInstance) {
        const [x] = echartsInstance.convertFromPixel({ seriesIndex: 0 }, [
          ev.offsetX,
          ev.offsetY
        ]);
        initialTimeWindow$1 = x ?? null;
      }
    }
    function mouseMoveEvent(ev) {
      if (echartsInstance && initialTimeWindow$1 !== null) {
        const [x] = echartsInstance.convertFromPixel({ seriesIndex: 0 }, [
          ev.offsetX,
          ev.offsetY
        ]);
        if (initialTimeWindow$1 === x) {
          setMarkArea([]);
        } else {
          setMarkArea([initialTimeWindow$1, x].sort((a, b) => a - b));
        }
      }
    }
    if (echartsInstance && filterable) {
      const mouseDownCleanUp = addEventWithCleanUp$1(
        zr,
        "mousedown",
        mouseDownEvent
      );
      const mouseMoveCleanUp = addEventWithCleanUp$1(
        zr,
        "mousemove",
        mouseMoveEvent
      );
      return () => {
        mouseDownCleanUp == null ? void 0 : mouseDownCleanUp();
        mouseMoveCleanUp == null ? void 0 : mouseMoveCleanUp();
      };
    }
  }, [zr, echartsInstance, filterable]);
  useEffect(() => {
    function mouseUpEvent() {
      if (markArea.length === 2 && onSelectedBarsChange) {
        const newSelectedBars = data.reduce((acc, item, idx) => {
          if (markArea[0] >= item[0] && markArea[0] <= item[1] || markArea[1] >= item[0] && markArea[1] <= item[1] || markArea[0] <= item[0] && markArea[1] >= item[1]) {
            acc.push(idx);
          }
          return acc;
        }, []);
        onSelectedBarsChange(
          data.length === newSelectedBars.length ? [] : newSelectedBars
        );
      }
      setMarkArea([]);
      initialTimeWindow$1 = null;
    }
    if (echartsInstance && filterable) {
      return addEventWithCleanUp$1(zr, "mouseup", mouseUpEvent);
    }
  }, [zr, echartsInstance, markArea, data, onSelectedBarsChange, filterable]);
  const clickEvent = useCallback(
    (ev) => {
      if (onSelectedBarsChange) {
        const newSelectedBars = new Set(selectedBars);
        const alreadyAdded = newSelectedBars.has(ev.dataIndex);
        if (alreadyAdded) {
          newSelectedBars.delete(ev.dataIndex);
        } else {
          newSelectedBars.add(ev.dataIndex);
        }
        onSelectedBarsChange(
          newSelectedBars.size === data.length ? [] : Array.from(newSelectedBars)
        );
      }
    },
    [onSelectedBarsChange, selectedBars, data.length]
  );
  return {
    markAreaOptions: markArea.length === 2 && {
      data: [[{ coord: [markArea[0]] }, { coord: [markArea[1]] }]],
      itemStyle: {
        color: theme.palette.primary.main,
        opacity: 0.2
      }
    },
    onEvents: filterable ? { click: clickEvent } : {}
  };
}
function addEventWithCleanUp$1(zr, eventKey, event) {
  if (zr == null ? void 0 : zr.handler) {
    events$1[eventKey] = event;
    zr.on(eventKey, event);
    return () => {
      if (events$1[eventKey] && (zr == null ? void 0 : zr.handler)) {
        zr.off(eventKey, event);
      }
    };
  }
}
function HistogramSkeleton({ height }) {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Box, { mb: 2, children: /* @__PURE__ */ jsx(Skeleton, { width: 48, height: 8 }) }),
    /* @__PURE__ */ jsxs(SkeletonGraphGrid, { style: { height: height ?? SKELETON_HEIGHT }, children: [
      /* @__PURE__ */ jsx(SkeletonThinBarItem, { variant: "rectangular", height: "20%" }),
      /* @__PURE__ */ jsx(SkeletonThinBarItem, { variant: "rectangular", height: "40%" }),
      /* @__PURE__ */ jsx(SkeletonThinBarItem, { variant: "rectangular", height: "60%" }),
      /* @__PURE__ */ jsx(SkeletonThinBarItem, { variant: "rectangular", height: "20%" }),
      /* @__PURE__ */ jsx(SkeletonThinBarItem, { variant: "rectangular", height: "80%" }),
      /* @__PURE__ */ jsx(SkeletonThinBarItem, { variant: "rectangular", height: "50%" }),
      /* @__PURE__ */ jsx(SkeletonThinBarItem, { variant: "rectangular", height: "20%" }),
      /* @__PURE__ */ jsx(SkeletonThinBarItem, { variant: "rectangular", height: "40%" }),
      /* @__PURE__ */ jsx(SkeletonThinBarItem, { variant: "rectangular", height: "60%" }),
      /* @__PURE__ */ jsx(SkeletonThinBarItem, { variant: "rectangular", height: "20%" }),
      /* @__PURE__ */ jsx(SkeletonThinBarItem, { variant: "rectangular", height: "80%" }),
      /* @__PURE__ */ jsx(SkeletonThinBarItem, { variant: "rectangular", height: "50%" })
    ] })
  ] });
}
const IS_TOUCH_SCREEN = detectTouchscreen();
const OptionsSelectedBar = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  "& .MuiTypography-caption": {
    color: theme.palette.text.secondary
  }
}));
const ClearButton$1 = styled(Link)(({ theme }) => ({
  ...theme.typography.caption,
  cursor: "pointer"
}));
const EMPTY_ARR$6 = [];
const EMPTY_FN$9 = () => void 0;
const IDENTITY$2 = (n) => n;
function defaultTooltipFormatter$4(params, xAxisFormatter = IDENTITY$2, yAxisFormatter = IDENTITY$2) {
  if (!params) {
    return null;
  }
  if (Array.isArray(params) && params.length === 0) {
    return;
  }
  const marker = Array.isArray(params) ? params[0].marker : params.marker;
  const data = Array.isArray(params) ? params[0].data : params.data;
  if ((data == null ? void 0 : data.value) === void 0) {
    return;
  }
  const [left, right, value] = data.value;
  const formattedLeft = processFormatterResult(xAxisFormatter(left));
  const formattedRight = processFormatterResult(xAxisFormatter(right));
  const formattedValue = processFormatterResult(yAxisFormatter(value));
  const title = `${formattedLeft} <span style="vertical-align: 1px;">—</span> ${formattedRight}`;
  const item = `<div style="margin-left: 8px; display: inline">${formattedValue}</div>`;
  return `${title} <div style="margin-top: 4px">${String(marker)}${item}</div>`;
}
function HistogramWidgetUI({
  data,
  ticks,
  min,
  max,
  xAxisFormatter = IDENTITY$2,
  yAxisFormatter = IDENTITY$2,
  yAxisType = "dense",
  selectedBars = EMPTY_ARR$6,
  onSelectedBarsChange = EMPTY_FN$9,
  tooltip = true,
  tooltipFormatter = defaultTooltipFormatter$4,
  animation = true,
  filterable: _filterable = true,
  height = 200,
  isLoading
}) {
  const intl = useIntl();
  const theme = useTheme();
  const intlConfig = useImperativeIntl(intl);
  const { showSkeleton } = useSkeleton(!!isLoading);
  const filterable = !!_filterable && !!onSelectedBarsChange;
  const [echartsInstance, setEchartInstance] = useState();
  const onChartReady = (_echartsInstance) => setEchartInstance(_echartsInstance);
  const formattedData = useMemo(
    () => formatData(data, ticks, min, max),
    [data, ticks, min, max]
  );
  const { onEvents, markAreaOptions } = useHistogramInteractivity({
    data: formattedData,
    filterable,
    selectedBars,
    onSelectedBarsChange,
    echartsInstance
  });
  const tooltipOptions = useMemo(
    () => ({
      show: tooltip,
      trigger: "item",
      padding: [SPACING * 0.5, SPACING],
      borderWidth: 0,
      textStyle: {
        ...theme.typography.caption,
        fontSize: 11,
        color: theme.palette.common.white
      },
      backgroundColor: theme.palette.black[90],
      confine: true,
      position: "top",
      formatter(params) {
        return tooltipFormatter(params, xAxisFormatter, yAxisFormatter);
      }
    }),
    [theme, tooltip, tooltipFormatter, xAxisFormatter, yAxisFormatter]
  );
  const xAxisOptions = useMemo(
    () => ({
      min,
      max,
      interval: (max - min) / formattedData.length,
      axisLine: {
        show: false
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: theme.palette.black[4]
        }
      },
      axisTick: {
        show: false
      },
      axisLabel: {
        showMinLabel: true,
        showMaxLabel: true,
        ...theme.typography.overlineDelicate,
        hideOverlap: true,
        padding: [SPACING * 0.5, SPACING * 0.5, 0, SPACING * 0.5],
        formatter: (value) => {
          const formattedValue = processFormatterResult(xAxisFormatter(value));
          if (value === min) {
            return formatMin(formattedValue);
          }
          if (value === max) {
            return formatMax(formattedValue);
          }
          return formattedValue;
        },
        color: theme.palette.black[60]
      }
    }),
    [min, max, formattedData.length, theme, xAxisFormatter]
  );
  const yAxisOptions = useMemo(() => {
    const denseAxisConfig = {
      margin: 0,
      verticalAlign: "bottom",
      padding: [0, 0, SPACING * 1.25, 0],
      inside: true,
      color: (value) => {
        const maxValue = Math.max(...data.map((d) => d ?? Number.MIN_SAFE_INTEGER)) || 1;
        let col = "transparent";
        if (value >= maxValue) {
          col = theme.palette.black[60];
        }
        return col;
      },
      ...theme.typography.overlineDelicate,
      formatter: (v) => processFormatterResult(yAxisFormatter(v))
    };
    const fullAxisConfig = {
      verticalAlign: "middle",
      padding: [0, 0, 0, SPACING * 0.5],
      color: theme.palette.black[60]
    };
    const axisLabelConfig = yAxisType === "dense" ? denseAxisConfig : fullAxisConfig;
    return {
      type: "value",
      axisLine: {
        show: false
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: theme.palette.black[4]
        }
      },
      axisTick: {
        show: false
      },
      axisLabel: {
        show: true,
        showMaxLabel: true,
        showMinLabel: false,
        ...axisLabelConfig,
        ...theme.typography.overlineDelicate,
        formatter: (v) => processFormatterResult(yAxisFormatter(v))
      }
    };
  }, [
    theme.palette.black,
    theme.typography.overlineDelicate,
    data,
    yAxisFormatter,
    yAxisType
  ]);
  const seriesOptions = useMemo(() => {
    const isUniqueDataRow = min === max && formattedData.filter((row) => row[2] !== 0).length === 1;
    const data2 = isUniqueDataRow ? [formattedData[0], formattedData[formattedData.length - 1]] : formattedData;
    const dataWithColor = data2.map((item, idx) => {
      const isDisabled = selectedBars.length && !selectedBars.includes(idx);
      const color = isDisabled ? theme.palette.black[25] : theme.palette.secondary.main;
      return { value: item, itemStyle: { color } };
    });
    return {
      type: "custom",
      cursor: "pointer",
      markArea: markAreaOptions,
      renderItem: function(params, api) {
        const isLast = params.dataIndex === formattedData.length - 1;
        const isFirst = params.dataIndex === 0;
        const fill = dataWithColor[params.dataIndex].itemStyle.color;
        const tickStart = api.value(0);
        const tickEnd = api.value(1);
        const yValue = api.value(2);
        const [x, y] = api.coord([tickStart, yValue]);
        const [width, height2] = api.size([tickEnd - tickStart, yValue]);
        return {
          type: "rect",
          shape: {
            x: isUniqueDataRow ? x / 10 : x + (isFirst ? 0 : 1),
            y,
            // Division by 10 in the next line is done to avoid that the only bar rendered inside the histogram widget gets all the width of it
            width: isUniqueDataRow ? x - x / 10 : width - (isLast ? 0 : 1),
            height: height2
          },
          style: { fill },
          ...!IS_TOUCH_SCREEN && {
            emphasis: {
              style: {
                fill: darken(fill, 0.25)
              }
            }
          }
        };
      },
      encode: {
        x: [0, 1],
        y: 2,
        tooltip: [0, 1, 2]
      },
      data: dataWithColor,
      animation
    };
  }, [
    min,
    max,
    formattedData,
    markAreaOptions,
    theme.palette.black,
    theme.palette.secondary.main,
    selectedBars,
    animation
  ]);
  const options = useMemo(
    () => ({
      grid: {
        left: SPACING * 0.1,
        right: SPACING * 0.1,
        top: SPACING * 2,
        bottom: SPACING * 0.5,
        containLabel: true
      },
      tooltip: tooltipOptions,
      xAxis: xAxisOptions,
      yAxis: yAxisOptions,
      series: [seriesOptions]
    }),
    [tooltipOptions, xAxisOptions, yAxisOptions, seriesOptions]
  );
  const countSelectedElements = selectedBars.reduce(
    (acc, barIndex) => acc += data[barIndex] ?? 0,
    0
  );
  const handleClearPress = (e) => {
    if (e.key === "Enter") {
      onSelectedBarsChange([]);
    }
  };
  if (showSkeleton) {
    return /* @__PURE__ */ jsx(HistogramSkeleton, { height });
  }
  return /* @__PURE__ */ jsxs("div", { children: [
    filterable && /* @__PURE__ */ jsxs(OptionsSelectedBar, { container: true, children: [
      /* @__PURE__ */ jsx(Typography, { variant: "caption", weight: "strong", children: selectedBars.length > 0 ? intlConfig.formatMessage(
        { id: "c4r.widgets.histogram.selectedItems" },
        {
          items: processFormatterResult(
            yAxisFormatter(countSelectedElements)
          )
        }
      ) : intlConfig.formatMessage({ id: "c4r.widgets.histogram.all" }) }),
      selectedBars.length > 0 && /* @__PURE__ */ jsx(
        ClearButton$1,
        {
          onClick: () => onSelectedBarsChange([]),
          onKeyDown: handleClearPress,
          underline: "hover",
          tabIndex: 0,
          children: intlConfig.formatMessage({ id: "c4r.widgets.histogram.clear" })
        }
      )
    ] }),
    /* @__PURE__ */ jsx(
      ReactEcharts,
      {
        option: options,
        onEvents,
        lazyUpdate: true,
        onChartReady,
        style: { maxHeight: height }
      }
    )
  ] });
}
function formatMin(value) {
  const spaces = Array(String(value).length).fill("  ").join("");
  return `${spaces}${value}`;
}
function formatMax(value) {
  const spaces = Array(String(value).length).fill("  ").join("");
  return `${value}${spaces}`;
}
function formatData(data, ticks, min, max) {
  return data.map(
    (value, idx) => [
      idx === 0 ? min : ticks[idx - 1],
      idx === data.length - 1 ? max : ticks[idx],
      value
    ]
  );
}
function AlertBody({
  color = void 0,
  children
}) {
  return children ? /* @__PURE__ */ jsx(Box, { mt: 0.5, children: /* @__PURE__ */ jsx(
    Typography,
    {
      component: "div",
      variant: "caption",
      color: color ?? "inherit",
      style: { fontWeight: "normal" },
      children
    }
  ) }) : /* @__PURE__ */ jsx(Box, { mt: -1 });
}
function NoDataAlert({
  title = "No data available",
  body = "There are no results for the combination of filters applied to your data. Try tweaking your filters, or zoom and pan the map to adjust the Map View.",
  severity = void 0,
  ...otherProps
}) {
  return severity ? /* @__PURE__ */ jsx(Alert, { title, severity, ...otherProps, children: /* @__PURE__ */ jsx(AlertBody, { children: body }) }) : /* @__PURE__ */ jsxs(Box, { ...otherProps, children: [
    title && /* @__PURE__ */ jsx(Typography, { variant: "body2", children: title }),
    /* @__PURE__ */ jsx(AlertBody, { color: "textSecondary", children: body })
  ] });
}
function usePieCategories(data, order, maxItems, colors, othersValue) {
  const theme = useTheme();
  const orderedData = useMemo(() => {
    if (order === OrderTypes.Ranking) {
      return data.slice().sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
    }
    return data;
  }, [data, order]);
  const groupedData = useMemo(() => {
    const categories = [];
    let othersValueAgg = 0;
    for (const category of orderedData) {
      if (categories.length < maxItems) {
        categories.push({ ...category });
      } else if (othersValue === void 0) {
        othersValueAgg += category.value ?? 0;
      }
    }
    const actualOthersValue = othersValue ?? (othersValueAgg > 0 ? othersValueAgg : NaN);
    if (!isNaN(actualOthersValue)) {
      categories.push({
        name: OTHERS_CATEGORY_NAME,
        value: actualOthersValue,
        emphasis: { scale: false }
      });
    }
    return categories;
  }, [maxItems, orderedData, othersValue]);
  const dataWithColor = useMemo(() => {
    const fallbackColor = theme.palette.common.white;
    return groupedData.map((item, index) => {
      let color = fallbackColor;
      if (colors.length) {
        const newColor = colors[index % colors.length];
        if (newColor) {
          color = newColor;
        }
      } else {
        const palette = theme.palette.qualitative.bold;
        const newColor = palette[index];
        if (newColor) {
          color = newColor;
        }
      }
      const _item = item;
      _item.color = color;
      return _item;
    });
  }, [groupedData, colors, theme]);
  return dataWithColor;
}
const Root$4 = styled(Box)(({ theme }) => ({
  zIndex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  paddingBottom: theme.spacing(1)
}));
const Category = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  maxWidth: "140px",
  textTransform: "uppercase"
}));
const MarkerColor = styled(Box)(({ theme }) => ({
  display: "inline-block",
  borderRadius: theme.spacing(0.5),
  width: theme.spacing(1),
  minWidth: theme.spacing(1),
  height: theme.spacing(1)
}));
function PieCentralText({
  data,
  selectedCategories,
  formatter
}) {
  const [selectedItem, setSelectedItem] = useState();
  const topSelectedCategory = useMemo(() => {
    if (!data || data.length === 0) {
      return null;
    }
    let array;
    if (selectedCategories.length > 0) {
      array = data.filter(
        (dataItem) => selectedCategories.includes(dataItem.name)
      );
    } else {
      array = data;
    }
    const largestCategory = findLargestCategory(array);
    const category = array.find((element) => element === largestCategory);
    if (!category) {
      return null;
    }
    let sumValue = 0;
    for (const category2 of data) {
      sumValue += category2.value ?? 0;
    }
    const percentage2 = calculatePercentage(
      category.value ?? 0,
      sumValue,
      formatter
    );
    return { ...category, percentage: percentage2 };
  }, [data, selectedCategories, formatter]);
  useEffect(() => {
    if (topSelectedCategory) {
      setSelectedItem(topSelectedCategory);
    }
  }, [topSelectedCategory, setSelectedItem]);
  const { name, percentage, color } = selectedItem ?? {};
  if (!selectedItem) {
    return null;
  }
  return /* @__PURE__ */ jsxs(Root$4, { children: [
    /* @__PURE__ */ jsx(Typography, { variant: "h5", children: percentage }),
    /* @__PURE__ */ jsxs(Category, { children: [
      /* @__PURE__ */ jsx(MarkerColor, { bgcolor: color, component: "span" }),
      /* @__PURE__ */ jsx(Typography, { component: "span", variant: "body2", noWrap: true, children: name })
    ] })
  ] });
}
function findLargestCategory(array) {
  const filteredArray = array.filter(
    (item) => item.name !== OTHERS_CATEGORY_NAME
  );
  if (filteredArray.length === 0) {
    return array[0];
  }
  return filteredArray.reduce((largestItem, currentItem) => {
    if ((currentItem.value ?? 0) > (largestItem.value ?? 0)) {
      return currentItem;
    }
    return largestItem;
  });
}
function calculatePercentage(value, total, formatter) {
  let percentage = 0;
  if (total !== 0) {
    percentage = value / total * 100;
  }
  return formatter ? formatter(percentage) : `${percentage}%`;
}
const GUTTER = 32;
const Chart$1 = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginTop: theme.spacing(0.5)
}));
const PieShape = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative"
}));
const SkeletonCircleMask = styled(SkeletonMask)(() => ({
  position: "absolute",
  zIndex: 1
}));
const TextContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(1),
  position: "absolute",
  zIndex: 2
}));
const Leyend = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  height: theme.spacing(5)
}));
const LeyendItem = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5)
}));
function PieSkeleton({ height }) {
  const size = parseInt(String(height), 10) || SKELETON_HEIGHT;
  const pieSize = size - GUTTER;
  const pieInnerSize = pieSize - GUTTER;
  return /* @__PURE__ */ jsxs(Box, { mt: 0.5, children: [
    /* @__PURE__ */ jsx(Skeleton, { height: 8, width: 64 }),
    /* @__PURE__ */ jsx(Chart$1, { height: size, children: /* @__PURE__ */ jsxs(PieShape, { children: [
      /* @__PURE__ */ jsx(Skeleton, { variant: "circular", width: pieSize, height: pieSize }),
      /* @__PURE__ */ jsx(
        SkeletonCircleMask,
        {
          variant: "circular",
          width: pieInnerSize,
          height: pieInnerSize
        }
      ),
      /* @__PURE__ */ jsxs(TextContainer, { children: [
        /* @__PURE__ */ jsx(Skeleton, { height: 24, width: 72 }),
        /* @__PURE__ */ jsx(Skeleton, { height: 8, width: 48 })
      ] })
    ] }) }),
    /* @__PURE__ */ jsx(Leyend, { children: [...Array(2)].map((_, i) => /* @__PURE__ */ jsxs(LeyendItem, { children: [
      /* @__PURE__ */ jsx(Skeleton, { variant: "circular", width: 8, height: 8 }),
      /* @__PURE__ */ jsx(Skeleton, { width: 48, height: 8 })
    ] }, i)) })
  ] });
}
const Legend$1 = styled(Box)(({ theme }) => ({
  position: "relative",
  padding: theme.spacing(0.75, 0.75, 0, 0.75),
  width: "100%"
}));
const ItemsContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "row",
  overflowX: "hidden",
  gap: theme.spacing(2),
  // a11y: padding prevents clipping focus outline
  padding: theme.spacing(0.25)
}));
const Item = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(1),
  alignItems: "center",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  cursor: "pointer"
}));
const OverflowVeil = styled(Box)(({ theme }) => ({
  display: "inline-block",
  position: "absolute",
  left: theme.spacing(-2),
  top: theme.spacing(1),
  bottom: theme.spacing(1),
  zIndex: 1,
  width: theme.spacing(2.5),
  background: `linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, ${theme.palette.background.paper} 100%)`
}));
const ShowMoreButtons = styled(Box)(({ theme }) => ({
  position: "absolute",
  padding: theme.spacing(0.25, 1),
  top: theme.spacing(0),
  right: 0,
  background: theme.palette.background.paper
}));
const Circle$1 = styled(Box, {
  shouldForwardProp: (prop) => prop !== "color"
})(({ theme, color }) => {
  const size = theme.spacing(1);
  return {
    backgroundColor: color,
    borderRadius: "50%",
    width: size,
    height: size
  };
});
const EMPTY_OBJ$4 = {};
function ChartLegend({
  series,
  selectedCategories,
  labels = EMPTY_OBJ$4,
  onCategoryClick
}) {
  var _a, _b;
  const theme = useTheme();
  const [overflowing, setOverflowing] = useState(false);
  const [offset, setOffset] = useState(0);
  const [maxWidth, setMaxWidth] = useState(void 0);
  const legendRef = useRef(null);
  const containerRef = useRef(null);
  const showMoreButtonsRef = useRef(null);
  if (series.length === 0) {
    series = [{ name: "", color: theme.palette.text.disabled }];
  }
  const intl = useIntl();
  const intlConfig = useImperativeIntl(intl);
  function handleClickRight() {
    setOffset(offset + 1);
  }
  function handleClickLeft() {
    setOffset(Math.max(offset - 1));
  }
  function onCategoryPress(e, categoryName) {
    if (e.key === "Enter") {
      onCategoryClick == null ? void 0 : onCategoryClick(categoryName);
    }
  }
  const updateMaxWidth = useCallback(() => {
    var _a2, _b2, _c;
    const overflowing2 = !!containerRef.current && containerRef.current.scrollWidth > containerRef.current.clientWidth;
    setOverflowing(overflowing2);
    setMaxWidth(
      overflowing2 && containerRef.current ? (((_a2 = legendRef.current) == null ? void 0 : _a2.clientWidth) ?? 0) - (((_b2 = showMoreButtonsRef.current) == null ? void 0 : _b2.clientWidth) ?? 70) : (((_c = legendRef.current) == null ? void 0 : _c.clientWidth) ?? 0) - 12 || 500
    );
  }, [legendRef, containerRef, showMoreButtonsRef]);
  useEffect(() => {
    updateMaxWidth();
  }, [
    containerRef,
    (_a = containerRef.current) == null ? void 0 : _a.scrollWidth,
    (_b = containerRef.current) == null ? void 0 : _b.clientWidth,
    updateMaxWidth,
    offset
  ]);
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    let observer;
    if (legendRef.current) {
      observer = new ResizeObserver(() => {
        updateMaxWidth();
      });
      observer.observe(legendRef.current);
    }
    return () => {
      observer == null ? void 0 : observer.disconnect();
    };
  }, [legendRef, updateMaxWidth]);
  return /* @__PURE__ */ jsxs(Legend$1, { ref: legendRef, children: [
    /* @__PURE__ */ jsx(
      ItemsContainer,
      {
        ref: containerRef,
        style: {
          maxWidth: `${maxWidth}px`
        },
        children: series.map((category, i) => {
          if (i < offset) return null;
          const selected = selectedCategories.length === 0 || selectedCategories.includes(category.name);
          const label = category.name in labels ? labels[category.name] : category.name;
          return /* @__PURE__ */ jsxs(
            Item,
            {
              tabIndex: onCategoryClick ? 0 : void 0,
              onClick: onCategoryClick ? () => onCategoryClick(category.name) : void 0,
              onKeyDown: onCategoryClick ? (e) => onCategoryPress(e, category.name) : void 0,
              style: {
                pointerEvents: !onCategoryClick || category.name === "Others" ? "none" : void 0
              },
              children: [
                /* @__PURE__ */ jsx(
                  Circle$1,
                  {
                    color: selected ? category.color : theme.palette.text.disabled
                  }
                ),
                /* @__PURE__ */ jsx(
                  Typography,
                  {
                    variant: "overline",
                    color: selected ? void 0 : "text.disabled",
                    children: label || /* @__PURE__ */ jsx(Fragment, { children: " " })
                  }
                )
              ]
            },
            i
          );
        })
      }
    ),
    (overflowing || offset > 0) && /* @__PURE__ */ jsxs(ShowMoreButtons, { ref: showMoreButtonsRef, children: [
      /* @__PURE__ */ jsx(OverflowVeil, {}),
      /* @__PURE__ */ jsx(
        IconButton,
        {
          icon: /* @__PURE__ */ jsx(ChevronLeft, {}),
          component: "span",
          size: "small",
          disabled: offset === 0,
          onClick: handleClickLeft,
          tooltip: intlConfig.formatMessage({
            id: "c4r.widgets.chartLegend.prev"
          })
        }
      ),
      /* @__PURE__ */ jsx(
        IconButton,
        {
          icon: /* @__PURE__ */ jsx(ChevronRight, {}),
          component: "span",
          size: "small",
          disabled: !overflowing,
          onClick: handleClickRight,
          tooltip: intlConfig.formatMessage({
            id: "c4r.widgets.chartLegend.next"
          })
        }
      )
    ] })
  ] });
}
const CHART_SIZE = "232px";
const OptionsBar = styled(Grid)(({ theme }) => ({
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(0.5)
}));
const ChartContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== "height" && prop !== "width"
})(
  ({ height, width }) => ({
    position: "relative",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: width || "100%",
    height: height || "100%"
  })
);
const Chart = styled(ReactEcharts)(() => ({
  position: "absolute",
  top: 0,
  right: 0,
  left: 0,
  bottom: 0
}));
const EMPTY_ARR$5 = [];
const EMPTY_OBJ$3 = {};
const IDENTITY$1 = (v) => v;
function PieWidgetUI({
  name,
  data,
  formatter = IDENTITY$1,
  tooltipFormatter = defaultTooltipFormatter$3,
  percentFormatter,
  height = CHART_SIZE,
  width = CHART_SIZE,
  labels = EMPTY_OBJ$3,
  colors = EMPTY_ARR$5,
  animation = true,
  filterable = true,
  selectedCategories = EMPTY_ARR$5,
  onSelectedCategoriesChange,
  isLoading,
  maxItems = 11,
  othersValue,
  order = OrderTypes.Ranking
}) {
  const theme = useTheme();
  const processedData = usePieCategories(
    data,
    order,
    maxItems,
    colors,
    othersValue
  );
  const { showSkeleton } = useSkeleton(!!isLoading);
  const intl = useIntl();
  const intlConfig = useImperativeIntl(intl);
  const _percentFormatter = useMemo(() => {
    if (percentFormatter) {
      return percentFormatter;
    }
    return (v) => `${intl.formatNumber(v, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    })}%`;
  }, [intl, percentFormatter]);
  const tooltipOptions = useMemo(() => {
    let _formatter = void 0;
    if (tooltipFormatter) {
      _formatter = (params) => tooltipFormatter(params, formatter, _percentFormatter);
    }
    return {
      padding: [SPACING * 0.5, SPACING],
      backgroundColor: theme.palette.black[90],
      borderColor: "transparent",
      textStyle: { color: theme.palette.common.white },
      confine: true,
      formatter: _formatter
    };
  }, [
    theme.palette.black,
    theme.palette.common.white,
    tooltipFormatter,
    formatter,
    _percentFormatter
  ]);
  const seriesOptions = useMemo(
    () => [
      {
        type: "pie",
        name,
        animation,
        data: processedData.map((item) => {
          const clonedItem = { ...item };
          const disabled = (selectedCategories == null ? void 0 : selectedCategories.length) && !selectedCategories.includes(clonedItem.name);
          if (labels == null ? void 0 : labels[clonedItem.name]) {
            clonedItem.name = labels[clonedItem.name];
          }
          if (disabled) {
            return {
              ...clonedItem,
              disabled: true,
              itemStyle: { color: theme.palette.black[25] }
            };
          }
          return {
            ...clonedItem,
            itemStyle: { color: clonedItem.color }
          };
        }),
        radius: ["74%", "90%"],
        selectedOffset: 0,
        bottom: SPACING * 1.5,
        label: { show: false },
        emphasis: {
          focus: "series",
          scaleSize: 5
        },
        itemStyle: {
          borderColor: theme.palette.background.paper,
          borderWidth: 1
        }
      }
    ],
    [name, animation, processedData, labels, selectedCategories, theme]
  );
  const options = useMemo(
    () => ({
      grid: {
        left: 0,
        top: 0,
        right: 0,
        bottom: 0
      },
      tooltip: tooltipOptions,
      legend: {
        show: false
      },
      series: seriesOptions
    }),
    [tooltipOptions, seriesOptions]
  );
  const handleChartClick = useCallback(
    (params) => {
      if (onSelectedCategoriesChange) {
        const newSelectedCategories = [...selectedCategories];
        const { name: name2 } = processedData[params.dataIndex];
        if (name2 === OTHERS_CATEGORY_NAME) {
          return;
        }
        const selectedCategoryIdx = newSelectedCategories.indexOf(name2);
        if (selectedCategoryIdx === -1) {
          newSelectedCategories.push(name2);
        } else {
          newSelectedCategories.splice(selectedCategoryIdx, 1);
        }
        onSelectedCategoriesChange(newSelectedCategories);
      }
    },
    [processedData, onSelectedCategoriesChange, selectedCategories]
  );
  const handleLegendClick = useCallback(
    (category) => {
      if (onSelectedCategoriesChange) {
        const newSelectedCategories = [...selectedCategories];
        const selectedCategoryIdx = newSelectedCategories.indexOf(category);
        if (selectedCategoryIdx === -1) {
          newSelectedCategories.push(category);
        } else {
          newSelectedCategories.splice(selectedCategoryIdx, 1);
        }
        onSelectedCategoriesChange(newSelectedCategories);
      }
    },
    [onSelectedCategoriesChange, selectedCategories]
  );
  const onEvents = {
    ...filterable && { click: handleChartClick }
  };
  const handleClearSelectedCategories = () => {
    onSelectedCategoriesChange == null ? void 0 : onSelectedCategoriesChange([]);
  };
  const handleClearPress = (e) => {
    if (e.key === "Enter") {
      handleClearSelectedCategories();
    }
  };
  if (showSkeleton) {
    return /* @__PURE__ */ jsx(PieSkeleton, { height });
  }
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    filterable && /* @__PURE__ */ jsxs(OptionsBar, { container: true, children: [
      /* @__PURE__ */ jsx(Typography, { variant: "caption", color: "textSecondary", children: selectedCategories.length ? intlConfig.formatMessage(
        { id: "c4r.widgets.pie.selectedItems" },
        { items: selectedCategories.length }
      ) : intlConfig.formatMessage({ id: "c4r.widgets.pie.allSelected" }) }),
      selectedCategories.length > 0 && /* @__PURE__ */ jsx(
        Link,
        {
          variant: "caption",
          onClick: handleClearSelectedCategories,
          onKeyDown: handleClearPress,
          tabIndex: 0,
          children: intlConfig.formatMessage({ id: "c4r.widgets.pie.clear" })
        }
      )
    ] }),
    /* @__PURE__ */ jsxs(ChartContent, { height, width, children: [
      /* @__PURE__ */ jsx(
        PieCentralText,
        {
          data: processedData,
          selectedCategories,
          formatter: _percentFormatter
        }
      ),
      /* @__PURE__ */ jsx(
        Chart,
        {
          option: options,
          onEvents,
          lazyUpdate: true,
          style: { height, width }
        }
      )
    ] }),
    processedData.length > 0 && /* @__PURE__ */ jsx(
      ChartLegend,
      {
        series: processedData,
        labels,
        selectedCategories,
        onCategoryClick: onSelectedCategoriesChange && handleLegendClick
      }
    )
  ] });
}
function defaultTooltipFormatter$3(params, formatter, percentFormatter) {
  var _a;
  const item = Array.isArray(params) ? params[0] : params;
  if (!item) {
    return null;
  }
  const value = processFormatterResult(formatter(item.value));
  const percentage = percentFormatter(item.percent) || `${item.percent}%`;
  const markerColor = ((_a = item.data) == null ? void 0 : _a.color) || item.textStyle.color;
  const markerStyle = `display:inline-block;border-radius:4px;width:8px;height:8px;background-color:${markerColor}`;
  return `
    <div style="white-space:normal;"><p style="max-width:${CHART_SIZE};font-size:11px;font-weight:500;line-height:1.454;text-transform:uppercase;margin:0 0 4px 0;">${item.name}</p>
    <p style="display:flex;align-items:center;font-size: 11px;font-weight:500;line-height:1.454;margin:0;"><span style="${markerStyle}"></span> <span style="margin:0 4px;font-weight:400;">${value}</span> (${percentage})</p></div>
  `.trim();
}
const Root$3 = styled(Grid)(({ theme }) => ({
  position: "relative",
  alignItems: "center",
  height: theme.spacing(4)
}));
const DotsContainer = styled(Grid)(({ theme }) => ({
  position: "absolute",
  zIndex: 1,
  padding: theme.spacing(0, 3)
}));
function RangeSkeleton() {
  return /* @__PURE__ */ jsxs(Grid, { container: true, children: [
    /* @__PURE__ */ jsxs(Root$3, { container: true, item: true, children: [
      /* @__PURE__ */ jsxs(DotsContainer, { container: true, item: true, justifyContent: "space-between", children: [
        /* @__PURE__ */ jsx(SkeletonSolid, { variant: "circular", width: 12, height: 12 }),
        /* @__PURE__ */ jsx(SkeletonSolid, { variant: "circular", width: 12, height: 12 })
      ] }),
      /* @__PURE__ */ jsx(Skeleton, { height: 2, width: "100%" })
    ] }),
    /* @__PURE__ */ jsxs(Grid, { container: true, item: true, justifyContent: "space-between", children: [
      /* @__PURE__ */ jsx(Skeleton, { width: 56, height: 32 }),
      /* @__PURE__ */ jsx(Skeleton, { width: 56, height: 32 })
    ] })
  ] });
}
const Root$2 = styled(Box)(() => ({
  position: "relative"
}));
const ClearWrapper = styled(Box)(({ theme: { spacing } }) => ({
  display: "flex",
  flexDirection: "row-reverse",
  height: spacing(1.5)
}));
const ClearButton = styled(Link)(() => ({
  cursor: "pointer"
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
}));
const LimitTextField = styled(TextField)(({ theme: { spacing } }) => ({
  maxWidth: spacing(9),
  margin: 0,
  "& fieldset": {
    borderWidth: 1
  },
  "& input": {
    "&[type=number]": {
      appearance: "textfield"
    },
    "&::-webkit-outer-spin-button": {
      appearance: "none",
      margin: 0
    },
    "&::-webkit-inner-spin-button": {
      appearance: "none",
      margin: 0
    }
  }
}));
const StyledSlider = styled(Slider)(({ theme: { palette } }) => ({
  "& .MuiSlider-rail": {
    color: palette.text.hint
  }
}));
const SliderLimit = styled(Slider)(({ theme: { palette, spacing } }) => ({
  pointerEvents: "none",
  position: "absolute",
  zIndex: 1,
  left: 0,
  right: 0,
  "& .MuiSlider-rail": {
    display: "none"
  },
  "& .MuiSlider-thumb": {
    display: "none"
  },
  "& .MuiSlider-track": {
    color: palette.primary.main,
    opacity: 0.38
  },
  "& .MuiSlider-mark, & .MuiSlider-markActive": {
    backgroundColor: palette.primary.main,
    opacity: 0.38,
    height: spacing(1),
    width: spacing(0.25),
    top: "50%",
    transform: "translateY(-50%)"
  }
}));
function RangeWidgetUI({
  data,
  min,
  max,
  limits,
  onSelectedRangeChange,
  isLoading
}) {
  const [sliderValues, setSliderValues] = useState([min, max]);
  const [inputsValues, setInputsValues] = useState([
    min,
    max
  ]);
  const intl = useIntl();
  const intlConfig = useImperativeIntl(intl);
  const { showSkeleton } = useSkeleton(!!isLoading);
  const limitsMarks = useMemo(() => {
    if (!limits || limits.length !== 2) {
      return void 0;
    }
    if (limits[0] === limits[1]) {
      return [{ value: limits[0] }];
    }
    const result = [
      { value: Math.max(limits[0], min) },
      { value: Math.min(limits[1], max) }
    ];
    if (result[0].value > result[1].value) {
      return void 0;
    }
    return result;
  }, [limits, max, min]);
  const debouncedOnSelectedRangeChange = useMemo(
    () => onSelectedRangeChange ? debounce(onSelectedRangeChange, 250) : null,
    [onSelectedRangeChange]
  );
  const changeSliderValues = (newValues) => {
    setInputsValues([...newValues]);
    setSliderValues([...newValues]);
    if (debouncedOnSelectedRangeChange) {
      debouncedOnSelectedRangeChange([...newValues]);
    }
  };
  function handleInputChange(event, index) {
    const value = event.target.value === "" ? "" : Number(event.target.value);
    if (index === 0) {
      setInputsValues([value, inputsValues[1]]);
    }
    if (index === 1) {
      setInputsValues([inputsValues[0], value]);
    }
  }
  const hasBeenModified = min !== inputsValues[0] || max !== inputsValues[1];
  useEffect(() => {
    if (!data) {
      return;
    }
    setInputsValues([...data]);
    setSliderValues([...data]);
  }, [data]);
  function handleInputBlur(index) {
    let value = inputsValues[index];
    if (value === "") {
      value = sliderValues[index];
    }
    if (value > max) {
      value = max;
    }
    if (value < min) {
      value = min;
    }
    const newValues = index === 0 ? [value, inputsValues[1]] : [inputsValues[0], value];
    changeSliderValues(newValues.sort((a, b) => a - b));
  }
  const resetSlider = () => {
    changeSliderValues([min, max]);
  };
  function handleClearPress(e) {
    if (e.key === "Enter") {
      resetSlider();
    }
  }
  if (showSkeleton) {
    return /* @__PURE__ */ jsx(RangeSkeleton, {});
  }
  return /* @__PURE__ */ jsxs(Root$2, { children: [
    /* @__PURE__ */ jsx(ClearWrapper, { children: hasBeenModified && /* @__PURE__ */ jsx(Typography, { variant: "caption", color: "primary", children: /* @__PURE__ */ jsx(
      ClearButton,
      {
        onClick: resetSlider,
        onKeyDown: handleClearPress,
        tabIndex: 0,
        component: "button",
        underline: "hover",
        children: intlConfig.formatMessage({ id: "c4r.widgets.range.clear" })
      }
    ) }) }),
    /* @__PURE__ */ jsxs(Box, { children: [
      /* @__PURE__ */ jsx(
        StyledSlider,
        {
          getAriaLabel: (index) => index === 0 ? intlConfig.formatMessage({ id: "c4r.widgets.range.minValue" }) : intlConfig.formatMessage({ id: "c4r.widgets.range.maxValue" }),
          value: sliderValues,
          min,
          max,
          onChange: (_, values) => changeSliderValues(values)
        }
      ),
      limits && limits.length === 2 && /* @__PURE__ */ jsx(
        SliderLimit,
        {
          getAriaLabel: (index) => index === 0 ? intlConfig.formatMessage({ id: "c4r.widgets.range.minLimit" }) : intlConfig.formatMessage({ id: "c4r.widgets.range.maxLimit" }),
          value: limits,
          min,
          max,
          marks: limitsMarks
        }
      )
    ] }),
    /* @__PURE__ */ jsxs(Box, { display: "flex", justifyContent: "space-between", mb: 1, children: [
      /* @__PURE__ */ jsx(
        LimitTextField,
        {
          value: inputsValues[0],
          size: "small",
          onChange: (event) => handleInputChange(event, 0),
          onBlur: () => handleInputBlur(0),
          inputProps: {
            min,
            max,
            type: "number",
            "aria-label": intlConfig.formatMessage({
              id: "c4r.widgets.range.minValue"
            })
          }
        }
      ),
      /* @__PURE__ */ jsx(
        LimitTextField,
        {
          value: inputsValues[1],
          size: "small",
          onChange: (event) => handleInputChange(event, 1),
          onBlur: () => handleInputBlur(1),
          inputProps: {
            min,
            max,
            type: "number",
            "aria-label": intlConfig.formatMessage({
              id: "c4r.widgets.range.maxValue"
            })
          }
        }
      )
    ] })
  ] });
}
const GraphGrid = styled(SkeletonGraphGrid)(({ theme }) => ({
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  marginLeft: theme.spacing(4),
  containerType: "inline-size",
  [`@container (max-width: ${BREAKPOINTS.XS}px)`]: {
    " > div": {
      maxWidth: "75%"
    }
  }
}));
const DotsBox = styled(Box)(() => ({
  position: "relative",
  display: "flex",
  justifyContent: "space-between",
  width: "80%",
  maxWidth: "50%"
}));
function ScatterPlotSkeleton({ height }) {
  function getRandomValue() {
    return Math.floor(Math.random() * 5);
  }
  return /* @__PURE__ */ jsxs(Box, { height: height ?? SKELETON_HEIGHT, children: [
    /* @__PURE__ */ jsx(Box, { mb: 4, children: /* @__PURE__ */ jsx(Skeleton, { width: 48, height: 8 }) }),
    /* @__PURE__ */ jsx(GraphGrid, { height: "80%", children: [...Array(3)].map((_, i) => (
      // Every row is placed with an incremental margin left to reinforce the effect of a scatter plot
      /* @__PURE__ */ jsx(DotsBox, { ml: i * 3, children: [...Array(4)].map((_2, i2) => (
        // Random margins are used to create the effect of a scatter plot
        /* @__PURE__ */ jsx(Box, { mt: getRandomValue(), ml: getRandomValue(), children: /* @__PURE__ */ jsx(Skeleton, { variant: "circular", width: 12, height: 12 }) }, i2)
      )) }, i)
    )) })
  ] });
}
function areChartPropsEqual(optionPrev, optionNext) {
  var _a;
  const prevTooltop = Array.isArray(optionPrev.tooltip) ? optionPrev.tooltip[0] : optionPrev.tooltip;
  const nextTooltip = Array.isArray(optionNext.tooltip) ? optionNext.tooltip[0] : optionNext.tooltip;
  const tooltipFormatterPrev = prevTooltop == null ? void 0 : prevTooltop.formatter;
  const tooltipFormatterNext = nextTooltip == null ? void 0 : nextTooltip.formatter;
  const dataPrev = (_a = optionPrev.series[0]) == null ? void 0 : _a.data;
  const dataNext = optionNext.series[0].data;
  if (tooltipFormatterPrev !== tooltipFormatterNext) {
    return false;
  }
  if (dataPrev && dataNext && dataPrev.length === dataNext.length) {
    return !dataNext.some(({ value }, index) => {
      var _a2;
      return !(value === ((_a2 = dataPrev[index]) == null ? void 0 : _a2.value));
    });
  }
  return false;
}
function __generateDefaultConfig({
  tooltipFormatter,
  xAxisFormatter = IDENTITY,
  yAxisFormatter = IDENTITY
}, theme) {
  return {
    grid: {
      top: 10,
      left: 5,
      bottom: 10,
      right: 15,
      containLabel: true
    },
    tooltip: {
      padding: [SPACING * 0.5, SPACING],
      textStyle: {
        ...theme.typography.caption,
        fontSize: 11,
        color: theme.palette.common.white
      },
      backgroundColor: theme.palette.black[90],
      ...tooltipFormatter ? { formatter: tooltipFormatter } : {}
    },
    color: [theme.palette.secondary.main],
    xAxis: {
      axisLabel: {
        ...theme.typography.overlineDelicate,
        padding: [SPACING * 0.5, 0, 0, 0],
        formatter: (v) => processFormatterResult(xAxisFormatter(v))
      }
    },
    yAxis: {
      axisLabel: {
        ...theme.typography.overlineDelicate,
        formatter: (v) => processFormatterResult(yAxisFormatter(v))
      }
    }
  };
}
function __generateSerie({
  name,
  data,
  animation
}) {
  return [
    {
      type: "scatter",
      name,
      data,
      animation
    }
  ];
}
const EchartsWrapper = React.memo(
  ReactEcharts,
  (prev, next) => areChartPropsEqual(
    prev.option,
    next.option
  )
);
const HEIGHT = 225;
const EMPTY_ARR$4 = [];
const IDENTITY = (v) => v;
function ScatterPlotWidgetUI({
  name = null,
  data = EMPTY_ARR$4,
  animation = true,
  xAxisFormatter = IDENTITY,
  yAxisFormatter = IDENTITY,
  tooltipFormatter = defaultTooltipFormatter$2,
  isLoading
}) {
  const theme = useTheme();
  const chartInstance = useRef();
  const [options, setOptions] = useState({
    series: []
  });
  const { showSkeleton } = useSkeleton(!!isLoading);
  useEffect(() => {
    const config = __generateDefaultConfig(
      { xAxisFormatter, yAxisFormatter, tooltipFormatter },
      theme
    );
    const series = __generateSerie({
      name,
      data: data || [],
      animation
    });
    setOptions({
      ...config,
      series
    });
  }, [
    data,
    name,
    animation,
    theme,
    xAxisFormatter,
    yAxisFormatter,
    tooltipFormatter
  ]);
  if (showSkeleton) {
    return /* @__PURE__ */ jsx(ScatterPlotSkeleton, { height: HEIGHT });
  }
  return /* @__PURE__ */ jsx(
    EchartsWrapper,
    {
      ref: chartInstance,
      option: options,
      lazyUpdate: true,
      style: { height: HEIGHT }
    }
  );
}
function defaultTooltipFormatter$2(params) {
  const data = Array.isArray(params) ? params[0] : params;
  const value = (data == null ? void 0 : data.value) || [];
  return `[${value[0]}, ${value[1]})`;
}
function TableSkeletonRow({
  width,
  rows = 4,
  index = 0
}) {
  function getSkeletonWidth(index2) {
    const sizes2 = [72, 48, 96, 32, 64];
    const chosenIndex = index2 % sizes2.length;
    return sizes2[chosenIndex];
  }
  return [...Array(rows)].map((_, i) => /* @__PURE__ */ jsx(TableCell, { children: /* @__PURE__ */ jsx(Skeleton, { width: width ?? getSkeletonWidth(index), height: 8 }) }, i));
}
const Pagination = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: theme.spacing(2),
  margin: 0,
  padding: theme.spacing(2, 1)
}));
const TableContainerNoOverflow = styled(TableContainer)(() => ({
  overflow: "hidden"
}));
function TableSkeleton({
  style,
  dense,
  pagination,
  rowsPerPage
}) {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(TableContainerNoOverflow, { style, children: /* @__PURE__ */ jsxs(Table, { "aria-label": "Skeleton table", size: dense ? "small" : "medium", children: [
      /* @__PURE__ */ jsx(TableHead, { children: /* @__PURE__ */ jsxs(TableRow, { children: [
        /* @__PURE__ */ jsx(TableSkeletonRow, { rows: 1, width: 8 }),
        /* @__PURE__ */ jsx(TableSkeletonRow, { width: 56 })
      ] }) }),
      /* @__PURE__ */ jsx(TableBody, { children: [...Array(Math.min(10, rowsPerPage ?? 10))].map(
        (_, i) => /* @__PURE__ */ jsxs(TableRow, { children: [
          /* @__PURE__ */ jsx(TableSkeletonRow, { rows: 1, width: 8 }),
          /* @__PURE__ */ jsx(TableSkeletonRow, { index: i })
        ] }, i)
      ) })
    ] }) }),
    pagination && /* @__PURE__ */ jsxs(Pagination, { children: [
      /* @__PURE__ */ jsx(Skeleton, { width: 56, height: 8 }),
      /* @__PURE__ */ jsx(Box, { mr: 1, children: /* @__PURE__ */ jsx(Skeleton, { width: 52, height: 16 }) }),
      /* @__PURE__ */ jsx(Skeleton, { width: 60, height: 8 }),
      /* @__PURE__ */ jsx(Skeleton, { width: 16, height: 16 }),
      /* @__PURE__ */ jsx(Skeleton, { width: 16, height: 16 })
    ] })
  ] });
}
const TableHeadCellLabel = styled(TableSortLabel)(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
  whiteSpace: "nowrap"
}));
const TableRowStyled = styled(TableRow)(({ theme }) => ({
  maxHeight: theme.spacing(6.5),
  transition: "background-color 0.25s ease",
  "&.MuiTableRow-hover:hover": {
    cursor: "pointer",
    backgroundColor: theme.palette.background.default
  }
}));
const TableCellStyled = styled(TableCell)(() => ({
  overflow: "hidden",
  "& p": {
    maxWidth: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  }
}));
const StyledTablePagination = styled(TablePagination)(({ theme }) => ({
  height: theme.spacing(6),
  overflowX: "auto",
  overflowY: "hidden"
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
}));
const DEFAULT_RPP = 10;
const RPP_OPTIONS = [5, 10, 25];
const EMPTY_FN$8 = () => void 0;
function TableWidgetUI({
  columns,
  rows,
  sorting = false,
  sortBy,
  sortDirection = "asc",
  onSetSortBy = EMPTY_FN$8,
  onSetSortDirection = EMPTY_FN$8,
  pagination = false,
  totalCount,
  page,
  onSetPage = EMPTY_FN$8,
  rowsPerPage = DEFAULT_RPP,
  rowsPerPageOptions = RPP_OPTIONS,
  onSetRowsPerPage = EMPTY_FN$8,
  onRowClick = EMPTY_FN$8,
  onRowMouseEnter,
  onRowMouseLeave,
  height,
  dense = false,
  isLoading,
  lastPageTooltip
}) {
  var _a;
  const paginationRef = useRef(null);
  const intl = useIntl();
  const intlConfig = useImperativeIntl(intl);
  const { showSkeleton } = useSkeleton(!!isLoading);
  function defaultLabelDisplayedRows({
    from,
    to,
    count
  }) {
    return intlConfig.formatMessage(
      { id: "c4r.widgets.table.of" },
      { from, to, count }
    );
  }
  function handleSort(sortField) {
    const isAsc = sortBy === sortField && sortDirection === "asc";
    onSetSortDirection(isAsc ? "desc" : "asc");
    onSetSortBy(sortField);
  }
  function handleChangePage(_event, newPage) {
    onSetPage(newPage);
  }
  function handleChangeRowsPerPage(event) {
    onSetRowsPerPage(parseInt(event.target.value, 10));
    onSetPage(0);
  }
  const fixedHeightStyle = { height: void 0 };
  if (height) {
    const paginationHeight = Math.max(
      ((_a = paginationRef == null ? void 0 : paginationRef.current) == null ? void 0 : _a.clientHeight) ?? 0,
      pagination ? SPACING * 6 : 0
    );
    fixedHeightStyle.height = `calc(${height} - ${paginationHeight}px)`;
  }
  if (showSkeleton) {
    return /* @__PURE__ */ jsx(
      TableSkeleton,
      {
        rowsPerPage,
        style: fixedHeightStyle,
        dense,
        pagination
      }
    );
  }
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(TableContainer, { style: fixedHeightStyle, children: /* @__PURE__ */ jsxs(Table, { stickyHeader: true, size: dense ? "small" : "medium", children: [
      /* @__PURE__ */ jsx(
        TableHeaderComponent,
        {
          columns,
          sorting,
          sortBy,
          sortDirection,
          onSort: handleSort
        }
      ),
      /* @__PURE__ */ jsx(
        TableBodyComponent,
        {
          columns,
          rows,
          onRowMouseEnter,
          onRowMouseLeave,
          onRowClick
        }
      )
    ] }) }),
    pagination && /* @__PURE__ */ jsx(
      StyledTablePagination,
      {
        ref: paginationRef,
        rowsPerPageOptions,
        labelRowsPerPage: intlConfig.formatMessage({
          id: "c4r.widgets.table.rowsPerPage"
        }),
        labelDisplayedRows: defaultLabelDisplayedRows,
        component: "div",
        count: totalCount,
        rowsPerPage,
        page,
        onPageChange: handleChangePage,
        onRowsPerPageChange: handleChangeRowsPerPage,
        ActionsComponent: () => /* @__PURE__ */ jsx(
          TablePaginationActions,
          {
            count: totalCount,
            rowsPerPage,
            page,
            onPageChange: handleChangePage,
            lastPageTooltip
          }
        )
      }
    )
  ] });
}
function TableHeaderComponent({
  columns,
  sorting,
  sortBy,
  sortDirection,
  onSort
}) {
  return /* @__PURE__ */ jsx(TableHead, { children: /* @__PURE__ */ jsx(TableRow, { children: columns.map(({ field, headerName, align }) => /* @__PURE__ */ jsx(TableCell, { align: align ?? "left", children: sorting ? /* @__PURE__ */ jsx(
    TableHeadCellLabel,
    {
      active: sortBy === field,
      direction: sortBy === field ? sortDirection : "asc",
      onClick: () => onSort(field),
      children: headerName ?? field
    }
  ) : headerName ?? field }, field)) }) });
}
function TableBodyComponent({
  columns,
  rows,
  onRowMouseEnter,
  onRowMouseLeave,
  onRowClick
}) {
  return /* @__PURE__ */ jsx(TableBody, { children: rows.map((row, i) => {
    const rowKey = String(row.cartodb_id || row.id || i);
    return /* @__PURE__ */ jsx(
      TableRowStyled,
      {
        hover: !!onRowClick,
        onMouseEnter: () => onRowMouseEnter == null ? void 0 : onRowMouseEnter(row),
        onMouseLeave: () => onRowMouseLeave == null ? void 0 : onRowMouseLeave(row),
        onClick: () => onRowClick && onRowClick(row),
        children: columns.map(
          ({ field, headerName, align, component, formatter }) => {
            var _a;
            let cellValue = (_a = Object.entries(row).find(([key]) => {
              return key.toUpperCase() === field.toUpperCase();
            })) == null ? void 0 : _a[1];
            if (formatter) {
              cellValue = formatter(cellValue);
            } else if (typeof cellValue === "bigint") {
              cellValue = cellValue.toString();
            } else if (Array.isArray(cellValue)) {
              cellValue = `[${cellValue.map((c) => typeof c === "string" ? `"${c}"` : String(c)).join(", ")}]`;
            } else if (typeof cellValue === "object" && cellValue !== null) {
              cellValue = JSON.stringify(cellValue);
            }
            return (headerName ?? field) && /* @__PURE__ */ jsx(
              TableCellStyled,
              {
                scope: "row",
                align: align ?? "left",
                children: component ? component(cellValue) : cellValue
              },
              `${rowKey}_${field}`
            );
          }
        )
      },
      rowKey
    );
  }) });
}
function defaultTooltipFormatter$1(params, stepSize, valueFormatter, stepMultiplier, showNames) {
  const data = Array.isArray(params) ? params : [params];
  const [name] = data[0].data;
  const date = new Date(name);
  const title = formatBucketRange({ date, stepMultiplier, stepSize });
  return `<div style='minWidth: 160px;'>
    <p style='font-weight: 600; line-height: 1; margin: 4px 0;'>${title}</p>
    ${data.reduce((acc, serie) => {
    if (serie.value !== void 0 && serie.value !== null) {
      const HTML = `<div style='display: flex; flex-direction: row; align-items: center; justify-content: spread; height: 20px; gap: 8px;'>
            <div style='display: flex; flex-direction: row; align-items: center; margin: 4px 0;'>
              <div style='width: 8px; height: 8px; margin-right: 4px; border-radius: 50%; border: 2px solid ${String(
        serie.color
      )}'></div>
            </div>
            <p style='line-height: 1; flex: 1; margin-left: 0.5em; min-width: 20px; max-width: 200px; overflow: hidden; text-overflow: ellipsis; align-self: left;'>
            ${showNames && serie.seriesName ? `${serie.seriesName}</p>` : ""}
            </p>
            <p style='line-height: 1; justify-self: flex-end;'>${valueFormatter(
        serie.data[1]
      )}</p>
          </div>`;
      acc.push(HTML);
    }
    return acc;
  }, []).join("")}
    </div>`;
}
var TIME_SERIES_CHART_TYPES = /* @__PURE__ */ ((TIME_SERIES_CHART_TYPES2) => {
  TIME_SERIES_CHART_TYPES2["LINE"] = "line";
  TIME_SERIES_CHART_TYPES2["BAR"] = "bar";
  return TIME_SERIES_CHART_TYPES2;
})(TIME_SERIES_CHART_TYPES || {});
function getTimestamp(date) {
  if (typeof date === "object" && typeof (date == null ? void 0 : date.getTime) === "function") {
    return date.getTime();
  }
  const timestamp = new Date(date).getTime();
  if (isFinite(timestamp)) {
    return timestamp;
  }
}
function getDate(date) {
  if (typeof date === "object" && typeof (date == null ? void 0 : date.getTime) === "function") {
    return date;
  }
  return new Date(date);
}
function findItemIndexByTime(timestamp, data) {
  var _a, _b;
  for (let idx = 0; idx < data.length; idx++) {
    const currentDate = data[idx].name;
    const upperCloseDate = idx < data.length ? (_a = data[idx + 1]) == null ? void 0 : _a.name : currentDate;
    const lowerCloseDate = idx > 0 ? (_b = data[idx - 1]) == null ? void 0 : _b.name : currentDate;
    const upperDiff = Math.abs(currentDate - upperCloseDate);
    const lowerDiff = Math.abs(currentDate - lowerCloseDate);
    const lowerBound = currentDate - lowerDiff * 0.5;
    const upperBound = currentDate + upperDiff * 0.5;
    if (isFinite(lowerBound) && isFinite(upperBound)) {
      if (timestamp >= lowerBound && timestamp <= upperBound) {
        return idx;
      }
    } else if (isFinite(lowerBound)) {
      if (timestamp >= lowerBound) {
        return idx;
      }
    } else if (isFinite(upperBound)) {
      if (timestamp <= upperBound) {
        return idx;
      }
    }
  }
}
function countDistinctTimePoints(data) {
  let lastTime = void 0;
  let result = 0;
  for (const { name: time } of data) {
    if (time === lastTime) {
      continue;
    }
    lastTime = time;
    result++;
  }
  return result;
}
function getMonday(date) {
  const dateCp = new Date(date);
  const day = dateCp.getDay();
  const diff = dateCp.getDate() - day + (day ? 1 : -6);
  dateCp.setDate(diff);
  dateCp.setHours(0, 0, 0, 0);
  return dateCp.getTime();
}
var GroupDateTypes = /* @__PURE__ */ ((GroupDateTypes2) => {
  GroupDateTypes2["YEARS"] = "year";
  GroupDateTypes2["MONTHS"] = "month";
  GroupDateTypes2["WEEKS"] = "week";
  GroupDateTypes2["DAYS"] = "day";
  GroupDateTypes2["HOURS"] = "hour";
  GroupDateTypes2["MINUTES"] = "minute";
  GroupDateTypes2["SECONDS"] = "second";
  return GroupDateTypes2;
})(GroupDateTypes || {});
const fullTimeFormatters = {
  [
    "year"
    /* YEARS */
  ]: formatLocalDate,
  [
    "month"
    /* MONTHS */
  ]: formatLocalDate,
  [
    "week"
    /* WEEKS */
  ]: formatLocalDate,
  [
    "day"
    /* DAYS */
  ]: formatLocalDate,
  [
    "hour"
    /* HOURS */
  ]: formatDateAndTimeWithMinutes,
  [
    "minute"
    /* MINUTES */
  ]: formatDateAndTimeWithSeconds,
  [
    "second"
    /* SECONDS */
  ]: formatDateAndTimeWithSeconds
};
const intervalCalculators = {
  [
    "year"
    /* YEARS */
  ]: getBucketIntervalYears,
  [
    "month"
    /* MONTHS */
  ]: getBucketIntervalMonths,
  [
    "week"
    /* WEEKS */
  ]: getBucketIntervalWeeks,
  [
    "day"
    /* DAYS */
  ]: getBucketIntervalDays,
  [
    "hour"
    /* HOURS */
  ]: getBucketIntervalHours,
  [
    "minute"
    /* MINUTES */
  ]: getBucketIntervalMinutes,
  [
    "second"
    /* SECONDS */
  ]: getBucketIntervalSeconds
};
function formatTime({
  date,
  stepSize
}) {
  const formatter = fullTimeFormatters[stepSize];
  if (!formatter) throw new Error("formatTime: invalid stepSize");
  return formatter(date);
}
function formatTimeRange({
  start,
  end,
  stepSize
}) {
  if (start > end) {
    return formatTimeRange({ start: end, end: start, stepSize });
  }
  const formatter = fullTimeFormatters[stepSize];
  if (!formatter) {
    throw new Error("formatTimeRange: missing formatter or invalid stepSize");
  }
  const startFmt = formatter(start);
  const endFmt = formatter(end);
  return `${startFmt} - ${endFmt}`;
}
function formatBucketRange({
  date,
  stepSize,
  stepMultiplier
}) {
  const formatter = fullTimeFormatters[stepSize];
  if (!formatter) {
    throw new Error("formatBucketRange: missing formatter or invalid stepSize");
  }
  const { start, end } = getBucketInterval({ date, stepSize, stepMultiplier });
  if (stepMultiplier === 1) {
    if (stepSize === "day") {
      return formatTime({ date: start, stepSize });
    }
    if (stepSize === "second") {
      return `${formatLocalDate(start)} ${formatTimeWithSeconds(
        start
      )} - ${formatTimeWithSeconds(end)}`;
    }
  }
  return formatTimeRange({ start, end, stepSize });
}
const secondstoMs = (s) => 1e3 * s;
const minutesToMs = (m) => secondstoMs(60 * m);
const hoursToMs = (h) => minutesToMs(60 * h);
function formatLocalDate(date) {
  return date.toLocaleDateString();
}
function formatDateAndTimeWithMinutes(date) {
  return formatLocalDate(date) + " " + date.toLocaleTimeString(void 0, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  });
}
function formatTimeWithSeconds(date) {
  return date.toLocaleTimeString(void 0, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    second: "2-digit"
  });
}
function formatDateAndTimeWithSeconds(date) {
  return formatLocalDate(date) + " " + formatTimeWithSeconds(date);
}
function getBucketInterval({
  date,
  stepSize,
  stepMultiplier = 1
}) {
  const intervalCalculator = intervalCalculators[stepSize];
  if (!intervalCalculator) {
    throw new Error("getBucketInterval: invalid bucket size");
  }
  return intervalCalculator({ date, stepMultiplier });
}
function createSeries({
  data,
  categories,
  palette,
  fallbackColor
}) {
  const colorMapping = {};
  const series = categories ? categories.map((category, index) => ({
    name: category,
    data: [],
    color: getColorByCategory(String(index), {
      palette,
      fallbackColor,
      colorMapping
    })
  })) : [
    {
      name: "",
      data: [],
      color: fallbackColor
    }
  ];
  for (const { name, value, category, categoryIndex: _categoryIndex } of data) {
    const categoryIndex = _categoryIndex ?? (categories && category ? categories.indexOf(category) : 0);
    if (categoryIndex === -1 || categories && categoryIndex >= categories.length || !Number.isFinite(categoryIndex)) {
      continue;
    }
    series[categoryIndex].data.push([name, value]);
  }
  return series;
}
function getBucketIntervalSeconds({
  date,
  stepMultiplier = 1
}) {
  const start = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    0
  );
  const end = new Date(start.getTime() + secondstoMs(stepMultiplier - 1) + 999);
  return { start, end };
}
function getBucketIntervalMinutes({
  date,
  stepMultiplier = 1
}) {
  const start = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    0,
    0
  );
  const end = new Date(
    start.getTime() + minutesToMs(stepMultiplier - 1) + secondstoMs(59)
  );
  return { start, end };
}
function getBucketIntervalHours({
  date,
  stepMultiplier = 1
}) {
  const start = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    0,
    0,
    0
  );
  const end = new Date(
    start.getTime() + hoursToMs(stepMultiplier - 1) + minutesToMs(59)
  );
  return { start, end };
}
function getBucketIntervalDays({
  date,
  stepMultiplier = 1
}) {
  const start = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    0,
    0,
    0,
    0
  );
  const end = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() + stepMultiplier - 1
  );
  return { start, end };
}
function getBucketIntervalWeeks({
  date,
  stepMultiplier = 1
}) {
  const start = new Date(getMonday(date));
  const end = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate() + 6 + 7 * (stepMultiplier - 1)
  );
  return { start, end };
}
function getBucketIntervalMonths({
  date,
  stepMultiplier = 1
}) {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + stepMultiplier, 0);
  return { start, end };
}
function getBucketIntervalYears({
  date,
  stepMultiplier = 1
}) {
  const start = new Date(date.getFullYear(), 0, 1);
  const end = new Date(date.getFullYear() + (stepMultiplier - 1), 11, 31);
  return { start, end };
}
const EMPTY_FN$7 = () => void 0;
const TimeSeriesContext = createContext({
  isPlaying: false,
  setIsPlaying: EMPTY_FN$7,
  isPaused: false,
  setIsPaused: EMPTY_FN$7,
  timeWindow: [],
  setTimeWindow: EMPTY_FN$7,
  onTimeWindowUpdate: EMPTY_FN$7,
  togglePlay: EMPTY_FN$7,
  stop: EMPTY_FN$7,
  intl: {}
});
function useTimeSeriesContext() {
  return useContext(TimeSeriesContext);
}
function TimeSeriesProvider({
  children,
  isPlaying,
  isPaused,
  onPlay = EMPTY_FN$7,
  onPause = EMPTY_FN$7,
  onStop = EMPTY_FN$7,
  timeWindow,
  onTimeWindowUpdate = EMPTY_FN$7,
  intl
}) {
  const [_isPlaying, setIsPlaying] = useState(isPlaying);
  const [_isPaused, setIsPaused] = useState(isPaused);
  const [_timeWindow, setTimeWindow] = useState([]);
  const intlConfig = useImperativeIntl(intl);
  useEffect(() => {
    const sorted = _timeWindow.sort((timeA, timeB) => timeA < timeB ? -1 : 1).map(getDate);
    onTimeWindowUpdate == null ? void 0 : onTimeWindowUpdate(sorted);
  }, [_timeWindow, onTimeWindowUpdate]);
  const togglePlay = useCallback(() => {
    if (_isPlaying) {
      if (onPause) onPause();
      setIsPaused(true);
    }
    if (!_isPlaying) {
      if (onPlay) onPlay();
      setIsPaused(false);
    }
    setIsPlaying((oldState) => !oldState);
  }, [_isPlaying, onPause, onPlay]);
  const stopWrapper = useCallback(() => {
    setIsPlaying(false);
    setIsPaused(false);
    setTimeWindow([]);
  }, []);
  const stop = useCallback(() => {
    stopWrapper();
    setTimeout(onStop);
  }, [onStop, stopWrapper]);
  useEffect(() => {
    if (isPlaying !== _isPlaying) {
      setIsPlaying(isPlaying);
    }
  }, [isPlaying]);
  useEffect(() => {
    if (isPaused !== _isPaused) {
      setIsPaused(isPaused);
    }
  }, [isPaused]);
  useEffect(() => {
    if (!timeWindow) return;
    const timeWindowTimestamp = timeWindow.map(getTimestamp);
    const _timeWindowTimestamp = _timeWindow.map(getTimestamp);
    if (timeWindowTimestamp[0] !== _timeWindowTimestamp[0] || timeWindowTimestamp[1] !== _timeWindowTimestamp[1]) {
      setTimeWindow(timeWindowTimestamp);
    }
  }, timeWindow);
  return /* @__PURE__ */ jsx(
    TimeSeriesContext.Provider,
    {
      value: {
        isPlaying: !!_isPlaying,
        setIsPlaying,
        isPaused: !!_isPaused,
        setIsPaused,
        timeWindow: _timeWindow,
        setTimeWindow,
        onTimeWindowUpdate,
        togglePlay,
        stop,
        intl: intlConfig
      },
      children
    }
  );
}
const events = {};
let initialTimeWindow = null;
const seriesFinder = { seriesId: "0" };
function useTimeSeriesInteractivity({
  echartsInstance,
  data,
  canSelectLines,
  filterable
}) {
  const theme = useTheme();
  const { isPlaying, isPaused, setIsPaused, timeWindow, setTimeWindow, stop } = useTimeSeriesContext();
  const [isMarkLineSelected, setIsMarkLineSelected] = useState(false);
  const [isMarkAreaSelected, setIsMarkAreaSelected] = useState(false);
  const [isMarkAreaMoving, setIsMarkAreaMoving] = useState(false);
  const [oldMarkAreaPosition, setOldMarkAreaPosition] = useState(0);
  const zr = useMemo(() => echartsInstance == null ? void 0 : echartsInstance.getZr(), [echartsInstance]);
  const updateCursor = useCallback(
    (cursor) => zr == null ? void 0 : zr.setCursorStyle(cursor),
    [zr]
  );
  const updateTimelineByCoordinate = useCallback(
    (params) => {
      if (echartsInstance) {
        const [x] = echartsInstance.convertFromPixel(seriesFinder, [
          params.offsetX,
          params.offsetY
        ]);
        const itemIndex = findItemIndexByTime(x, data);
        setTimeWindow(itemIndex !== void 0 ? [data[itemIndex].name] : []);
      }
    },
    [data, echartsInstance, setTimeWindow]
  );
  useEffect(() => {
    function clickEvent(params) {
      if (!filterable) {
        return;
      }
      if (canSelectLines && params.target) {
        return;
      }
      if (!timeWindow.length) {
        updateTimelineByCoordinate(params);
        if (!isPaused && !isPlaying) {
          setIsPaused(true);
        }
      }
      if (timeWindow.length === 2) {
        setTimeWindow([]);
        stop();
      }
    }
    return addEventWithCleanUp(zr, "click", clickEvent);
  }, [
    zr,
    isPaused,
    isPlaying,
    setIsPaused,
    setTimeWindow,
    stop,
    timeWindow.length,
    updateTimelineByCoordinate,
    canSelectLines,
    filterable
  ]);
  useEffect(() => {
    function mouseDownEvent(params) {
      var _a;
      if (!filterable) {
        return;
      }
      if (((_a = params.target) == null ? void 0 : _a.type) === "ec-line") {
        setIsMarkLineSelected(true);
        updateCursor("grabbing");
        return;
      }
      if (timeWindow.length === 2) {
        const [x] = echartsInstance.convertFromPixel(seriesFinder, [
          params.offsetX,
          params.offsetY
        ]);
        if (x >= timeWindow[0] && x <= timeWindow[1]) {
          setIsMarkAreaMoving(true);
          const newMarkAreaPosition = x - timeWindow[0];
          setOldMarkAreaPosition(newMarkAreaPosition);
          return;
        }
      }
      if (echartsInstance) {
        setIsMarkAreaSelected(true);
        const [x] = echartsInstance.convertFromPixel(seriesFinder, [
          params.offsetX,
          params.offsetY
        ]);
        initialTimeWindow = x;
        return;
      }
    }
    return addEventWithCleanUp(zr, "mousedown", mouseDownEvent);
  }, [zr, echartsInstance, timeWindow, updateCursor, filterable]);
  useEffect(() => {
    function mouseMoveEvent(params) {
      var _a;
      if (((_a = params == null ? void 0 : params.target) == null ? void 0 : _a.type) === "polygon") {
        updateCursor("move");
      }
      if (isMarkLineSelected && echartsInstance) {
        updateTimelineByCoordinate(params);
        updateCursor("grabbing");
      }
      if (isMarkAreaSelected && echartsInstance) {
        const [x] = echartsInstance.convertFromPixel(seriesFinder, [
          params.offsetX,
          params.offsetY
        ]);
        if (initialTimeWindow === x) {
          setTimeWindow([]);
        } else {
          setTimeWindow([initialTimeWindow, x]);
        }
      }
    }
    return addEventWithCleanUp(zr, "mousemove", mouseMoveEvent);
  }, [
    zr,
    echartsInstance,
    isMarkAreaSelected,
    isMarkLineSelected,
    setTimeWindow,
    updateCursor,
    updateTimelineByCoordinate
  ]);
  useEffect(() => {
    function mouseUpEvent(params) {
      if (isMarkLineSelected) {
        setIsMarkLineSelected(false);
        updateCursor("default");
      }
      if (isMarkAreaSelected) {
        setIsMarkAreaSelected(false);
        initialTimeWindow = null;
        if (timeWindow.length === 1) {
          setTimeWindow([]);
        }
      }
      if (isMarkAreaMoving && echartsInstance) {
        const [x] = echartsInstance.convertFromPixel(seriesFinder, [
          params.offsetX,
          params.offsetY
        ]);
        const newMarkAreaPosition = x - timeWindow[0];
        if (oldMarkAreaPosition) {
          const diff = newMarkAreaPosition - oldMarkAreaPosition;
          setTimeWindow([timeWindow[0] + diff, timeWindow[1] + diff]);
        }
        setIsMarkAreaMoving(false);
      }
    }
    return addEventWithCleanUp(zr, "mouseup", mouseUpEvent);
  }, [
    zr,
    echartsInstance,
    isMarkAreaMoving,
    isMarkAreaSelected,
    isMarkLineSelected,
    oldMarkAreaPosition,
    setTimeWindow,
    timeWindow,
    updateCursor
  ]);
  useEffect(() => {
    if (!filterable && timeWindow.length) {
      setTimeWindow([]);
    }
  }, [filterable, setTimeWindow, timeWindow.length]);
  const timelineOptions = useMemo(() => {
    var _a;
    if (timeWindow.length !== 1) {
      return void 0;
    }
    const timestamp = timeWindow[0];
    const itemIndex = findItemIndexByTime(timestamp, data);
    if (itemIndex === void 0) {
      return void 0;
    }
    const xAxis = (_a = data[itemIndex]) == null ? void 0 : _a.name;
    return (
      // Cannot have markLine and markArea at the same time
      xAxis !== void 0 && {
        symbol: ["none", "none"],
        animationDuration: 100,
        animationDurationUpdate: 150,
        animationEasingUpdate: "linear",
        data: isPaused || isPlaying ? [
          {
            label: {
              show: false
            },
            emphasis: {
              label: {
                show: false
              }
            },
            xAxis,
            lineStyle: {
              type: "solid",
              color: theme.palette.primary.main,
              width: 1.5,
              shadowColor: theme.palette.primary.light,
              shadowBlur: 3
            }
          }
        ] : []
      }
    );
  }, [data, isPaused, isPlaying, theme, timeWindow]);
  const timeWindowOptions = useMemo(
    () => timeWindow.length === 2 && {
      data: [[{ coord: [timeWindow[0]] }, { coord: [timeWindow[1]] }]],
      itemStyle: {
        color: theme.palette.primary.main,
        opacity: 0.2
      }
    },
    [theme, timeWindow]
  );
  return { timelineOptions, timeWindowOptions };
}
function addEventWithCleanUp(zr, eventKey, event) {
  if (zr == null ? void 0 : zr.handler) {
    events[eventKey] = event;
    zr.on(eventKey, event);
    return () => {
      if (events[eventKey] && (zr == null ? void 0 : zr.handler)) {
        zr.off(eventKey, event);
      }
    };
  }
}
const CHART_HEIGHT_DEFAULT = SPACING * 22;
const CHART_HEIGHT_FITHEIGHT = "100%";
const DEFAULT_SPLIT_NUMBER = 5;
const MIN_ADAPTIVE_SPLIT_NUMBER = 3;
const ADAPTIVE_SPLIT_NUMBER_SPACING = SPACING * 20;
function TimeSeriesChart({
  chartType,
  formatter,
  timeAxisSplitNumber,
  tooltip,
  tooltipFormatter,
  data,
  series,
  categories,
  height: heightProp,
  fitHeight,
  animation,
  filterable,
  selectedCategories,
  onCategoryClick,
  yAxisType
}) {
  const theme = useTheme();
  const [echartsInstance, setEchartInstance] = useState();
  const onChartReady = (_echartsInstance) => setEchartInstance(_echartsInstance);
  const [width, setWidth] = useState();
  const maxValue = useMemo(
    () => series.reduce(
      (accOut, { data: data2 }) => data2.reduce(
        (accInt, row) => row[1] > accInt ? row[1] : accInt,
        accOut
      ),
      Number.MIN_VALUE
    ),
    [series]
  );
  const tooltipOptions = useMemo(
    () => ({
      show: tooltip,
      trigger: "axis",
      appendToBody: true,
      padding: [SPACING * 0.5, SPACING],
      textStyle: {
        ...theme.typography.caption,
        fontSize: 11,
        color: theme.palette.common.white
      },
      borderWidth: 0,
      backgroundColor: theme.palette.black[90],
      position: (point, params, dom, rect, size) => {
        const position = { top: 0 };
        if (size.contentSize[0] < size.viewSize[0] - point[0]) {
          position.left = point[0] + SPACING * 1.5;
        } else {
          position.right = size.viewSize[0] - point[0] + SPACING * 1.5;
        }
        return position;
      },
      ...tooltipFormatter ? { formatter: tooltipFormatter } : {}
    }),
    [theme, tooltip, tooltipFormatter]
  );
  const axisOptions = useMemo(() => {
    const denseAxisConfig = {
      margin: 0,
      verticalAlign: "bottom",
      padding: [0, 0, SPACING * 1.25, 0],
      inside: true,
      color: (value) => {
        let col = "transparent";
        if (value >= maxValue) {
          col = theme.palette.black[60];
        }
        return col;
      }
    };
    const fullAxisConfig = {
      margin: 0,
      verticalAlign: "middle",
      padding: [0, SPACING * 0.75, 0, 0],
      color: theme.palette.black[60]
    };
    const yAxisLabelConfig = yAxisType === "dense" ? denseAxisConfig : fullAxisConfig;
    return {
      axisPointer: {
        lineStyle: {
          color: theme.palette.black[40]
        }
      },
      xAxis: {
        type: "time",
        axisLine: {
          show: true,
          lineStyle: {
            color: theme.palette.grey[900],
            opacity: 0.2
          }
        },
        axisLabel: {
          fontWeight: theme.typography.fontWeightRegular,
          fontSize: theme.typography.caption.fontSize,
          fontFamily: theme.typography.caption.fontFamily,
          // echarts doesn't intepret lineHeight properly, so hack it around
          lineHeight: theme.typography.caption.lineHeight * 8,
          letterSpacing: theme.typography.caption.letterSpacing,
          // https://echarts.apache.org/en/option.html#xAxis.axisLabel.formatter
          formatter: {
            year: "{yearStyle|{yyyy}}"
          },
          rich: {
            yearStyle: {
              fontWeight: theme.typography.fontWeightMedium,
              fontSize: theme.typography.caption.fontSize,
              fontFamily: theme.typography.caption.fontFamily,
              letterSpacing: theme.typography.caption.letterSpacing,
              lineHeight: theme.typography.caption.lineHeight * 8
            }
          }
        },
        axisTick: {
          show: false
        },
        splitNumber: timeAxisSplitNumber ?? (width !== void 0 ? Math.min(
          MIN_ADAPTIVE_SPLIT_NUMBER,
          Math.ceil(width / ADAPTIVE_SPLIT_NUMBER_SPACING)
        ) : DEFAULT_SPLIT_NUMBER)
      },
      yAxis: {
        type: "value",
        axisLabel: {
          show: true,
          showMaxLabel: true,
          showMinLabel: false,
          ...yAxisLabelConfig,
          ...formatter ? { formatter: (v) => formatter(v) } : {},
          fontWeight: theme.typography.fontWeightRegular,
          fontSize: theme.typography.overlineDelicate.fontSize,
          fontFamily: theme.typography.overlineDelicate.fontFamily,
          // echarts doesn't intepret lineHeight properly, so hack it around
          lineHeight: theme.typography.overlineDelicate.lineHeight * 8,
          letterSpacing: theme.typography.overlineDelicate.letterSpacing
        },
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        splitLine: {
          show: true,
          onZero: false,
          lineStyle: {
            color: theme.palette.black[4]
          }
        },
        max: maxValue
      }
    };
  }, [theme, maxValue, formatter, width, timeAxisSplitNumber, yAxisType]);
  const { timelineOptions: markLine, timeWindowOptions: markArea } = useTimeSeriesInteractivity({
    echartsInstance,
    data,
    canSelectLines: Boolean(onCategoryClick),
    filterable
  });
  const seriesOptions = useMemo(
    () => series.map(({ data: data2, color, name }, i) => {
      const somethingSelected = selectedCategories && selectedCategories.length > 0;
      const isSelected = somethingSelected && selectedCategories.includes(name);
      const actualColor = !somethingSelected || isSelected ? color : theme.palette.action.disabledBackground;
      return {
        id: String(i),
        name,
        markLine: i === 0 ? markLine : void 0,
        markArea: i === 0 ? markArea : void 0,
        animation,
        data: data2,
        type: chartType,
        smooth: true,
        color: actualColor,
        z: somethingSelected && isSelected ? 10 : 0,
        lineStyle: {
          width: 2
        },
        showSymbol: false,
        emphasis: {
          scale: 2,
          lineStyle: {
            width: 2,
            color: actualColor
          }
        }
      };
    }),
    [
      series,
      markLine,
      markArea,
      animation,
      chartType,
      selectedCategories,
      theme
    ]
  );
  const options = useMemo(
    () => ({
      grid: {
        left: SPACING * (yAxisType === "dense" ? 2 : 3.5),
        top: SPACING * 4,
        right: SPACING * 2,
        bottom: SPACING * 3
      },
      color: [theme.palette.secondary.main],
      tooltip: tooltipOptions,
      ...axisOptions,
      series: seriesOptions
    }),
    [
      axisOptions,
      seriesOptions,
      yAxisType,
      theme.palette.secondary.main,
      tooltipOptions
    ]
  );
  const handleClick = useCallback(
    (params) => {
      if (categories && onCategoryClick && params.componentType === "series") {
        const category = categories[params.seriesIndex];
        onCategoryClick(category);
      }
    },
    [categories, onCategoryClick]
  );
  const onEvents = { click: handleClick };
  useEffect(() => {
    var _a;
    if (typeof ResizeObserver === "undefined" || !echartsInstance) {
      return;
    }
    const element = (_a = echartsInstance == null ? void 0 : echartsInstance.getDom()) == null ? void 0 : _a.parentElement;
    if (!element) {
      return;
    }
    const observer = new ResizeObserver(() => {
      setWidth(element.clientWidth);
      echartsInstance.resize();
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [echartsInstance]);
  const height = fitHeight ? CHART_HEIGHT_FITHEIGHT : heightProp ?? CHART_HEIGHT_DEFAULT;
  useLayoutEffect(() => {
    var _a;
    const element = (_a = echartsInstance == null ? void 0 : echartsInstance.getDom()) == null ? void 0 : _a.parentElement;
    if (element) {
      setWidth(element.clientWidth);
    }
    echartsInstance == null ? void 0 : echartsInstance.resize();
  }, [height, fitHeight, echartsInstance]);
  return /* @__PURE__ */ jsx(
    ReactEcharts,
    {
      option: options,
      onEvents,
      onChartReady,
      style: { height }
    }
  );
}
const TIME_WINDOW_STEP_BY_STEP_SIZE = {
  [GroupDateTypes.YEARS]: 60 * 60 * 24 * 7,
  // Week
  [GroupDateTypes.MONTHS]: 60 * 60 * 24,
  // Day
  [GroupDateTypes.WEEKS]: 60 * 60 * 24,
  // Day
  [GroupDateTypes.DAYS]: 60 * 60 * 12,
  // Half day
  [GroupDateTypes.HOURS]: 60 * 60,
  // Hour
  [GroupDateTypes.MINUTES]: 60 * 15,
  // Quarter hour
  [GroupDateTypes.SECONDS]: 1
  // Second
};
const SPEED_FACTORS = [0.5, 1, 2, 3];
function TimeSeriesControls({
  data,
  stepSize
}) {
  const [anchorSpeedEl, setAnchorSpeedEl] = useState(null);
  const [speed, setSpeed] = useState(1);
  const animationRef = useRef({ animationFrameId: null, timeoutId: null });
  const {
    isPlaying,
    isPaused,
    timeWindow,
    setTimeWindow,
    stop,
    togglePlay,
    intl
  } = useTimeSeriesContext();
  useDidMountEffect(
    () => (isPlaying || isPaused) && stop(),
    // Only executed when data changes
    [data]
  );
  const stopAnimation = () => {
    const { animationFrameId, timeoutId } = animationRef.current;
    if (animationFrameId) {
      window.cancelAnimationFrame(animationFrameId);
    }
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  };
  const handleStop = useCallback(() => {
    stopAnimation();
    stop();
  }, [stop]);
  const handleTogglePlay = () => {
    stopAnimation();
    togglePlay();
  };
  useEffect(() => {
    if (isPlaying && timeWindow.length === 2 && data.length) {
      const timeWindowStep = TIME_WINDOW_STEP_BY_STEP_SIZE[stepSize];
      const msTimeWindowStep = timeWindowStep * 1e3;
      animateTimeWindow({
        data,
        timeWindow,
        msTimeWindowStep: msTimeWindowStep * speed,
        drawFrame: (newTimeWindow) => {
          setTimeWindow(newTimeWindow);
        },
        onEnd: () => {
          setTimeout(handleStop, 250);
        },
        animationRef
      });
      return () => stopAnimation();
    }
  }, [data, isPlaying, stepSize, setTimeWindow, handleStop, speed]);
  useEffect(() => {
    if (isPlaying && timeWindow.length === 1 && data.length) {
      animateTimeline({
        speed,
        timelinePosition: timeWindow[0],
        data,
        drawFrame: (newTimelinePosition) => {
          setTimeWindow([newTimelinePosition]);
        },
        onEnd: () => {
          setTimeout(handleStop, 250);
        },
        animationRef
      });
      return () => stopAnimation();
    } else if (isPlaying && timeWindow.length === 0) {
      setTimeWindow([data[0].name]);
    }
  }, [data, isPlaying, speed, timeWindow.length, handleStop]);
  const handleOpenSpeedMenu = (e) => {
    if (e == null ? void 0 : e.currentTarget) {
      setAnchorSpeedEl(e.currentTarget);
    }
  };
  const handleCloseSpeedMenu = () => {
    setAnchorSpeedEl(null);
  };
  const handleSpeedUpdate = (newSpeed) => {
    setSpeed(newSpeed);
    handleCloseSpeedMenu();
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      IconButton$1,
      {
        size: "small",
        color: "default",
        onClick: handleOpenSpeedMenu,
        "data-testid": "clock",
        children: /* @__PURE__ */ jsx(ClockIcon, {})
      }
    ),
    /* @__PURE__ */ jsxs(
      Menu,
      {
        anchorEl: anchorSpeedEl,
        keepMounted: true,
        open: Boolean(anchorSpeedEl),
        onClose: handleCloseSpeedMenu,
        children: [
          /* @__PURE__ */ jsx(MenuItem, { disabled: true, children: /* @__PURE__ */ jsx(Typography, { variant: "caption", color: "textSecondary", children: intl.formatMessage({ id: "c4r.widgets.timeSeries.speed" }) }) }),
          SPEED_FACTORS.map((speedItem) => /* @__PURE__ */ jsxs(
            MenuItem,
            {
              selected: speedItem === speed,
              onClick: () => handleSpeedUpdate(speedItem),
              children: [
                speedItem,
                "x"
              ]
            },
            speedItem
          ))
        ]
      }
    ),
    /* @__PURE__ */ jsx(Box, { mt: 2, children: /* @__PURE__ */ jsx(
      IconButton$1,
      {
        size: "small",
        color: "primary",
        disabled: !(isPaused || isPlaying),
        onClick: handleStop,
        "data-testid": "stop",
        children: /* @__PURE__ */ jsx(StopIcon, {})
      }
    ) }),
    /* @__PURE__ */ jsx(Box, { mt: 0.75, children: /* @__PURE__ */ jsx(
      IconButton$1,
      {
        "data-testid": "play-pause",
        size: "small",
        color: "primary",
        onClick: handleTogglePlay,
        children: isPlaying ? /* @__PURE__ */ jsx(PauseIcon, {}) : /* @__PURE__ */ jsx(PlayIcon, {})
      }
    ) })
  ] });
}
function ClockIcon() {
  return /* @__PURE__ */ jsx(SvgIcon, { viewBox: "0 0 20 20", children: /* @__PURE__ */ jsx(
    "path",
    {
      d: "M12.5 1.254h-5v1.667h5V1.254zM9.167 12.088h1.666v-5H9.167v5zm6.691-5.517 1.184-1.183a9.207 9.207 0 0 0-1.175-1.175l-1.184 1.183A7.468 7.468 0 0 0 10 3.746a7.5 7.5 0 0 0-7.5 7.5c0 4.141 3.35 7.5 7.5 7.5s7.5-3.358 7.5-7.5a7.504 7.504 0 0 0-1.642-4.675zM10 17.088a5.83 5.83 0 0 1-5.833-5.834A5.83 5.83 0 0 1 10 5.421a5.83 5.83 0 0 1 5.833 5.833A5.83 5.83 0 0 1 10 17.087z",
      id: "-↳Color",
      fill: "inherit"
    }
  ) });
}
function PlayIcon() {
  return /* @__PURE__ */ jsx(SvgIcon, { "data-testid": "play-icon", children: /* @__PURE__ */ jsx(
    "path",
    {
      d: "M10 16.5l6-4.5-6-4.5v9zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z",
      id: "-↳Color",
      fill: "inherit"
    }
  ) });
}
function StopIcon() {
  return /* @__PURE__ */ jsx(SvgIcon, { children: /* @__PURE__ */ jsx(
    "path",
    {
      d: "M12 2c5.52 0 10 4.48 10 10s-4.48 10-10 10S2 17.52 2 12 6.48 2 12 2zm0 2c-4.41 0-8 3.59-8 8s3.59 8 8 8 8-3.59 8-8-3.59-8-8-8zm4 4v8H8V8h8zm-2 2h-4v4h4v-4z",
      id: "-↳Color",
      fill: "inherit"
    }
  ) });
}
function PauseIcon() {
  return /* @__PURE__ */ jsx(SvgIcon, { "data-testid": "pause-icon", children: /* @__PURE__ */ jsx(
    "path",
    {
      d: "M9,16 L11,16 L11,8 L9,8 L9,16 Z M12,2 C6.48,2 2,6.48 2,12 C2,17.52 6.48,22 12,22 C17.52,22 22,17.52 22,12 C22,6.48 17.52,2 12,2 Z M12,20 C7.59,20 4,16.41 4,12 C4,7.59 7.59,4 12,4 C16.41,4 20,7.59 20,12 C20,16.41 16.41,20 12,20 Z M13,16 L15,16 L15,8 L13,8 L13,16 Z",
      id: "-↳Color",
      fill: "inherit"
    }
  ) });
}
function useDidMountEffect(func, deps = []) {
  const didMount = useRef(false);
  useEffect(() => {
    if (didMount.current) func();
    else didMount.current = true;
  }, deps);
}
function animateTimeWindow({
  msTimeWindowStep,
  timeWindow,
  data,
  drawFrame,
  onEnd,
  animationRef
}) {
  let currentTimeWindow = timeWindow;
  const fireAnimation = () => {
    animationRef.current.animationFrameId = window.requestAnimationFrame(animate);
  };
  const animate = () => {
    currentTimeWindow = [
      currentTimeWindow[0] + msTimeWindowStep,
      currentTimeWindow[1] + msTimeWindowStep
    ];
    if (currentTimeWindow[1] > data[data.length - 1].name) {
      onEnd();
    } else {
      drawFrame(currentTimeWindow);
      fireAnimation();
    }
  };
  fireAnimation();
}
const MIN_FPS = 2;
function animateTimeline({
  speed,
  timelinePosition,
  data,
  drawFrame,
  onEnd,
  animationRef
}) {
  let currentItemIndex = findItemIndexByTime(timelinePosition, data) ?? 0;
  let currentTime = data[currentItemIndex].name;
  const numberOfTimePoints = countDistinctTimePoints(data);
  const fpsToUse = Math.max(
    Math.round(Math.sqrt(numberOfTimePoints) / 2),
    // FPS based on number of unique time points
    MIN_FPS
    // Min FPS
  ) * speed;
  const fireAnimation = () => {
    animationRef.current.timeoutId = setTimeout(() => {
      animationRef.current.animationFrameId = window.requestAnimationFrame(animate);
    }, 1e3 / fpsToUse);
  };
  const animate = () => {
    for (; currentItemIndex < data.length; currentItemIndex++) {
      const itemTime = data[currentItemIndex].name;
      if (itemTime !== currentTime) {
        currentTime = itemTime;
        break;
      }
    }
    if (currentItemIndex === data.length) {
      onEnd();
    } else {
      drawFrame(currentTime);
      fireAnimation();
    }
  };
  fireAnimation();
}
const Root$1 = styled(Box)(() => ({
  containerType: "inline-size",
  display: "flex",
  flexDirection: "column"
}));
const BoxVert = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column"
}));
const BoxHorz = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row"
}));
const Header$1 = styled(BoxHorz)(() => ({
  alignItems: "center",
  justifyContent: "space-between"
}));
const ControlsBox = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  marginLeft: 0,
  paddingLeft: theme.spacing(1),
  [`@container (max-width: ${BREAKPOINTS.XS}px)`]: {
    paddingLeft: 0
  },
  paddingBottom: theme.spacing(1.5),
  alignSelf: "flex-end"
}));
const ChartBox = styled(Box)(({ theme }) => ({
  alignSelf: "normal",
  minWidth: 0,
  paddingLeft: theme.spacing(5),
  paddingBottom: theme.spacing(1.5),
  [`@container (max-width: ${BREAKPOINTS.XS}px)`]: {
    paddingLeft: theme.spacing(1)
  }
}));
function TimeSeriesLayout({
  fitHeight,
  header,
  controls,
  chart,
  legend
}) {
  const legendFragment = legend && /* @__PURE__ */ jsx(Box, { flex: 0, mt: 2, children: legend });
  return /* @__PURE__ */ jsxs(
    Root$1,
    {
      style: {
        flex: fitHeight ? "1" : void 0
      },
      children: [
        /* @__PURE__ */ jsx(Header$1, { flex: 0, children: header }),
        controls ? /* @__PURE__ */ jsxs(BoxVert, { flex: 1, children: [
          /* @__PURE__ */ jsxs(BoxHorz, { flex: 1, children: [
            /* @__PURE__ */ jsx(ControlsBox, { flex: 0, children: controls }),
            /* @__PURE__ */ jsx(ChartBox, { flex: 1, children: chart })
          ] }),
          legendFragment
        ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(Box, { pb: 1.5, flex: 1, children: chart }),
          legendFragment
        ] })
      ]
    }
  );
}
const GraphLine = (props) => /* @__PURE__ */ jsx(SvgIcon, { width: "720", height: "124", viewBox: "0 0 720 124", ...props, children: /* @__PURE__ */ jsx(
  "path",
  {
    d: "M0.74707 123C11.8168 119.656 26.7934 118.37 53.4909 102.164C73.1959 90.2031 92.5604 81.3284 108.188 76.6983C122.841 72.357 136.188 74.3832 144.653 75.1549C150.496 75.6876 195.443 82.8718 207.164 83.6435C218.885 84.4152 241.676 87.502 274.234 80.5567C306.791 73.6115 305.869 75.4585 330.233 67.438C388.837 48.1456 433.461 2.6348 458.511 1.07229C495.627 -1.24279 512.557 52.7758 539.255 58.1776C565.952 63.5795 583.534 64.3512 601.766 55.0909C619.999 45.8305 625.859 40.4287 646.045 38.1136C666.231 35.7985 683.812 41.972 718.975 70.5247",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinejoin: "round",
    vectorEffect: "non-scaling-stroke"
  }
) });
const Controls = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-end"
}));
const Graph = styled(Box)(() => ({
  position: "relative",
  height: "100%",
  alignSelf: "normal",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-end"
}));
const MaxValue = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.25, 0, 0.5, 2)
}));
const GraphLineBox = styled(SkeletonBarsGrid)(({ theme }) => ({
  flexDirection: "column",
  flex: 1,
  height: "100%",
  overflow: "hidden",
  svg: {
    height: "100%",
    width: "100%",
    fontSize: "initial",
    fill: "none",
    path: {
      stroke: theme.palette.black[8]
    }
  }
}));
const GraphXAxis = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  padding: theme.spacing(1.25, 0.5, 0.75, 0.5)
}));
const Legend = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(2),
  margin: 0,
  padding: theme.spacing(1.5, 1, 0.25, 1)
}));
const LegendItem = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(1)
}));
const TimeSeriesSkeleton = ({
  fitHeight,
  height: heightProp,
  showControls,
  showLegend
}) => {
  const header = /* @__PURE__ */ jsx(Box, { pt: 1.25, pb: 0.75, children: /* @__PURE__ */ jsx(Skeleton, { width: 48, height: 8 }) });
  const controls = showControls && /* @__PURE__ */ jsxs(Controls, { children: [
    /* @__PURE__ */ jsx(Box, { p: 0.5, children: /* @__PURE__ */ jsx(Skeleton, { width: 16, height: 16 }) }),
    /* @__PURE__ */ jsx(Box, { mt: 2, p: 0.5, children: /* @__PURE__ */ jsx(Skeleton, { width: 16, height: 16 }) }),
    /* @__PURE__ */ jsx(Box, { mt: 1, p: 0.5, children: /* @__PURE__ */ jsx(Skeleton, { width: 16, height: 16 }) })
  ] });
  const height = fitHeight ? CHART_HEIGHT_FITHEIGHT : heightProp ?? CHART_HEIGHT_DEFAULT;
  const chart = /* @__PURE__ */ jsxs(Graph, { style: { height }, children: [
    /* @__PURE__ */ jsx(MaxValue, { flex: 0, children: /* @__PURE__ */ jsx(Skeleton, { width: 24, height: 8 }) }),
    /* @__PURE__ */ jsx(GraphLineBox, { flex: 1, children: /* @__PURE__ */ jsx(GraphLine, { preserveAspectRatio: "none" }) }),
    /* @__PURE__ */ jsxs(GraphXAxis, { flex: 0, children: [
      /* @__PURE__ */ jsx(Skeleton, { width: 32, height: 8 }),
      /* @__PURE__ */ jsx(Skeleton, { width: 32, height: 8 })
    ] })
  ] });
  const legend = showLegend && /* @__PURE__ */ jsxs(Legend, { children: [
    /* @__PURE__ */ jsxs(LegendItem, { children: [
      /* @__PURE__ */ jsx(Skeleton, { width: 8, height: 8 }),
      /* @__PURE__ */ jsx(Skeleton, { width: 48, height: 8 })
    ] }),
    /* @__PURE__ */ jsxs(LegendItem, { children: [
      /* @__PURE__ */ jsx(Skeleton, { width: 8, height: 8 }),
      /* @__PURE__ */ jsx(Skeleton, { width: 48, height: 8 })
    ] })
  ] });
  return /* @__PURE__ */ jsx(
    TimeSeriesLayout,
    {
      fitHeight: !!fitHeight,
      header,
      controls,
      chart,
      legend: showLegend && legend
    }
  );
};
const IDENTITY_FN$4 = (v) => v;
const EMPTY_ARR$3 = [];
function TimeSeriesWidgetUI({
  data = EMPTY_ARR$3,
  categories,
  stepSize,
  stepMultiplier = 1,
  chartType = TIME_SERIES_CHART_TYPES.LINE,
  timeAxisSplitNumber,
  tooltip = true,
  tooltipFormatter = defaultTooltipFormatter$1,
  formatter = IDENTITY_FN$4,
  height,
  fitHeight,
  showControls = true,
  animation = true,
  filterable = true,
  onTimelineUpdate,
  timeWindow,
  timelinePosition,
  onTimeWindowUpdate,
  selectedCategories,
  onSelectedCategoriesChange,
  isPlaying = false,
  onPlay,
  isPaused = false,
  onPause,
  onStop,
  isLoading = false,
  palette = Object.values(commonPalette.qualitative.bold),
  showLegend,
  yAxisType = "dense"
}) {
  const prevEmittedTimeWindow = useRef([]);
  const intl = useIntl();
  const { showSkeleton } = useSkeleton(!!isLoading);
  const handleTimeWindowUpdate = useCallback(
    (timeWindow2) => {
      var _a, _b;
      if (timeWindow2.length === 2) {
        if (((_a = prevEmittedTimeWindow.current) == null ? void 0 : _a.length) === 1) {
          onTimelineUpdate == null ? void 0 : onTimelineUpdate(void 0);
        }
        const sorted = timeWindow2.sort((timeA, timeB) => timeA < timeB ? -1 : 1).map(getDate);
        onTimeWindowUpdate == null ? void 0 : onTimeWindowUpdate(sorted);
      }
      if (timeWindow2.length === 1) {
        if (((_b = prevEmittedTimeWindow.current) == null ? void 0 : _b.length) === 2) {
          onTimeWindowUpdate == null ? void 0 : onTimeWindowUpdate([]);
        }
        const itemIndex = findItemIndexByTime(Number(timeWindow2[0]), data);
        onTimelineUpdate == null ? void 0 : onTimelineUpdate(itemIndex);
      }
      prevEmittedTimeWindow.current = timeWindow2;
    },
    [onTimeWindowUpdate, onTimelineUpdate, data]
  );
  if (showSkeleton) {
    return /* @__PURE__ */ jsx(
      TimeSeriesSkeleton,
      {
        fitHeight,
        height,
        showControls,
        showLegend
      }
    );
  }
  return /* @__PURE__ */ jsx(
    TimeSeriesProvider,
    {
      isPlaying,
      onPlay,
      isPaused,
      onPause,
      onStop,
      timeWindow,
      onTimeWindowUpdate: handleTimeWindowUpdate,
      intl,
      children: /* @__PURE__ */ jsx(
        TimeSeriesWidgetUIContent,
        {
          data,
          categories,
          stepSize,
          stepMultiplier,
          chartType,
          timeAxisSplitNumber,
          tooltip,
          tooltipFormatter,
          formatter,
          height,
          fitHeight,
          showControls,
          animation,
          filterable,
          palette,
          showLegend,
          selectedCategories,
          timelinePosition,
          onSelectedCategoriesChange,
          yAxisType
        }
      )
    }
  );
}
function TimeSeriesWidgetUIContent({
  data = EMPTY_ARR$3,
  categories,
  stepSize,
  stepMultiplier = 1,
  chartType = TIME_SERIES_CHART_TYPES.LINE,
  timeAxisSplitNumber,
  tooltip = true,
  tooltipFormatter = defaultTooltipFormatter$1,
  formatter = IDENTITY_FN$4,
  height,
  fitHeight = false,
  showControls = true,
  animation = true,
  filterable = true,
  palette = Object.values(commonPalette.qualitative.bold),
  selectedCategories = [],
  onSelectedCategoriesChange,
  showLegend,
  timelinePosition,
  yAxisType = "dense"
}) {
  const theme = useTheme();
  const { isPlaying, isPaused, timeWindow, stop, setTimeWindow, intl } = useTimeSeriesContext();
  useEffect(() => {
    if (timelinePosition !== void 0) {
      if (timelinePosition < 0 || timelinePosition >= data.length) return;
      const timeAtSelectedPosition = data[timelinePosition].name;
      setTimeWindow([timeAtSelectedPosition]);
    }
  }, [timelinePosition, data]);
  useEffect(() => {
    if (!data.length) return;
    const start = data[0].name;
    const end = data[data.length - 1].name;
    if (timeWindow[0] < start || timeWindow[1] > end || timeWindow[1] < start || timeWindow[1] > end) {
      setTimeWindow([]);
    }
  }, [data]);
  const series = useMemo(
    () => createSeries({
      data,
      categories,
      palette,
      fallbackColor: theme.palette.secondary.main
    }),
    [categories, data, palette, theme.palette.secondary.main]
  );
  const currentDate = useMemo(() => {
    if (!data.length) {
      return "";
    }
    if (timeWindow.length === 2) {
      const [start, end] = timeWindow.map((time) => new Date(time));
      return formatTimeRange({ start, end, stepSize });
    }
    if (!isPlaying && !isPaused) {
      const start = new Date(data[0].name);
      const end = new Date(data[data.length - 1].name);
      return formatTimeRange({ start, end, stepSize });
    }
    if (timeWindow.length === 1) {
      const date = new Date(timeWindow[0]);
      return formatBucketRange({ date, stepSize, stepMultiplier });
    }
  }, [data, timeWindow, isPlaying, isPaused, stepSize, stepMultiplier]);
  const showClearButton = useMemo(() => {
    return isPlaying || isPaused || timeWindow.length > 0 || (selectedCategories == null ? void 0 : selectedCategories.length) > 0;
  }, [isPaused, isPlaying, selectedCategories == null ? void 0 : selectedCategories.length, timeWindow.length]);
  const handleClear = () => {
    stop();
    setTimeWindow([]);
    onSelectedCategoriesChange == null ? void 0 : onSelectedCategoriesChange([]);
  };
  const handleClearPress = (e) => {
    if (e.key === "Enter") {
      handleClear();
    }
  };
  const handleCategoryClick = useCallback(
    (category) => {
      if (onSelectedCategoriesChange) {
        const newSelectedCategories = [...selectedCategories];
        const selectedCategoryIdx = newSelectedCategories.indexOf(category);
        if (selectedCategoryIdx === -1) {
          newSelectedCategories.push(category);
        } else {
          newSelectedCategories.splice(selectedCategoryIdx, 1);
        }
        onSelectedCategoriesChange(newSelectedCategories);
      }
    },
    [onSelectedCategoriesChange, selectedCategories]
  );
  const isLegendVisible = Boolean(showLegend ?? series.length > 1);
  const header = /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(Typography, { color: "textSecondary", variant: "caption", children: currentDate || "-" }) }),
    filterable && showClearButton && /* @__PURE__ */ jsx(
      Link,
      {
        variant: "caption",
        style: { cursor: "pointer" },
        onClick: handleClear,
        onKeyDown: handleClearPress,
        tabIndex: 0,
        underline: "hover",
        children: intl.formatMessage({ id: "c4r.widgets.timeSeries.clear" })
      }
    )
  ] });
  const controls = filterable && showControls && /* @__PURE__ */ jsx(TimeSeriesControls, { data, stepSize });
  const chart = /* @__PURE__ */ jsx(
    TimeSeriesChart,
    {
      chartType,
      timeAxisSplitNumber,
      data,
      series,
      categories,
      tooltip,
      formatter,
      tooltipFormatter: (params) => tooltipFormatter(
        params,
        stepSize,
        formatter,
        stepMultiplier,
        isLegendVisible
      ),
      height,
      fitHeight,
      animation,
      filterable,
      selectedCategories,
      onCategoryClick: onSelectedCategoriesChange && handleCategoryClick,
      yAxisType
    }
  );
  const legend = isLegendVisible && /* @__PURE__ */ jsx(
    ChartLegend,
    {
      series,
      selectedCategories,
      onCategoryClick: onSelectedCategoriesChange && handleCategoryClick
    }
  );
  return /* @__PURE__ */ jsx(
    TimeSeriesLayout,
    {
      fitHeight,
      header,
      controls,
      chart,
      legend
    }
  );
}
const Root = styled(Box, {
  shouldForwardProp: (prop) => prop !== "margin"
})(({ theme, margin }) => {
  return {
    margin: 0,
    position: "relative",
    maxWidth: "100%",
    padding: margin ?? theme.spacing(2, 2.5)
  };
});
const LoadingBar = styled(LinearProgress)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: theme.spacing(0.25)
}));
const Header = styled(Grid, {
  shouldForwardProp: (prop) => prop !== "expanded"
})(({ theme, expanded = true }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  justifyContent: "space-between",
  width: "100%",
  ...expanded ? { minHeight: theme.spacing(3) } : { height: theme.spacing(3) },
  padding: 0
}));
const HeaderButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "expandable"
})(({ theme, expandable = true }) => ({
  flex: 1,
  padding: 0,
  alignItems: "flex-start",
  justifyContent: "flex-start",
  height: "auto",
  minHeight: "auto",
  marginBottom: theme.spacing(1),
  cursor: expandable ? "pointer" : "default",
  "& .MuiButton-startIcon": {
    marginTop: "3px",
    marginRight: theme.spacing(1)
  },
  "&:hover": {
    background: "none"
  }
}));
const ParentIcon = ({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: theme.spacing(3),
  height: theme.spacing(3),
  color: theme.palette.text.secondary
});
const HideButton = styled(ExpandLess)(({ theme }) => ParentIcon({ theme }));
const ShowButton = styled(ExpandMore)(({ theme }) => ParentIcon({ theme }));
const Text = styled(Typography$1, {
  shouldForwardProp: (prop) => prop !== "expanded"
})(({ expanded = true }) => ({
  wordBreak: "break-word",
  overflow: "hidden",
  ...expanded && {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical"
  },
  ...!expanded && {
    whiteSpace: "nowrap",
    textOverflow: "ellipsis"
  }
}));
const HeaderItems = styled(Grid)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginRight: theme.spacing(-0.5),
  marginLeft: theme.spacing(1)
}));
const IconActionButton = styled(IconButton$1)(({ theme }) => ({
  color: theme.palette.text.secondary
}));
const PaperMenu = styled(Menu)(({ theme }) => ({
  ".MuiPaper-root": {
    marginTop: theme.spacing(5),
    maxHeight: theme.spacing(21),
    minWidth: theme.spacing(16)
  }
}));
const EMPTY_ARR$2 = [];
const defaultOptionsIcon = /* @__PURE__ */ jsx(MoreVert, {});
function WrapperWidgetUI({
  title,
  actions = EMPTY_ARR$2,
  options = EMPTY_ARR$2,
  optionsIcon = defaultOptionsIcon,
  children,
  contentProps,
  disabled = false,
  expandable = true,
  expanded: _expanded = true,
  onExpandedChange,
  footer,
  headerItems,
  isLoading = false,
  margin
}) {
  const wrapper = createRef();
  const [expandedInt, setExpandedInt] = useState(true);
  const externalExpanded = _expanded !== void 0 && onExpandedChange !== void 0;
  const expanded = expandable !== false ? externalExpanded ? _expanded : expandedInt : true;
  const setExpanded = externalExpanded ? onExpandedChange : setExpandedInt;
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleExpandClick = () => {
    if (expandable) {
      setExpanded(!expanded);
    }
  };
  const handleClick = (ev) => setAnchorEl(ev.currentTarget);
  const handleClose = () => setAnchorEl(null);
  function handleOptionAction(action) {
    if (action) {
      action();
    }
    handleClose();
  }
  const iconButtonTooltip = (action) => {
    return /* @__PURE__ */ jsx(
      IconActionButton,
      {
        "aria-label": action.label,
        onClick: action.action,
        size: "small",
        children: action.icon
      },
      action.id
    );
  };
  if (disabled) {
    return children ? /* @__PURE__ */ jsx(Fragment, { children }) : null;
  }
  return /* @__PURE__ */ jsxs(Root, { margin, component: "section", "aria-label": title, children: [
    isLoading ? /* @__PURE__ */ jsx(LoadingBar, {}) : null,
    /* @__PURE__ */ jsxs(Header, { container: true, expanded: _expanded, children: [
      /* @__PURE__ */ jsx(
        HeaderButton,
        {
          expandable,
          startIcon: expandable && /* @__PURE__ */ jsx(Icon, { children: expanded ? /* @__PURE__ */ jsx(HideButton, {}) : /* @__PURE__ */ jsx(ShowButton, {}) }),
          onClick: handleExpandClick,
          tabIndex: expandable ? 0 : -1,
          children: /* @__PURE__ */ jsx(Tooltip, { title, children: /* @__PURE__ */ jsx(Text, { expanded: _expanded, align: "left", variant: "subtitle1", children: title }) })
        }
      ),
      /* @__PURE__ */ jsxs(HeaderItems, { item: true, children: [
        headerItems,
        actions.length > 0 && actions.map((action) => {
          return action.tooltip ? /* @__PURE__ */ jsx(
            Tooltip,
            {
              title: action.tooltip.text,
              placement: action.tooltip.placement ?? "top",
              children: iconButtonTooltip(action)
            },
            action.id
          ) : iconButtonTooltip(action);
        }),
        options.length > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
            IconActionButton,
            {
              "aria-label": "Options",
              "aria-controls": "options-menu",
              "aria-haspopup": "true",
              onClick: handleClick,
              size: "small",
              children: optionsIcon
            }
          ),
          /* @__PURE__ */ jsx(
            PaperMenu,
            {
              id: "options-menu",
              elevation: 8,
              anchorOrigin: {
                vertical: "top",
                horizontal: "right"
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "right"
              },
              anchorEl,
              keepMounted: true,
              open,
              onClose: handleClose,
              children: options.map((option) => /* @__PURE__ */ jsx(
                MenuItem,
                {
                  selected: option.selected,
                  onClick: () => handleOptionAction(option.action),
                  children: option.name
                },
                option.id
              ))
            }
          )
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsx(Collapse, { ref: wrapper, in: expanded, timeout: "auto", unmountOnExit: true, children: /* @__PURE__ */ jsxs(Box, { ...contentProps, children: [
      /* @__PURE__ */ jsx(Box, { pt: 1, children }),
      footer ?? /* @__PURE__ */ jsx(Box, { children: footer })
    ] }) })
  ] });
}
const Wrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(2, 0),
  ...theme.typography.body2
}));
const CategoriesList = styled(Box)(({ theme }) => ({
  overflow: "auto",
  maxHeight: theme.spacing(40),
  paddingRight: theme.spacing(1),
  margin: theme.spacing(0.5, 0)
}));
const BulletListWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: theme.spacing(1.5),
  padding: theme.spacing(2, 0)
}));
const BulletWrapper = styled(Box)(({ theme, alignItems = "center" }) => ({
  display: "flex",
  alignItems,
  gap: theme.spacing(0.75)
}));
const Bullet = styled("div", {
  shouldForwardProp: (prop) => prop !== "color"
})(({ theme, color: backgroundColor }) => ({
  flexShrink: 0,
  width: theme.spacing(1),
  height: theme.spacing(1),
  borderRadius: theme.spacing(1),
  backgroundColor
}));
const ProgressbarWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2)
}));
const Progressbar = styled("div")(({ theme }) => ({
  height: theme.spacing(0.5),
  width: "100%",
  borderRadius: theme.spacing(0.5),
  backgroundColor: theme.palette.action.disabledBackground,
  "& div": {
    width: 0,
    height: "100%",
    borderRadius: theme.spacing(0.5),
    transition: `background-color ${theme.transitions.easing.sharp} ${theme.transitions.duration.shortest}ms,
                 width ${theme.transitions.easing.sharp} ${theme.transitions.duration.complex}ms`
  }
}));
const Toolbar = styled(Box, {
  shouldForwardProp: (prop) => prop !== "center"
})(({ theme, center = false }) => ({
  marginBottom: theme.spacing(2),
  paddingRight: theme.spacing(1),
  ...center && {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  "& .MuiTypography-caption": {
    color: theme.palette.text.secondary
  },
  "& .MuiButton-label": {
    ...theme.typography.caption
  },
  "& a": {
    cursor: "pointer"
  }
}));
const StyledTooltip = styled(Tooltip)(({ theme }) => ({
  ".MuiTooltip-tooltip": {
    color: theme.palette.common.white,
    maxWidth: 260,
    marginBottom: 0,
    overflow: "hidden"
  }
}));
const SearchInput = styled(TextField)(({ theme }) => ({
  marginTop: theme.spacing(-0.5)
}));
styled(Box)(() => ({
  display: "flex",
  flexWrap: "nowrap",
  justifyContent: "space-between"
}));
const CategoryItemWrapper = styled("div", {
  shouldForwardProp: (prop) => prop !== "filterable"
})(({ filterable }) => ({
  "& .progressbar div": {
    backgroundColor: "var(--color)"
  },
  ...filterable && {
    cursor: "pointer",
    "& .MuiBox-root:hover .progressbar div": {
      backgroundColor: "var(--hover-color)"
    }
  }
}));
const CategoryItemWrapperRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexWrap: "nowrap",
  overflow: "hidden",
  gap: theme.spacing(1)
}));
const CategoryItemWrapperInner = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.5, 0),
  flexGrow: "1",
  maxWidth: "100%",
  minWidth: 0
}));
const SignWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "backgroundColor"
})(({ theme, backgroundColor }) => ({
  marginLeft: theme.spacing(1),
  padding: theme.spacing(0, 1),
  backgroundColor,
  color: "white",
  borderRadius: theme.spacing(2)
}));
function transposeCategoryData(data, colors, labels, selectedCategories, order) {
  const reference = data[0] ?? [];
  const transposed = reference.map((item, itemIndex) => {
    const name = String(item.name);
    const isDisabled = selectedCategories.length > 0 && !selectedCategories.includes(name);
    const label = labels[itemIndex] ?? name;
    const indexData = data.map((group, groupIndex) => {
      var _a;
      return {
        color: isDisabled ? lighten(colors[groupIndex], 0.8) : colors[groupIndex],
        value: ((_a = group[itemIndex]) == null ? void 0 : _a.value) ?? 0
      };
    });
    return {
      label,
      key: name,
      data: indexData
    };
  });
  if (order === OrderTypes.Ranking) {
    transposed.sort((a, b) => {
      const aMax = Math.max(...a.data.map((d) => d.value));
      const bMax = Math.max(...b.data.map((d) => d.value));
      return bMax - aMax;
    });
  }
  return transposed;
}
const DEFAULT_DURATION = 500;
function useAnimatedNumber(value, options = {}) {
  const { disabled, duration, animateOnMount, initialValue = 0 } = options;
  const requestAnimationFrameRef = useRef(null);
  const [animatedValue, setAnimatedValue] = useState(
    () => animateOnMount ? initialValue : value
  );
  useEffect(() => {
    if (!disabled) {
      animateValue({
        start: animatedValue,
        end: value,
        duration: duration ?? DEFAULT_DURATION,
        drawFrame: (val) => setAnimatedValue(val),
        requestRef: requestAnimationFrameRef
      });
    } else {
      setAnimatedValue(value);
    }
    return () => {
      if (requestAnimationFrameRef.current) {
        cancelAnimationFrame(requestAnimationFrameRef.current);
      }
    };
  }, [value, disabled, duration]);
  return animatedValue;
}
const EMPTY_OBJ$2 = {};
function AnimatedNumber({
  enabled = true,
  value = 0,
  options = EMPTY_OBJ$2,
  formatter
}) {
  const defaultOptions = {
    animateOnMount: true,
    disabled: enabled === false || value === null || value === void 0
  };
  const animated = useAnimatedNumber(value || 0, {
    ...defaultOptions,
    ...options
  });
  return /* @__PURE__ */ jsx("span", { children: formatter ? formatter(animated) : animated });
}
const IDENTITY_FN$3 = (v) => v;
const EMPTY_FN$6 = () => void 0;
const EMPTY_ARRAY$3 = [];
function ComparativeCategoryTooltip({
  item,
  index = 0,
  names = EMPTY_ARRAY$3,
  formatter = IDENTITY_FN$3
}) {
  const theme = useTheme();
  const reference = item.data[0];
  const data = item.data[index];
  const name = names[index];
  const compareValue = (data.value - reference.value) / (reference.value || 1) * 100;
  const signText = Math.sign(compareValue) === -1 ? "-" : "+";
  const valueColor = Math.sign(compareValue) === -1 ? theme.palette.error.main : theme.palette.success.main;
  const numberColor = item.key === OTHERS_CATEGORY_NAME ? theme.palette.text.disabled : valueColor;
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsx(Typography$1, { color: "inherit", variant: "caption", noWrap: true, children: item.label }),
    /* @__PURE__ */ jsx(Box, { pt: 1, pb: 0.5, children: /* @__PURE__ */ jsxs(BulletWrapper, { alignItems: "baseline", children: [
      /* @__PURE__ */ jsx(
        Bullet,
        {
          color: item.key === OTHERS_CATEGORY_NAME ? theme.palette.background.default : data.color
        }
      ),
      /* @__PURE__ */ jsx(Typography$1, { color: "inherit", variant: "caption", children: name }),
      /* @__PURE__ */ jsx(Box, { style: { flexGrow: 1 } }),
      /* @__PURE__ */ jsx(SignWrapper, { backgroundColor: numberColor, children: /* @__PURE__ */ jsxs(Typography$1, { color: "inherit", variant: "caption", children: [
        signText,
        formatter(Math.abs(compareValue))
      ] }) })
    ] }) })
  ] });
}
function CategoryItem({
  item,
  animation = true,
  animationOptions,
  maxValue,
  showCheckbox,
  checkboxChecked,
  formatter = IDENTITY_FN$3,
  tooltipFormatter = IDENTITY_FN$3,
  onClick = EMPTY_FN$6,
  names,
  tooltip
}) {
  function getProgressbarLength(value) {
    return `${Math.min(100, (value || 0) / maxValue * 100)}%`;
  }
  const tooltipContent = (index) => /* @__PURE__ */ jsx(
    ComparativeCategoryTooltip,
    {
      item,
      names,
      formatter: tooltipFormatter,
      index
    }
  );
  return /* @__PURE__ */ jsxs(CategoryItemWrapperRoot, { onClick: () => onClick(item.key), children: [
    showCheckbox ? /* @__PURE__ */ jsx(Checkbox, { checked: checkboxChecked }) : null,
    /* @__PURE__ */ jsxs(CategoryItemWrapperInner, { children: [
      /* @__PURE__ */ jsx(Typography$1, { variant: "body2", noWrap: true, children: item.label }),
      item.data.map((d, i) => /* @__PURE__ */ jsx(
        StyledTooltip,
        {
          title: tooltipContent(i),
          placement: "top-start",
          arrow: false,
          disableHoverListener: !tooltip,
          children: /* @__PURE__ */ jsxs(ProgressbarWrapper, { children: [
            /* @__PURE__ */ jsx(Progressbar, { className: "progressbar", children: /* @__PURE__ */ jsx(
              "div",
              {
                style: {
                  "--hover-color": darken(d.color, 0.2),
                  "--color": d.color,
                  width: getProgressbarLength(d.value)
                }
              }
            ) }),
            /* @__PURE__ */ jsx(
              Typography$1,
              {
                variant: i === 0 ? "body2" : "caption",
                color: i === 0 ? "textPrimary" : "textSecondary",
                children: /* @__PURE__ */ jsx(
                  AnimatedNumber,
                  {
                    value: d.value,
                    enabled: animation,
                    options: animationOptions,
                    formatter
                  }
                )
              }
            )
          ] })
        },
        `${item.key}_${i}`
      ))
    ] })
  ] });
}
const IDENTITY_FN$2 = (v) => v;
const EMPTY_FN$5 = () => void 0;
const EMPTY_ARRAY$2 = [];
function ComparativeCategoryWidgetUI({
  names = EMPTY_ARRAY$2,
  data = EMPTY_ARRAY$2,
  labels = EMPTY_ARRAY$2,
  colors = EMPTY_ARRAY$2,
  maxItems = 5,
  order = OrderTypes.Fixed,
  animation = true,
  animationOptions,
  searchable = true,
  filterable = true,
  selectedCategories = EMPTY_ARRAY$2,
  onSelectedCategoriesChange = EMPTY_FN$5,
  formatter = IDENTITY_FN$2,
  tooltipFormatter = IDENTITY_FN$2,
  tooltip = true,
  isLoading = false
}) {
  const theme = useTheme();
  const [searchActive, setSearchActive] = useState(false);
  const [blockingActive, setBlockingActive] = useState(false);
  const [tempSelection, setTempSelection] = useState(selectedCategories);
  const [searchValue, setSearchValue] = useState("");
  const intl = useIntl();
  const intlConfig = useImperativeIntl(intl);
  const processedData = useMemo(() => {
    const _colors = colors.length ? colors : [
      theme.palette.secondary.main,
      theme.palette.primary.main,
      theme.palette.info.main
    ];
    return transposeCategoryData(
      data,
      _colors,
      labels,
      selectedCategories,
      order
    );
  }, [data, colors, labels, theme, selectedCategories, order]);
  const maxValue = useMemo(() => {
    return Math.max(
      ...data.map((group) => group.map((g) => g.value ?? 0)).flat()
    );
  }, [data]);
  const compressedData = useMemo(() => {
    if (maxItems >= processedData.length) {
      return processedData;
    }
    const visibleItems = processedData.slice(0, maxItems);
    const otherItems = processedData.slice(maxItems);
    const otherSum = [];
    for (const item of otherItems) {
      item.data.forEach((d, i) => {
        otherSum[i] = otherSum[i] ?? 0;
        otherSum[i] += d.value;
      });
    }
    const combinedOther = {
      key: OTHERS_CATEGORY_NAME,
      label: searchable ? "Others" : `Others (${processedData.length - maxItems})`,
      data: otherSum.map((sum) => ({
        value: sum,
        color: theme.palette.divider
      }))
    };
    return [...visibleItems, combinedOther];
  }, [processedData, searchable, maxItems, theme.palette.divider]);
  const blockedData = useMemo(() => {
    return processedData.filter((c) => selectedCategories.includes(c.key));
  }, [processedData, selectedCategories]);
  const filteredData = useMemo(() => {
    if (!searchValue) {
      return processedData;
    }
    return processedData.filter((el) => {
      const key = (el.key || "").toLowerCase();
      const label = (el.label || "").toLowerCase();
      const keyMatches = key == null ? void 0 : key.includes(searchValue.toLowerCase());
      const labelMatches = label == null ? void 0 : label.includes(searchValue.toLowerCase());
      return keyMatches || labelMatches;
    });
  }, [processedData, searchValue]);
  const otherCount = processedData.length - compressedData.length + 1;
  const showSearchToggle = searchable && !searchActive && maxItems < processedData.length;
  if (processedData.length === 0 || isLoading) {
    return /* @__PURE__ */ jsx(CategorySkeleton, {});
  }
  const list = searchActive ? filteredData : blockingActive ? blockedData : compressedData;
  function applyTempSelection() {
    setBlockingActive(true);
    onSelectedCategoriesChange([...tempSelection]);
    disableSearchMode();
  }
  function disableBlocking() {
    setBlockingActive(false);
  }
  function clearSelection() {
    onSelectedCategoriesChange([]);
  }
  function enableBlocking() {
    setBlockingActive(true);
  }
  function enableSearchMode() {
    setSearchActive(true);
    setTempSelection([...selectedCategories]);
  }
  function disableSearchMode() {
    setSearchActive(false);
    setTempSelection([]);
  }
  function selectCategory(category) {
    const isSelected = selectedCategories.includes(category);
    const set = new Set(selectedCategories);
    if (isSelected) {
      set.delete(category);
    } else {
      set.add(category);
    }
    let newCategories = Array.from(set);
    if (newCategories.length === processedData.length) {
      newCategories = [];
    }
    onSelectedCategoriesChange(newCategories);
  }
  function selectTempCategory(category) {
    const isSelected = tempSelection.includes(category);
    const set = new Set(tempSelection);
    if (isSelected) {
      set.delete(category);
    } else {
      set.add(category);
    }
    let newCategories = Array.from(set);
    if (newCategories.length === processedData.length) {
      newCategories = [];
    }
    setTempSelection(newCategories);
  }
  const clickHandler = filterable ? searchActive ? selectTempCategory : selectCategory : void 0;
  return /* @__PURE__ */ jsxs(Wrapper, { children: [
    filterable ? /* @__PURE__ */ jsxs(Toolbar, { center: true, children: [
      /* @__PURE__ */ jsx(Typography, { variant: "caption", children: selectedCategories.length > 0 ? intlConfig.formatMessage(
        { id: "c4r.widgets.category.selectedItems" },
        { items: selectedCategories.length }
      ) : intlConfig.formatMessage({ id: "c4r.widgets.category.all" }) }),
      /* @__PURE__ */ jsx(Typography, { variant: "caption", component: "div", children: searchActive ? /* @__PURE__ */ jsx(Link$1, { onClick: applyTempSelection, children: intlConfig.formatMessage({ id: "c4r.widgets.category.apply" }) }) : blockingActive ? /* @__PURE__ */ jsx(Link$1, { onClick: disableBlocking, children: intlConfig.formatMessage({
        id: "c4r.widgets.category.unlock"
      }) }) : selectedCategories.length ? /* @__PURE__ */ jsxs(
        Box,
        {
          style: {
            display: "flex",
            justifyContent: "flex-end",
            gap: theme.spacing(1)
          },
          children: [
            /* @__PURE__ */ jsx(Link$1, { onClick: enableBlocking, children: intlConfig.formatMessage({
              id: "c4r.widgets.category.lock"
            }) }),
            /* @__PURE__ */ jsx(Divider, { orientation: "vertical", flexItem: true }),
            /* @__PURE__ */ jsx(Link$1, { onClick: clearSelection, children: intlConfig.formatMessage({
              id: "c4r.widgets.category.clear"
            }) })
          ]
        }
      ) : null })
    ] }) : null,
    searchActive ? /* @__PURE__ */ jsx(Toolbar, { children: /* @__PURE__ */ jsx(
      SearchInput,
      {
        size: "small",
        placeholder: intlConfig.formatMessage({
          id: "c4r.widgets.category.search"
        }),
        onChange: (ev) => setSearchValue(ev.currentTarget.value),
        onFocus: (ev) => ev.currentTarget.scrollIntoView(),
        InputProps: {
          startAdornment: /* @__PURE__ */ jsx(InputAdornment, { position: "start", children: /* @__PURE__ */ jsx(Search, {}) })
        }
      }
    ) }) : null,
    /* @__PURE__ */ jsxs(CategoriesList, { children: [
      list.length === 0 ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(Typography, { variant: "body2", children: intlConfig.formatMessage({
          id: "c4r.widgets.category.noResults"
        }) }),
        /* @__PURE__ */ jsx(Typography, { variant: "caption", children: intlConfig.formatMessage(
          { id: "c4r.widgets.category.noResultsMessage" },
          { searchValue }
        ) })
      ] }) : null,
      list.map((d) => /* @__PURE__ */ jsx(CategoryItemWrapper, { children: /* @__PURE__ */ jsx(
        CategoryItem,
        {
          item: d,
          animation,
          animationOptions,
          maxValue,
          showCheckbox: filterable && searchActive,
          checkboxChecked: tempSelection.includes(d.key),
          formatter,
          tooltipFormatter,
          tooltip,
          onClick: clickHandler,
          names
        }
      ) }, d.key))
    ] }),
    showSearchToggle ? /* @__PURE__ */ jsx(
      Button,
      {
        size: "small",
        color: "primary",
        startIcon: /* @__PURE__ */ jsx(Search, {}),
        onClick: enableSearchMode,
        children: intlConfig.formatMessage(
          { id: "c4r.widgets.category.searchInfo" },
          { elements: otherCount }
        )
      }
    ) : null,
    searchActive ? /* @__PURE__ */ jsx(Button, { size: "small", color: "primary", onClick: disableSearchMode, children: intlConfig.formatMessage({ id: "c4r.widgets.category.cancel" }) }) : null,
    /* @__PURE__ */ jsx(BulletListWrapper, { children: names.map((name, i) => /* @__PURE__ */ jsxs(BulletWrapper, { children: [
      /* @__PURE__ */ jsx(Bullet, { color: (colors == null ? void 0 : colors[i]) ?? theme.palette.background.default }),
      /* @__PURE__ */ jsx(Typography, { variant: "overline", children: name })
    ] }, names[i])) })
  ] });
}
const Note = styled(Typography)(({ theme }) => ({
  display: "inline-block",
  marginTop: theme.spacing(0.5)
}));
function FormulaLabel({
  row
}) {
  const theme = useTheme();
  return row.label ? /* @__PURE__ */ jsx(Box, { color: row.color ?? theme.palette.text.secondary, children: /* @__PURE__ */ jsx(Note, { color: "inherit", variant: "caption", children: row.label }) }) : null;
}
const MainLine = styled("div")(({ theme }) => ({
  margin: 0,
  ...theme.typography.h5,
  fontWeight: Number(theme.typography.fontWeightMedium),
  color: theme.palette.text.primary,
  display: "flex"
}));
const Preffix = styled(Typography)(({ theme }) => ({
  marginRight: theme.spacing(0.5)
}));
const Unit = styled(Typography)(({ theme }) => ({
  marginLeft: theme.spacing(0.5)
}));
function FormulaValue({
  row,
  animated,
  animationOptions,
  formatter
}) {
  const theme = useTheme();
  const { prefix, value, suffix, shouldBeHighlighted } = row;
  return /* @__PURE__ */ jsxs(MainLine, { children: [
    prefix ? /* @__PURE__ */ jsx(Box, { color: theme.palette.text.secondary, children: /* @__PURE__ */ jsx(Preffix, { color: "inherit", component: "span", variant: "subtitle2", children: prefix }) }) : null,
    /* @__PURE__ */ jsx(Box, { fontWeight: shouldBeHighlighted ? "bold" : "", children: /* @__PURE__ */ jsx(
      AnimatedNumber,
      {
        value,
        enabled: animated,
        options: animationOptions,
        formatter
      }
    ) }),
    suffix ? /* @__PURE__ */ jsx(Box, { color: theme.palette.text.secondary, children: /* @__PURE__ */ jsx(Unit, { color: "inherit", component: "span", variant: "subtitle2", children: suffix }) }) : null
  ] });
}
const IDENTITY_FN$1 = (v) => v;
const EMPTY_ARRAY$1 = [];
const FormulaGroup = styled("div")(({ theme }) => ({
  "&:nth-of-type(n+2)": {
    marginTop: theme.spacing(2)
  }
}));
function ComparativeFormulaWidgetUI({
  data = EMPTY_ARRAY$1,
  colors = EMPTY_ARRAY$1,
  animated = true,
  animationOptions,
  formatter = IDENTITY_FN$1,
  isLoading = false
}) {
  const formulaValues = prepareFormulaValues(data, colors);
  if (isLoading) {
    return /* @__PURE__ */ jsx(FormulaSkeleton, {});
  }
  return /* @__PURE__ */ jsx("div", { children: formulaValues.map((row, index) => /* @__PURE__ */ jsxs(FormulaGroup, { children: [
    /* @__PURE__ */ jsx(
      FormulaValue,
      {
        row,
        animated,
        animationOptions,
        formatter
      }
    ),
    /* @__PURE__ */ jsx(FormulaLabel, { row })
  ] }, index)) });
}
function prepareFormulaValues(data, colors) {
  const values = data.map((d, i) => ({ ...d, color: colors[i], shouldBeHighlighted: false })).filter((d) => d.value !== void 0);
  const isReference = values && values.length > 1;
  if (isReference) {
    values[0].shouldBeHighlighted = true;
  }
  return values;
}
function processData(data, colors = [], labels = [], theme, selectedCategories = []) {
  return data.map((item, index) => {
    const isDisabled = selectedCategories.length > 0 && !selectedCategories.includes(item.name);
    const palette = (colors == null ? void 0 : colors.length) ? colors : theme.palette.qualitative.bold;
    const color = palette[index];
    return {
      ...item,
      key: item.name,
      color: isDisabled ? lighten(color, 0.8) : color,
      name: labels[index] ?? item.name
    };
  });
}
function defaultTooltipFormatter(params, formatter, isMultiple) {
  const titleStyle = `
    font-size: 12px;
    line-height: 1.33;
    font-weight: 600;
    margin: 4px 0 4px 0;
  `;
  const valueStyle = `
    font-size: 12px;
    line-height: 1.33;
    font-weight: normal;
    margin: 0 0 4px 0;
  `;
  const singleParams = Array.isArray(params) ? params[0] : params;
  if (!singleParams) {
    return null;
  }
  const data = singleParams.data;
  const value = formatter(data.value);
  const label = isMultiple ? singleParams.seriesName : value;
  const percent = formatter(singleParams.percent);
  return `
    <p style="${titleStyle}">${data.name}</p>
    <p style="${valueStyle}">${String(singleParams.marker)} ${label} (${percent} %)</p>
  `.trim();
}
const IDENTITY_FN = (v) => v;
const EMPTY_FN$4 = () => void 0;
const EMPTY_ARRAY = [];
const DEFAULT_HEIGHT = "260px";
function ComparativePieWidgetUI({
  names = EMPTY_ARRAY,
  data = EMPTY_ARRAY,
  labels = EMPTY_ARRAY,
  colors = EMPTY_ARRAY,
  height = DEFAULT_HEIGHT,
  animation = true,
  formatter = IDENTITY_FN,
  tooltipFormatter = defaultTooltipFormatter,
  selectedCategories = [],
  onCategorySelected = EMPTY_FN$4,
  isLoading = false
}) {
  const theme = useTheme();
  const chartRef = useRef();
  const processedData = useMemo(() => {
    return data.map(
      (d, i) => processData(d, colors[i], labels[i], theme, selectedCategories)
    );
  }, [data, colors, labels, theme, selectedCategories]);
  const options = useMemo(() => {
    const isMultiple = processedData.length > 1;
    let _formatter = void 0;
    if (tooltipFormatter) {
      _formatter = (params) => tooltipFormatter(params, formatter, isMultiple);
    }
    const tooltip = {
      trigger: "item",
      show: true,
      backgroundColor: theme.palette.black[90],
      textStyle: { color: theme.palette.common.white },
      confine: true,
      borderWidth: 0,
      formatter: _formatter
    };
    const legendData = isMultiple ? processedData.map((d, i) => names[i]) : (processedData[0] ?? []).map((d) => d.name);
    const legend = {
      data: legendData,
      selectedMode: false,
      type: "scroll",
      left: SPACING,
      bottom: SPACING * -0.5,
      itemGap: SPACING * 3,
      icon: "circle",
      itemWidth: SPACING,
      itemHeight: SPACING,
      textStyle: {
        ...theme.typography.overline,
        color: theme.palette.text.primary,
        fontWeight: 400,
        lineHeight: 1,
        verticalAlign: "bottom",
        padding: [0, 0, 0, SPACING * 0.5]
      },
      inactiveColor: theme.palette.text.disabled,
      pageIcons: {
        horizontal: [
          "path://M15.41 7.41 14 6 8 12 14 18 15.41 16.59 10.83 12z",
          "path://M9 16.59 13.3265857 12 9 7.41 10.3319838 6 16 12 10.3319838 18z"
        ]
      },
      pageIconSize: SPACING * 1.5,
      pageIconColor: theme.palette.text.secondary,
      pageIconInactiveColor: theme.palette.text.disabled,
      pageTextStyle: {
        fontFamily: theme.typography.overline.fontFamily,
        fontSize: SPACING * 1.5,
        lineHeight: SPACING * 1.75,
        fontWeight: 400,
        color: theme.palette.text.primary
      }
    };
    const labelOptions = {
      formatter: ({ name, percent }) => `{per|${formatter(percent)}%}
{b|${name}}`,
      position: "center",
      rich: {
        b: {
          fontFamily: theme.typography.overline.fontFamily,
          fontSize: SPACING * 1.75,
          lineHeight: SPACING * 1.75,
          fontWeight: 400,
          color: theme.palette.text.primary
        },
        per: {
          ...theme.typography.overline,
          fontSize: SPACING * 3,
          lineHeight: SPACING * 4.5,
          fontWeight: 600,
          color: theme.palette.text.primary
        }
      }
    };
    const series = processedData.map((data2, i) => {
      var _a;
      return {
        type: "pie",
        name: names[i],
        itemStyle: {
          color: (_a = data2[0]) == null ? void 0 : _a.color
        },
        animation,
        data: data2.map((d) => ({ ...d, itemStyle: { color: d.color } })),
        radius: i === 0 ? ["75%", "90%"] : ["56%", "72%"],
        selectedOffset: 0,
        hoverOffset: 5,
        bottom: SPACING * 2.5,
        avoidLabelOverlap: true,
        label: { show: false, ...labelOptions },
        emphasis: {
          label: {
            show: true,
            formatter: labelOptions.formatter,
            rich: labelOptions.rich
          }
        }
      };
    });
    const grid = {
      left: 0,
      top: 0,
      right: 0,
      bottom: 0
    };
    return { grid, tooltip, legend, series };
  }, [theme, names, animation, processedData, formatter, tooltipFormatter]);
  const onEvents = {
    mouseover: (selected, chart) => {
      if (processedData.length === 1) {
        if (selected.seriesIndex !== 0 || selected.dataIndex !== 0) {
          chart.dispatchAction({
            type: "downplay",
            seriesIndex: 0,
            dataIndex: 0
          });
        }
      }
    },
    mouseout: (_, chart) => {
      if (processedData.length === 1) {
        chart.dispatchAction({
          type: "highlight",
          seriesIndex: 0,
          dataIndex: 0
        });
      }
    },
    click: (ev) => {
      const item = processedData[ev.seriesIndex][ev.dataIndex];
      const category = item.key;
      const isSelected = selectedCategories.includes(category);
      const set = new Set(selectedCategories);
      if (isSelected) {
        set.delete(category);
      } else {
        set.add(category);
      }
      const dataLength = processedData[0].length;
      let newCategories = Array.from(set);
      if (newCategories.length === dataLength) {
        newCategories = [];
      }
      onCategorySelected(newCategories);
    }
  };
  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.dispatchAction({
        type: processedData.length === 1 ? "highlight" : "downplay",
        seriesIndex: 0,
        dataIndex: 0
      });
    }
  }, [processedData]);
  function onChartReady(chart) {
    chartRef.current = chart;
  }
  if (!processedData.length) {
    return null;
  }
  if (isLoading) {
    return /* @__PURE__ */ jsx(PieSkeleton, { height });
  }
  return /* @__PURE__ */ jsx(
    ReactEcharts,
    {
      onChartReady,
      notMerge: true,
      option: options,
      onEvents,
      style: { maxHeight: height, width: "100%" }
    }
  );
}
const EMPTY_FN$3 = () => void 0;
function FeatureSelectionUIToggleButton({
  icon,
  hoverTooltip,
  clickTooltip,
  enabled,
  onEnabledChange = EMPTY_FN$3,
  tooltipPlacement = "bottom"
}) {
  const [tooltipShown, setTooltipShown] = useState(false);
  const [showClickAwayTooltip, setShowClickAwayTooltip] = useState(enabled);
  useEffect(() => {
    setShowClickAwayTooltip(enabled);
  }, [enabled]);
  useEffect(() => {
    if (enabled) {
      const fn = (ev) => {
        if (ev.key === "Escape") {
          if (onEnabledChange) onEnabledChange(false);
        }
      };
      window.addEventListener("keydown", fn);
      return () => {
        window.removeEventListener("keydown", fn);
      };
    }
  }, [enabled, onEnabledChange]);
  const tooltipTitle = showClickAwayTooltip ? clickTooltip : hoverTooltip;
  return /* @__PURE__ */ jsx(ClickAwayListener, { onClickAway: () => setShowClickAwayTooltip(false), children: /* @__PURE__ */ jsx(
    Tooltip,
    {
      title: tooltipTitle,
      placement: tooltipPlacement,
      open: tooltipShown,
      onOpen: () => setTooltipShown(true),
      onClose: () => {
        if (!showClickAwayTooltip) {
          setTooltipShown(false);
        }
      },
      children: /* @__PURE__ */ jsx(
        ToggleButton,
        {
          value: "selectedMode",
          selected: enabled,
          onClick: () => onEnabledChange && onEnabledChange(!enabled),
          children: icon
        }
      )
    }
  ) });
}
const ArrowButton = styled(IconButton$1, {
  shouldForwardProp: (prop) => prop !== "isOpen"
})(({ isOpen, theme }) => ({
  color: theme.palette.text.secondary,
  width: theme.spacing(3),
  transform: `rotate(${isOpen ? "180" : "0"}deg)`,
  backgroundColor: isOpen ? theme.palette.action.hover : void 0
}));
const SelectionMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== "disabled"
})(({ disabled, theme }) => ({
  ...disabled && {
    pointerEvents: "none",
    color: theme.palette.text.disabled
  }
}));
const DisabledMenuItem = styled(MenuItem)(() => ({
  "&.Mui-disabled": {
    opacity: 1
  }
}));
const EMPTY_FN$2 = () => void 0;
function FeatureSelectionUIDropdown({
  selectionModes,
  editModes,
  selectedMode,
  onSelectMode = EMPTY_FN$2,
  enabled,
  tooltipPlacement = "bottom",
  tooltipText = "",
  menuHeaderText = "",
  editDisabled
}) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const openDropdown = (ev) => setAnchorEl(ev.currentTarget);
  const closeDropdown = () => setAnchorEl(null);
  const handleSelectMode = (newSelectedMode) => {
    if (onSelectMode) {
      onSelectMode(newSelectedMode);
    }
    closeDropdown();
  };
  const showDivider = !!selectionModes.length && !!editModes.length;
  const isEditItem = (mode) => editModes.some((editMode) => editMode.id === mode.id);
  function createMenuItemWrapper(mode) {
    return /* @__PURE__ */ jsx(
      SelectionMenuItem,
      {
        selected: enabled && selectedMode === mode.id,
        onClick: () => handleSelectMode(mode.id),
        disabled: editDisabled && isEditItem(mode),
        children: /* @__PURE__ */ jsxs(Box, { display: "flex", justifyContent: "space-between", alignItems: "center", children: [
          mode.icon,
          /* @__PURE__ */ jsx(Box, { ml: 2, children: /* @__PURE__ */ jsx(Typography, { variant: "body2", color: "inherit", children: capitalize(mode.label) }) })
        ] })
      },
      mode.id
    );
  }
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Tooltip, { title: tooltipText, placement: tooltipPlacement, children: /* @__PURE__ */ jsx(
      ArrowButton,
      {
        id: "feature-selection-menu-button",
        "aria-controls": "feature-selection-menu",
        "aria-haspopup": "true",
        "aria-expanded": open ? "true" : void 0,
        onClick: openDropdown,
        isOpen: open,
        children: /* @__PURE__ */ jsx(KeyboardArrowDown, {})
      }
    ) }),
    /* @__PURE__ */ jsxs(
      Menu,
      {
        id: "feature-selection-menu",
        style: { zIndex: theme.zIndex.tooltip + 1 },
        anchorEl,
        open,
        onClose: closeDropdown,
        MenuListProps: { "aria-labelledby": "feature-selection-menu-button" },
        children: [
          menuHeaderText && /* @__PURE__ */ jsx(DisabledMenuItem, { disabled: true, children: /* @__PURE__ */ jsx(Typography, { variant: "caption", color: "textSecondary", children: menuHeaderText }) }),
          !!selectionModes.length && selectionModes.map(createMenuItemWrapper),
          showDivider && /* @__PURE__ */ jsx(Divider, { sx: { margin: ({ spacing }) => spacing(1, 0) } }),
          !!editModes.length && editModes.map(createMenuItemWrapper)
        ]
      }
    )
  ] });
}
const ChipList = styled(List)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginLeft: theme.spacing(1.5),
  padding: theme.spacing(0.5),
  overflowX: "auto",
  maxWidth: "100%",
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": {
    display: "none"
  }
}));
const EMPTY_FN$1 = () => void 0;
function FeatureSelectionUIGeometryChips({
  features,
  onSelectGeometry = EMPTY_FN$1,
  onDeleteGeometry,
  chipTooltip,
  disabledChipTooltip,
  size = "medium",
  tooltipPlacement = "bottom",
  chipLabel
}) {
  const intl = useIntl();
  const intlConfig = useImperativeIntl(intl);
  function translateType(type) {
    if (type === "MultiPoint") {
      return intlConfig.formatMessage({
        id: `c4r.widgets.featureSelection.point`
      });
    }
    if (type === "MultiLineString") {
      return intlConfig.formatMessage({
        id: `c4r.widgets.featureSelection.lineString`
      });
    }
    if (type === "MultiPolygon" || type === "Polygon" || type === "GeometryCollection") {
      return intlConfig.formatMessage({
        id: `c4r.widgets.featureSelection.polygon`
      });
    }
    return type;
  }
  function getFeatureChipLabel(feature, index) {
    var _a;
    const type = translateType(feature.geometry.type);
    return chipLabel || ((_a = feature.properties) == null ? void 0 : _a.name) || `${type} ${index + 1}`;
  }
  const [showDeleteTooltip, setShowDeleteTooltip] = useState(false);
  return /* @__PURE__ */ jsx(Box, { sx: { overflowX: "auto" }, children: /* @__PURE__ */ jsx(ChipList, { sx: { gap: size === "small" ? 0.5 : 1 }, children: features.map((feature, index) => {
    var _a, _b;
    const isDisabled = (_a = feature.properties) == null ? void 0 : _a.disabled;
    const isInvalid = (_b = feature.properties) == null ? void 0 : _b.invalid;
    let tooltipText = chipTooltip;
    if (isDisabled) {
      tooltipText = disabledChipTooltip || chipTooltip;
    }
    if (isInvalid) {
      tooltipText = intlConfig.formatMessage({
        id: `c4r.widgets.featureSelection.invalid`
      });
    }
    if (showDeleteTooltip) {
      tooltipText = intlConfig.formatMessage({
        id: `c4r.widgets.featureSelection.remove`
      });
    }
    let color = "secondary";
    if (isDisabled) {
      color = "default";
    }
    if (isInvalid) {
      color = "error";
    }
    return /* @__PURE__ */ jsx(ListItem, { disablePadding: true, children: /* @__PURE__ */ jsx(
      Tooltip,
      {
        disableHoverListener: isDisabled ? !disabledChipTooltip : !chipTooltip,
        title: tooltipText,
        placement: tooltipPlacement,
        children: /* @__PURE__ */ jsx(
          Chip,
          {
            size,
            label: getFeatureChipLabel(feature, index),
            color,
            onClick: () => onSelectGeometry(feature),
            onDelete: onDeleteGeometry ? () => onDeleteGeometry(feature) : void 0,
            icon: isInvalid ? /* @__PURE__ */ jsx(ErrorOutline, { color: "error" }) : void 0,
            deleteIcon: /* @__PURE__ */ jsx(
              Cancel,
              {
                onMouseEnter: () => setShowDeleteTooltip(true),
                onMouseLeave: () => setShowDeleteTooltip(false)
              }
            )
          }
        )
      }
    ) }, index);
  }) }) });
}
const StylesWrapper = styled(Paper)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  maxHeight: theme.spacing(5),
  padding: theme.spacing(0.5)
}));
const EMPTY_ARR$1 = [];
function FeatureSelectionWidgetUI({
  selectionModes,
  editModes = EMPTY_ARR$1,
  selectedMode,
  onSelectMode,
  enabled = false,
  onEnabledChange,
  geometry,
  onSelectGeometry,
  onDeleteGeometry,
  tooltipPlacement = "bottom",
  size = "medium",
  chipLabel
}) {
  const intl = useIntl();
  const intlConfig = useImperativeIntl(intl);
  const selectionModeWithLocales = useMemo(() => {
    return selectionModes.map((mode) => ({
      ...mode,
      label: intlConfig.formatMessage({
        id: `c4r.widgets.featureSelection.${getModeKeyForLocale(mode.id)}`
      })
    }));
  }, [selectionModes, intlConfig]);
  const editModesWithLocales = useMemo(() => {
    return editModes.map((mode) => ({
      ...mode,
      label: intlConfig.formatMessage({
        id: `c4r.widgets.featureSelection.${getModeKeyForLocale(mode.id)}`
      })
    }));
  }, [editModes, intlConfig]);
  const selectedModeData = useMemo(() => {
    const modes = [
      ...selectionModeWithLocales.map((mode) => ({
        ...mode,
        isEdit: false
      })),
      ...editModesWithLocales.map((mode) => ({
        ...mode,
        isEdit: true
      }))
    ];
    const foundMode = modes.find((m) => m.id === selectedMode);
    if (!foundMode) {
      throw new Error(`Selected mode "${selectedMode}" not supported`);
    }
    return foundMode;
  }, [editModesWithLocales, selectionModeWithLocales, selectedMode]);
  const label = capitalize((selectedModeData == null ? void 0 : selectedModeData.label) || "");
  const hoverTooltip = (selectedModeData == null ? void 0 : selectedModeData.isEdit) ? label : intlConfig.formatMessage(
    {
      id: `c4r.widgets.featureSelection.selectTool`
    },
    { label }
  );
  const clickTooltip = (selectedModeData == null ? void 0 : selectedModeData.isEdit) ? intlConfig.formatMessage({
    id: `c4r.widgets.featureSelection.clickToEdit`
  }) : intlConfig.formatMessage({
    id: `c4r.widgets.featureSelection.clickToCreate`
  });
  return /* @__PURE__ */ jsxs(StylesWrapper, { children: [
    /* @__PURE__ */ jsx(
      FeatureSelectionUIToggleButton,
      {
        icon: selectedModeData == null ? void 0 : selectedModeData.icon,
        hoverTooltip,
        clickTooltip,
        enabled,
        onEnabledChange,
        tooltipPlacement
      }
    ),
    /* @__PURE__ */ jsx(
      FeatureSelectionUIDropdown,
      {
        selectionModes: selectionModeWithLocales,
        editModes: editModesWithLocales,
        selectedMode,
        onSelectMode,
        enabled,
        tooltipPlacement,
        tooltipText: intlConfig.formatMessage({
          id: `c4r.widgets.featureSelection.selectMode`
        }),
        menuHeaderText: intlConfig.formatMessage({
          id: `c4r.widgets.featureSelection.chooseSelectionMode`
        }),
        editDisabled: !geometry
      }
    ),
    !!geometry && /* @__PURE__ */ jsx(
      FeatureSelectionUIGeometryChips,
      {
        features: [geometry],
        onSelectGeometry,
        onDeleteGeometry,
        disabledChipTooltip: intlConfig.formatMessage({
          id: `c4r.widgets.featureSelection.applyMask`
        }),
        chipTooltip: intlConfig.formatMessage({
          id: `c4r.widgets.featureSelection.clearMask`
        }),
        tooltipPlacement,
        size,
        chipLabel
      }
    )
  ] });
}
function getModeKeyForLocale(id) {
  return id.charAt(0).toLowerCase() + id.slice(1);
}
const LEGEND_WIDTH = 240;
const LegendRoot = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "collapsed"
})(({ theme, collapsed }) => ({
  width: collapsed ? "min-content" : LEGEND_WIDTH,
  background: theme.palette.background.paper,
  maxHeight: "calc(100% - 120px)",
  display: "flex",
  flexDirection: "column"
}));
const LegendToggleHeader = styled("header", {
  shouldForwardProp: (prop) => prop !== "collapsed"
})(({ theme, collapsed }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1),
  paddingLeft: theme.spacing(2),
  borderBottom: collapsed ? void 0 : `1px solid ${theme.palette.divider}`
}));
const LegendItemHeader = styled("header")(({ theme }) => ({
  padding: theme.spacing(1.5),
  paddingRight: theme.spacing(2),
  gap: theme.spacing(0.5),
  display: "flex",
  justifyContent: "space-between",
  position: "sticky",
  zIndex: 2,
  top: 0,
  background: theme.palette.background.paper
}));
const StyledOpacityControl = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  alignItems: "center",
  padding: theme.spacing(1),
  minWidth: `calc(${LEGEND_WIDTH}px - ${theme.spacing(4)})`
}));
const OpacityTextField = styled(TextField)(({ theme }) => ({
  display: "flex",
  width: theme.spacing(7.5),
  flexShrink: 0,
  "input[type=number]::-webkit-inner-spin-button, input[type=number]::-webkit-outer-spin-button": {
    WebkitAppearance: "none",
    margin: 0
  }
}));
const LayerVariablesList = styled("ul", {
  shouldForwardProp: (prop) => prop !== "opacity"
})(({ theme, opacity }) => ({
  opacity,
  margin: 0,
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1)
}));
const LegendVariableList = styled("ul")(({ theme }) => ({
  margin: 0,
  padding: 0,
  paddingBottom: theme.spacing(1),
  display: "flex",
  flexDirection: "column"
}));
const LegendIconWrapper = styled("li")(() => ({
  display: "flex",
  alignItems: "center"
}));
const LegendIconImageWrapper = styled("div")(({ theme }) => ({
  marginRight: theme.spacing(1.5),
  width: ICON_SIZE_MEDIUM,
  height: ICON_SIZE_MEDIUM,
  "& img": {
    margin: "auto",
    display: "block"
  }
}));
const LegendContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== "width"
})(({ width, theme }) => ({
  width,
  overflow: "auto",
  maxHeight: `calc(100% - ${theme.spacing(1.5)})`
}));
const LegendLayerWrapper = styled("section")(({ theme }) => ({
  "&:not(:first-of-type)": {
    borderTop: `1px solid ${theme.palette.divider}`
  }
}));
const LegendLayerTitleWrapper = styled("div")(() => ({
  flexGrow: 1,
  flexShrink: 1,
  minWidth: 0
}));
function LegendOpacityControl({
  menuRef,
  open,
  toggleOpen,
  opacity,
  onChange
}) {
  const intl = useIntl();
  const intlConfig = useImperativeIntl(intl);
  function handleTextFieldChange(e) {
    const newOpacity = parseInt(e.target.value || "0");
    const clamped = Math.min(Math.max(newOpacity, 0), 100);
    onChange(clamped / 100);
  }
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      Tooltip,
      {
        title: intlConfig.formatMessage({ id: "c4r.widgets.legend.opacity" }),
        children: /* @__PURE__ */ jsx(
          IconButton$1,
          {
            size: "small",
            color: open ? "primary" : "default",
            onClick: () => toggleOpen(!open),
            children: /* @__PURE__ */ jsx(SwatchSquare, {})
          }
        )
      }
    ),
    /* @__PURE__ */ jsx(
      Popover,
      {
        open,
        onClose: () => toggleOpen(false),
        anchorEl: menuRef.current,
        anchorOrigin: {
          vertical: "top",
          horizontal: "right"
        },
        transformOrigin: {
          vertical: "top",
          horizontal: "right"
        },
        slotProps: {
          root: {
            sx: { transform: "translate(-12px, 36px)" }
          }
        },
        children: /* @__PURE__ */ jsxs(StyledOpacityControl, { children: [
          /* @__PURE__ */ jsx(
            Slider,
            {
              value: opacity * 100,
              onChange: (_, v) => onChange(v / 100),
              min: 0,
              max: 100,
              step: 1
            }
          ),
          /* @__PURE__ */ jsx(
            OpacityTextField,
            {
              size: "small",
              type: "number",
              value: Math.round(opacity * 100),
              onChange: handleTextFieldChange,
              inputProps: {
                step: 1,
                min: 0,
                max: 100,
                style: { appearance: "textfield" },
                "data-testid": "opacity-slider"
              },
              InputProps: {
                endAdornment: /* @__PURE__ */ jsxs(InputAdornment, { position: "end", sx: { margin: 0 }, children: [
                  " ",
                  "%"
                ] })
              }
            }
          )
        ] })
      }
    )
  ] });
}
function LegendLayerTitle({
  title,
  visible
}) {
  const ref = useRef(null);
  const [isOverflow, setIsOverflow] = useState(false);
  useLayoutEffect(() => {
    if (visible && ref.current) {
      const { offsetWidth, scrollWidth } = ref.current;
      setIsOverflow(offsetWidth < scrollWidth);
    }
  }, [title, visible]);
  const element = /* @__PURE__ */ jsx(
    Typography,
    {
      ref,
      color: visible ? "textPrimary" : "textSecondary",
      variant: "caption",
      weight: "regular",
      component: "p",
      noWrap: true,
      my: 0.25,
      textTransform: "none",
      children: title
    }
  );
  if (!isOverflow) {
    return element;
  }
  return /* @__PURE__ */ jsx(Tooltip, { title, children: element });
}
var LEGEND_TYPES = /* @__PURE__ */ ((LEGEND_TYPES2) => {
  LEGEND_TYPES2["CATEGORY"] = "category";
  LEGEND_TYPES2["ICON"] = "icon";
  LEGEND_TYPES2["CONTINUOUS_RAMP"] = "continuous_ramp";
  LEGEND_TYPES2["BINS"] = "bins";
  LEGEND_TYPES2["PROPORTION"] = "proportion";
  return LEGEND_TYPES2;
})(LEGEND_TYPES || {});
const MAX_CATEGORIES = 20;
function LegendCategories({
  legend
}) {
  const {
    labels = [],
    colors = [],
    isStrokeColor = false,
    customMarkers,
    maskedMarkers = true
  } = legend;
  const palette = getPalette(colors, labels.length);
  const showHelperText = labels.length > MAX_CATEGORIES;
  const intl = useIntl();
  const intlConfig = useImperativeIntl(intl);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(LegendVariableList, { "data-testid": "categories-legend", children: labels.slice(0, MAX_CATEGORIES).map((label, idx) => /* @__PURE__ */ jsx(
      LegendCategoriesRow,
      {
        label: String(label),
        color: palette[idx % palette.length],
        icon: customMarkers && Array.isArray(customMarkers) ? customMarkers[idx] : customMarkers,
        maskedIcon: maskedMarkers,
        isStrokeColor
      },
      `${label}${idx}`
    )) }),
    showHelperText && /* @__PURE__ */ jsx(
      Typography,
      {
        variant: "caption",
        color: "textSecondary",
        component: "div",
        py: 2,
        children: intlConfig.formatMessage(
          {
            id: "c4r.widgets.legend.maxCategories"
          },
          {
            n: MAX_CATEGORIES
          }
        )
      }
    )
  ] });
}
const getCircleStyles = ({
  isMax,
  color,
  isStrokeColor,
  theme
}) => ({
  border: "2px solid transparent",
  "&::after": {
    position: "absolute",
    display: isMax ? "block" : "none",
    content: '""',
    width: theme.spacing(2),
    height: theme.spacing(2),
    border: `2px solid ${theme.palette.grey[900]}`,
    transform: "translate(-30%, -30%)",
    borderRadius: "50%",
    boxSizing: "content-box"
  },
  ...isStrokeColor ? { borderColor: color } : { backgroundColor: color }
});
const getIconStyles = ({
  icon,
  color,
  maskedIcon
}) => ({
  maskRepeat: "no-repeat",
  maskSize: "cover",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  ...maskedIcon ? {
    backgroundColor: color,
    maskImage: `url(${icon})`,
    WebkitMaskImage: `url(${icon})`
  } : {
    backgroundColor: `rgba(0,0,0,0)`,
    backgroundImage: `url(${icon})`
  }
});
const Marker = styled(Box, {
  shouldForwardProp: (prop) => {
    return prop !== "icon" && prop !== "maskedIcon" && prop !== "color" && prop !== "isStrokeColor";
  }
})(({ icon, maskedIcon, color, isStrokeColor, theme }) => ({
  whiteSpace: "nowrap",
  display: "block",
  width: theme.spacing(1.5),
  height: theme.spacing(1.5),
  flexShrink: "0",
  borderRadius: "50%",
  position: "relative",
  border: "2px solid transparent",
  ...icon ? getIconStyles({ icon, color, maskedIcon }) : getCircleStyles({ color, isStrokeColor, theme })
}));
function LegendCategoriesRow({
  label,
  isStrokeColor,
  color = "#000",
  icon,
  maskedIcon
}) {
  return /* @__PURE__ */ jsxs(Box, { component: "li", sx: { display: "flex", alignItems: "center" }, children: [
    /* @__PURE__ */ jsx(
      Marker,
      {
        className: "marker",
        mr: 1.5,
        component: "span",
        icon,
        maskedIcon,
        isStrokeColor,
        color
      }
    ),
    /* @__PURE__ */ jsx(LegendLayerTitle, { title: label, visible: true })
  ] });
}
const MAX_SIG_DIGITS = 6;
const sizes = {
  0: 12,
  1: 9,
  2: 6,
  3: 3
};
const Circle = styled(Box, {
  shouldForwardProp: (prop) => prop !== "index"
})(({ index = 0, theme }) => {
  const width = theme.spacing(sizes[index]);
  const height = theme.spacing(sizes[index]);
  return {
    border: `solid 1px ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    borderRadius: "50%",
    position: "absolute",
    right: 0,
    bottom: 0,
    width,
    height
  };
});
const CircleGrid = styled(Box)(({ theme: { spacing } }) => ({
  display: "flex",
  justifyContent: "flex-end",
  flexShrink: 0,
  position: "relative",
  width: spacing(sizes[0]),
  height: spacing(sizes[0])
}));
const LegendProportionWrapper = styled(Box)(({ theme: { spacing } }) => ({
  display: "flex",
  gap: spacing(1),
  alignItems: "stretch",
  justifyContent: "stretch",
  padding: spacing(2, 0)
}));
const LabelList = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-around",
  gap: 1,
  flexGrow: 1,
  flexShrink: 1
}));
function LegendProportion({
  legend
}) {
  const intl = useIntl();
  const intlConfig = useImperativeIntl(intl);
  const showMinMax = legend.showMinMax ?? true;
  const { min, max, error } = calculateRange(legend);
  const [step1, step2] = !error ? calculateSteps(min, max) : [0, 0];
  return /* @__PURE__ */ jsxs(LegendProportionWrapper, { "data-testid": "proportion-legend", children: [
    /* @__PURE__ */ jsx(CircleGrid, { children: [...Array(4)].map((_, index) => /* @__PURE__ */ jsx(
      Circle,
      {
        index,
        component: "span"
      },
      index
    )) }),
    /* @__PURE__ */ jsx(LabelList, { children: error ? /* @__PURE__ */ jsx(Box, { maxWidth: 240, children: /* @__PURE__ */ jsx(Typography, { variant: "overline", children: "You need to specify valid numbers for the labels property" }) }) : /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsxs(Typography, { variant: "overline", color: "textSecondary", children: [
        showMinMax && intlConfig.formatMessage({ id: "c4r.widgets.legend.max" }) + ": ",
        intl.formatNumber(max, {
          maximumSignificantDigits: MAX_SIG_DIGITS
        })
      ] }),
      /* @__PURE__ */ jsx(Typography, { variant: "overline", color: "textSecondary", children: intl.formatNumber(step2, {
        maximumSignificantDigits: MAX_SIG_DIGITS
      }) }),
      /* @__PURE__ */ jsx(Typography, { variant: "overline", color: "textSecondary", children: intl.formatNumber(step1, {
        maximumSignificantDigits: MAX_SIG_DIGITS
      }) }),
      /* @__PURE__ */ jsxs(Typography, { variant: "overline", color: "textSecondary", children: [
        showMinMax && intlConfig.formatMessage({ id: "c4r.widgets.legend.min" }) + ": ",
        intl.formatNumber(min, {
          maximumSignificantDigits: MAX_SIG_DIGITS
        })
      ] })
    ] }) })
  ] });
}
function getMinMax({ labels }) {
  let max = labels == null ? void 0 : labels[labels.length - 1];
  let min = labels == null ? void 0 : labels[0];
  if (!Number.isFinite(min)) {
    min = parseInt(String(min), 10);
  }
  if (!Number.isFinite(max)) {
    max = parseInt(String(max), 10);
  }
  return [min, max];
}
function calculateRange(legend) {
  let error = false;
  const [min, max] = getMinMax(legend);
  if (Number.isNaN(min) || Number.isNaN(max)) {
    error = true;
  }
  return { min, max, error };
}
function calculateSteps(min, max) {
  const step = (max - min) / 3;
  return [min + step, max - step];
}
function LegendIcon({
  legend
}) {
  const { labels = [], icons = [] } = legend;
  return /* @__PURE__ */ jsx(LegendVariableList, { "data-testid": "icon-legend", children: labels.map((label, idx) => /* @__PURE__ */ jsxs(LegendIconWrapper, { children: [
    /* @__PURE__ */ jsx(LegendIconImageWrapper, { children: /* @__PURE__ */ jsx(
      "img",
      {
        src: icons[idx],
        alt: label,
        width: "auto",
        height: ICON_SIZE_MEDIUM
      }
    ) }),
    /* @__PURE__ */ jsx(LegendLayerTitle, { visible: true, title: label })
  ] }, label)) });
}
function isComplexLabels(labels) {
  return typeof labels[0] === "object" && "label" in labels[0];
}
function LegendRamp({
  isContinuous = false,
  legend
}) {
  const { labels = [], colors = [], showMinMax = true } = legend;
  const palette = getPalette(
    colors,
    !labels.length || isContinuous ? 2 : labels.length + 1
  );
  const _isComplexLabels = isComplexLabels(labels);
  const values = _isComplexLabels ? labels.map(({ value }) => value) : labels;
  const formattedLabels = _isComplexLabels ? labels.map(({ label }) => label) : labels;
  const [min, max] = getMinMax({ labels: values });
  const error = Number.isNaN(min) || Number.isNaN(max);
  let maxLabel = formattedLabels[formattedLabels.length - 1];
  let minLabel = formattedLabels[0];
  if (!isContinuous && showMinMax) {
    minLabel = "< " + minLabel;
    maxLabel = "≥ " + maxLabel;
  }
  return /* @__PURE__ */ jsx(Box, { py: 2, "data-testid": "ramp-legend", children: error ? /* @__PURE__ */ jsx(Box, { maxWidth: 240, children: /* @__PURE__ */ jsx(Typography, { variant: "overline", children: "You need to specify valid numbers for the labels property" }) }) : /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Box, { display: "flex", pb: 1, children: isContinuous ? /* @__PURE__ */ jsx(
      StepsContinuous,
      {
        "data-testid": "step-continuous",
        palette
      }
    ) : /* @__PURE__ */ jsx(
      StepsDiscontinuous,
      {
        palette,
        labels: formattedLabels,
        max: maxLabel,
        min: minLabel
      }
    ) }),
    /* @__PURE__ */ jsxs(Box, { display: "flex", justifyContent: "space-between", children: [
      /* @__PURE__ */ jsx(Typography, { variant: "overlineDelicate", color: "textSecondary", children: minLabel }),
      /* @__PURE__ */ jsx(Typography, { variant: "overlineDelicate", color: "textSecondary", children: maxLabel })
    ] })
  ] }) });
}
const StepsContinuous = styled(Box, {
  shouldForwardProp: (prop) => prop !== "palette"
})(({ palette, theme }) => ({
  display: "block",
  flexGrow: 1,
  height: theme.spacing(1),
  borderRadius: theme.spacing(0.5),
  background: `linear-gradient(to right, ${palette.join()})`
}));
const StepGrid = styled(Box, {
  shouldForwardProp: (prop) => prop !== "color"
})(({ color, theme }) => ({
  display: "block",
  flexGrow: 1,
  height: theme.spacing(1),
  backgroundColor: color,
  "&:first-of-type": {
    borderRadius: theme.spacing(0.5, 0, 0, 0.5)
  },
  "&:last-of-type": {
    borderRadius: theme.spacing(0, 0.5, 0.5, 0)
  }
}));
function StepsDiscontinuous({
  labels = [],
  palette = [],
  max,
  min
}) {
  const rightLabels = labels.length ? [min, ...labels] : [min, max];
  return /* @__PURE__ */ jsx(Fragment, { children: rightLabels.map((label, idx) => {
    const title = idx === 0 ? min : idx === rightLabels.length - 1 ? max : `${label} - ${rightLabels[idx + 1]}`;
    return /* @__PURE__ */ jsx(Tooltip, { title, children: /* @__PURE__ */ jsx(StepGrid, { "data-testid": "step-discontinuous", color: palette[idx] }) }, idx);
  }) });
}
const legendTypeMap = {
  [LEGEND_TYPES.CATEGORY]: LegendCategories,
  [LEGEND_TYPES.PROPORTION]: LegendProportion,
  [LEGEND_TYPES.ICON]: LegendIcon,
  [LEGEND_TYPES.BINS]: (props) => /* @__PURE__ */ jsx(LegendRamp, { ...props, isContinuous: false }),
  [LEGEND_TYPES.CONTINUOUS_RAMP]: (props) => /* @__PURE__ */ jsx(LegendRamp, { ...props, isContinuous: true })
};
function LegendUnknown({ legend }) {
  const intl = useIntl();
  const intlConfig = useImperativeIntl(intl);
  if (legend.select || !legend.type) {
    return null;
  }
  return /* @__PURE__ */ jsxs(Typography, { variant: "body2", color: "textSecondary", component: "p", children: [
    legend.type,
    " ",
    intlConfig.formatMessage({ id: "c4r.widgets.legend.notSupported" }),
    "."
  ] });
}
function getLegendSubtitle(legend) {
  if (legend.type === LEGEND_TYPES.PROPORTION) {
    return "c4r.widgets.legend.subtitles.proportion";
  }
  if (legend.type === LEGEND_TYPES.ICON || !!legend.customMarkers) {
    return "c4r.widgets.legend.subtitles.icon";
  }
  if (legend.isStrokeColor) {
    return "c4r.widgets.legend.subtitles.strokeColor";
  }
  return "c4r.widgets.legend.subtitles.color";
}
function LegendLayerVariable({
  layer,
  legend,
  customLegendTypes,
  onChangeSelection
}) {
  var _a;
  const intl = useIntl();
  const intlConfig = useImperativeIntl(intl);
  const type = legend.type;
  const TypeComponent = legendTypeMap[type] ?? customLegendTypes[type] ?? LegendUnknown;
  const TypedComponent = TypeComponent;
  const selectOptions = ((_a = legend.select) == null ? void 0 : _a.options) ?? [];
  return /* @__PURE__ */ jsxs(Box, { component: "li", "data-testid": "legend-layer-variable", px: 2, children: [
    legend.attr ? /* @__PURE__ */ jsxs(Box, { pb: 1, children: [
      /* @__PURE__ */ jsx(
        Typography,
        {
          gutterBottom: true,
          variant: "overlineDelicate",
          color: "textSecondary",
          component: "p",
          children: intlConfig.formatMessage({ id: getLegendSubtitle(legend) })
        }
      ),
      /* @__PURE__ */ jsx(Typography, { variant: "caption", component: "p", children: legend.attr })
    ] }) : null,
    legend.select ? /* @__PURE__ */ jsxs(Box, { pb: 1, children: [
      /* @__PURE__ */ jsx(Typography, { variant: "caption", weight: "medium", component: "p", children: legend.select.label }),
      /* @__PURE__ */ jsx(
        Select,
        {
          "data-testid": "legend-layer-variable-select",
          name: "legend-select",
          value: legend.select.value,
          renderValue: (value) => {
            var _a2;
            return ((_a2 = selectOptions.find((option) => option.value === value)) == null ? void 0 : _a2.label) ?? value;
          },
          onChange: (ev) => onChangeSelection(ev.target.value),
          MenuProps: {
            transformOrigin: { vertical: "bottom", horizontal: "left" },
            anchorOrigin: { vertical: "top", horizontal: "left" },
            PaperProps: {
              style: {
                maxHeight: 240
              }
            }
          },
          children: selectOptions.map((option) => /* @__PURE__ */ jsx(MenuItem, { value: option.value, children: /* @__PURE__ */ jsx(ListItemText, { primary: option.label }) }, option.value))
        }
      )
    ] }) : null,
    /* @__PURE__ */ jsx(TypedComponent, { layer, legend })
  ] });
}
const EMPTY_OBJ$1 = {};
function isLegendEmpty(legend) {
  if (!legend) {
    return true;
  }
  if (Array.isArray(legend)) {
    return legend.every((l) => isLegendEmpty(l));
  }
  return !legend.select && !legend.type;
}
function LegendLayer({
  customLegendTypes = EMPTY_OBJ$1,
  layer,
  onChangeCollapsed,
  onChangeOpacity,
  onChangeVisibility,
  onChangeSelection,
  maxZoom = 21,
  minZoom = 0,
  currentZoom = 0
}) {
  const intl = useIntl();
  const intlConfig = useImperativeIntl(intl);
  const menuAnchorRef = useRef(null);
  const [opacityOpen, setOpacityOpen] = useState(false);
  const id = layer.id;
  const title = layer.title;
  const visible = layer.visible ?? true;
  const switchable = layer.switchable ?? true;
  const collapsed = layer.collapsed ?? false;
  const collapsible = (layer.collapsible ?? true) && !isLegendEmpty(layer.legend);
  const opacity = layer.opacity ?? 1;
  const showOpacityControl = layer.showOpacityControl ?? true;
  const isExpanded = visible && !collapsed;
  const collapseIcon = isExpanded ? /* @__PURE__ */ jsx(ExpandLess, {}) : /* @__PURE__ */ jsx(ExpandMore, {});
  const layerHasZoom = (layer == null ? void 0 : layer.minZoom) !== void 0 || (layer == null ? void 0 : layer.maxZoom) !== void 0;
  const layerMinZoom = layer.minZoom ?? minZoom;
  const layerMaxZoom = layer.maxZoom ?? maxZoom;
  const showZoomNote = layerHasZoom && (layerMinZoom > minZoom || layerMaxZoom < maxZoom);
  const outsideCurrentZoom = currentZoom < layerMinZoom || currentZoom > layerMaxZoom;
  const zoomHelperText = getZoomHelperText({
    intl: intlConfig,
    minZoom,
    maxZoom,
    layerMinZoom,
    layerMaxZoom
  });
  const helperText = layer.helperText ?? (showZoomNote ? zoomHelperText : "");
  const legendLayerVariables = useMemo(() => {
    if (!layer.legend) {
      return [];
    }
    return Array.isArray(layer.legend) ? layer.legend : [layer.legend];
  }, [layer.legend]);
  if (!layer.legend) {
    return null;
  }
  return /* @__PURE__ */ jsxs(LegendLayerWrapper, { "data-testid": "legend-layer", "aria-label": title, children: [
    /* @__PURE__ */ jsxs(LegendItemHeader, { ref: menuAnchorRef, children: [
      collapsible && /* @__PURE__ */ jsx(
        IconButton$1,
        {
          size: "small",
          "aria-label": intlConfig.formatMessage({
            id: collapsed ? "c4r.widgets.legend.expand" : "c4r.widgets.legend.collapse"
          }),
          disabled: !visible,
          onClick: () => onChangeCollapsed({ id, collapsed: !collapsed }),
          children: collapseIcon
        }
      ),
      /* @__PURE__ */ jsxs(LegendLayerTitleWrapper, { children: [
        /* @__PURE__ */ jsx(LegendLayerTitle, { visible, title: title ?? "" }),
        showZoomNote && /* @__PURE__ */ jsx(
          Tooltip,
          {
            title: intlConfig.formatMessage({
              id: "c4r.widgets.legend.zoomLevelTooltip"
            }),
            children: /* @__PURE__ */ jsxs(
              Typography,
              {
                color: visible ? "textPrimary" : "textSecondary",
                variant: "caption",
                component: "p",
                children: [
                  intlConfig.formatMessage({
                    id: "c4r.widgets.legend.zoomLevel"
                  }),
                  " ",
                  layer.minZoom,
                  " - ",
                  layer.maxZoom
                ]
              }
            )
          }
        )
      ] }),
      showOpacityControl && visible && !collapsed && /* @__PURE__ */ jsx(
        LegendOpacityControl,
        {
          menuRef: menuAnchorRef,
          open: opacityOpen,
          toggleOpen: setOpacityOpen,
          opacity,
          onChange: (opacity2) => onChangeOpacity({ id, opacity: opacity2 })
        }
      ),
      switchable && /* @__PURE__ */ jsx(
        Tooltip,
        {
          title: intlConfig.formatMessage({
            id: visible ? "c4r.widgets.legend.hideLayer" : "c4r.widgets.legend.showLayer"
          }),
          children: /* @__PURE__ */ jsx(
            IconButton$1,
            {
              size: "small",
              onClick: () => onChangeVisibility({
                id,
                collapsed: visible ? collapsed : false,
                visible: !visible
              }),
              children: visible ? /* @__PURE__ */ jsx(VisibilityOutlined, {}) : /* @__PURE__ */ jsx(VisibilityOffOutlined, {})
            }
          )
        }
      )
    ] }),
    /* @__PURE__ */ jsxs(Collapse, { unmountOnExit: true, timeout: 100, in: isExpanded, children: [
      /* @__PURE__ */ jsx(
        LayerVariablesList,
        {
          "data-testid": "legend-layer-variable-list",
          opacity: outsideCurrentZoom ? 0.5 : 1,
          children: legendLayerVariables.map((legend, index) => /* @__PURE__ */ jsx(
            LegendLayerVariable,
            {
              legend,
              layer,
              customLegendTypes,
              onChangeSelection: (selection) => onChangeSelection({ id, index, selection })
            },
            `${legend.type}-${index}`
          ))
        }
      ),
      helperText && /* @__PURE__ */ jsx(
        Typography,
        {
          variant: "caption",
          color: "textSecondary",
          component: "div",
          p: 2,
          children: /* @__PURE__ */ jsx("div", { dangerouslySetInnerHTML: { __html: helperText } })
        }
      )
    ] })
  ] });
}
function getZoomHelperText({
  intl,
  minZoom,
  maxZoom,
  layerMinZoom,
  layerMaxZoom
}) {
  const and = intl.formatMessage({ id: "c4r.widgets.legend.and" });
  const lowerThan = intl.formatMessage({ id: "c4r.widgets.legend.lowerThan" });
  const greaterThan = intl.formatMessage({
    id: "c4r.widgets.legend.greaterThan"
  });
  const note = intl.formatMessage({ id: "c4r.widgets.legend.zoomNote" });
  const maxZoomText = layerMaxZoom < maxZoom ? `${lowerThan} ${layerMaxZoom}` : "";
  const minZoomText = layerMinZoom > minZoom ? `${greaterThan} ${layerMinZoom}` : "";
  const texts = [maxZoomText, minZoomText].filter(Boolean).join(` ${and} `);
  return texts ? `${note} ${texts}` : "";
}
const EMPTY_OBJ = {};
const EMPTY_FN = () => void 0;
const EMPTY_ARR = [];
function LegendWidgetUI({
  customLegendTypes = EMPTY_OBJ,
  layers = EMPTY_ARR,
  collapsed = false,
  onChangeCollapsed = EMPTY_FN,
  onChangeVisibility = EMPTY_FN,
  onChangeOpacity = EMPTY_FN,
  onChangeLegendRowCollapsed = EMPTY_FN,
  onChangeSelection = EMPTY_FN,
  title = "Layers",
  maxZoom = 20,
  minZoom = 0,
  currentZoom,
  isMobile,
  sx
}) {
  const intl = useIntl();
  const intlConfig = useImperativeIntl(intl);
  const legendToggleHeader = /* @__PURE__ */ jsxs(LegendToggleHeader, { collapsed, children: [
    /* @__PURE__ */ jsx(Typography, { variant: "caption", flexGrow: 1, children: title }),
    /* @__PURE__ */ jsx(
      Tooltip,
      {
        title: intlConfig.formatMessage({ id: "c4r.widgets.legend.close" }),
        children: /* @__PURE__ */ jsx(IconButton$1, { size: "small", onClick: () => onChangeCollapsed(true), children: /* @__PURE__ */ jsx(Close, {}) })
      }
    )
  ] });
  const legendToggleButton = /* @__PURE__ */ jsx(
    Tooltip,
    {
      title: intlConfig.formatMessage({ id: "c4r.widgets.legend.open" }),
      children: /* @__PURE__ */ jsx(IconButton$1, { "aria-label": title, onClick: () => onChangeCollapsed(false), children: /* @__PURE__ */ jsx(LayersOutlined, {}) })
    }
  );
  return /* @__PURE__ */ jsx(LegendRoot, { sx, elevation: 3, collapsed: collapsed || !!isMobile, children: isMobile ? /* @__PURE__ */ jsxs(Fragment, { children: [
    legendToggleButton,
    /* @__PURE__ */ jsxs(
      Drawer,
      {
        anchor: "bottom",
        open: !collapsed,
        onClose: () => onChangeCollapsed(true),
        children: [
          legendToggleHeader,
          /* @__PURE__ */ jsx(LegendContent, { children: layers.map((l) => /* @__PURE__ */ jsx(
            LegendLayer,
            {
              layer: l,
              onChangeCollapsed: onChangeLegendRowCollapsed,
              onChangeOpacity,
              onChangeVisibility,
              onChangeSelection,
              maxZoom,
              minZoom,
              currentZoom,
              customLegendTypes
            },
            l.id
          )) })
        ]
      }
    )
  ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
    collapsed ? legendToggleButton : legendToggleHeader,
    /* @__PURE__ */ jsx(LegendContent, { width: collapsed ? 0 : void 0, children: /* @__PURE__ */ jsx(Collapse, { unmountOnExit: true, in: !collapsed, timeout: 500, children: layers.map((l) => /* @__PURE__ */ jsx(
      LegendLayer,
      {
        layer: l,
        onChangeCollapsed: onChangeLegendRowCollapsed,
        onChangeOpacity,
        onChangeVisibility,
        onChangeSelection,
        maxZoom,
        minZoom,
        currentZoom,
        customLegendTypes
      },
      l.id
    )) }) })
  ] }) });
}
export {
  BarWidgetUI,
  CategoryWidgetUI,
  ComparativeCategoryWidgetUI,
  ComparativeFormulaWidgetUI,
  ComparativePieWidgetUI,
  FeatureSelectionUIDropdown,
  FeatureSelectionUIGeometryChips,
  FeatureSelectionUIToggleButton,
  FeatureSelectionWidgetUI,
  FormulaWidgetUI,
  GroupDateTypes,
  HistogramWidgetUI,
  LEGEND_TYPES,
  LegendCategories,
  LegendIcon,
  LegendProportion,
  LegendRamp,
  LegendWidgetUI,
  NoDataAlert,
  OrderTypes,
  PieWidgetUI,
  RangeWidgetUI,
  ScatterPlotWidgetUI,
  TIME_SERIES_CHART_TYPES,
  TableWidgetUI,
  TimeSeriesProvider,
  TimeSeriesWidgetUI,
  WrapperWidgetUI,
  areChartPropsEqual,
  useTimeSeriesContext,
  useTimeSeriesInteractivity
};
